import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { isPrivatoPerContoProprio } from '../utils/clientDisplay';
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { useUnsavedGuard } from "../hooks/useUnsavedGuard";
import {
    ChevronLeft, Download, Bold, Italic, Underline,
    Image as ImageIcon, FileText, Save, Clock, PlayCircle, X, Check,
    PlusCircle, Sparkles, Wand2, Send, Bot, RefreshCw,
    Trash2, Calendar, User, MapPin,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    List, ListOrdered, Type, Camera, Layout,
    ArrowUpRight, Layers, Edit3, Settings, Tag,
    Maximize2, Minimize2, MoveHorizontal, Loader2, RotateCw, RotateCcw,
    Eye,
    PanelRightOpen,
    Columns2,
    SeparatorHorizontal,
} from "lucide-react";
import { ToolbarBtn, useActiveFormats, FONT_SIZES, applyFontSize } from "../components/reportEditor/formatToolbar";
import {
    PraticaService,
    IncaricoService,
    RelazioneGiornalieraService,
    AiService,
    SettingsService, FilesService
} from "../services/apiService";
import { Pratica, Incarico, Files } from "../types";
import { useToast } from "../components/ToastContext";
import ForensicImageEditor from "../components/ForensicImageEditor";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from "../utils/api";
import { applyCachedImageUrls, refreshImageUrls, getSignedImageUrl } from "../utils/signedImageUrlCache";
import { formatIsoDateToItalian } from "../utils/utils";
import PdfLivePreview from "../components/PdfLivePreview";
import { relazioneFileName } from '../utils/relazioneFileName';

// Tipi annotazioni
type AnnotationTool = 'none' | 'arrow' | 'blur';

// Ancoraggio al layout reale, calcolato dall'editor alla creazione: indice
// dell'elemento di contenuto (esclusi spacer e copertina) sotto il punto più
// alto dell'annotazione, più l'offset verticale interno. Il render server lo usa
// per posizionare censure e frecce come nell'editor.
interface AnnAnchor { anchorIdx?: number; anchorDy?: number; }
interface ArrowAnnotation extends AnnAnchor {
    id: string; type: 'arrow';
    x1: number; y1: number; x2: number; y2: number;
    color: string;
}
interface BlurAnnotation extends AnnAnchor {
    id: string; type: 'blur';
    x: number; y: number; w: number; h: number;
}
type Annotation = ArrowAnnotation | BlurAnnotation;

// Helper cursore per undo/redo
function getCaretOffset(root: HTMLElement): number {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;
    try {
        const r = sel.getRangeAt(0).cloneRange();
        r.selectNodeContents(root);
        r.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
        return r.toString().length;
    } catch { return 0; }
}

function restoreCaretOffset(root: HTMLElement, offset: number) {
    try {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        let rem = offset;
        let node: Text | null;
        while ((node = walker.nextNode() as Text | null)) {
            if (rem <= node.length) {
                const range = document.createRange();
                range.setStart(node, rem);
                range.collapse(true);
                const sel = window.getSelection();
                sel?.removeAllRanges();
                sel?.addRange(range);
                return;
            }
            rem -= node.length;
        }
        // Fallback: fine del contenuto
        const range = document.createRange();
        range.selectNodeContents(root);
        range.collapse(false);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
    } catch { /**/ }
}

// Helper
const getMediaType = (file: Files) => {
    const ext = file.extension?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'image';
    if (['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'].includes(ext)) return 'video';
    return 'doc';
};

// Per i media di campo il backend salva `fileType` come MIME (es. "video/mp4"), ma
// alcuni upload da app arrivano con null o "application/octet-stream": fallback
// sull'estensione, come in TabMedia.tsx.
const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'mts', 'm2ts', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg'];
const isFieldVideo = (m: { fileType?: string; filename?: string }): boolean => {
    if (m.fileType?.toLowerCase().startsWith('video')) return true;
    const dot = m.filename?.lastIndexOf('.') ?? -1;
    const ext = dot >= 0 ? m.filename!.slice(dot + 1).toLowerCase() : '';
    return VIDEO_EXTS.includes(ext);
};

// Altezza area contenuto: A4 (297mm) - header (35mm) - footer (25mm) - padding (5mm + 5mm) = 227mm.
// Deve restare allineata a HEADER_MM/FOOTER_MM/CONTENT_PAD_MM in handleDownloadPdf,
// altrimenti la slice HTML viene stirata o compressa nel PDF.
const PAGE_H_PX = Math.round(227 * 96 / 25.4); // 858px, area contenuto tra header e footer

// Layout del box di una foto, uguale al render server (render-relazione.js):
// - verticale a dimensione di default (senza larghezza manuale né float) → una
// per pagina, centrata: box alto una pagina + flex center.
// - verticale ridimensionata a mano, flottante o orizzontale → box normale che
// segue il flusso.
function applyMediaBoxLayout(img: HTMLImageElement) {
    const box = img.closest<HTMLElement>('.report-media-box');
    if (!box) return;
    const nw = img.naturalWidth || 0, nh = img.naturalHeight || 0;
    const manualW = /(?:^|;)\s*width\s*:\s*[0-9.]+(?:px|%)/i.test(img.getAttribute('style') || '');
    const floated = img.style.float === 'left' || img.style.float === 'right';
    const portraitCentered = nw > 0 && nh > 0 && nh > nw * 1.1 && !manualW && !floated;
    const s = box.style;
    if (portraitCentered) {
        s.minHeight = `${MEDIA_FULLPAGE_H_PX}px`;
        s.display = 'flex'; s.flexDirection = 'column';
        s.alignItems = 'center'; s.justifyContent = 'center';
        s.marginTop = '0'; s.marginBottom = '0';
        s.breakInside = 'avoid';
        box.dataset.mediaFullPage = '1';
    } else if (s.minHeight) {
        s.minHeight = ''; s.display = ''; s.flexDirection = '';
        s.alignItems = ''; s.justifyContent = '';
        s.marginTop = ''; s.marginBottom = ''; s.breakInside = '';
        delete box.dataset.mediaFullPage;
    }
    // Foto flottante limitata a 0.8 pagine (vedi ReportEditorView).
    if (floated) {
        img.style.maxHeight = `${Math.round(PAGE_H_PX * 0.8)}px`;
    } else if (img.style.maxHeight === `${Math.round(PAGE_H_PX * 0.8)}px`) {
        img.style.maxHeight = '';
    }
}
const PAGE_W_MM = 210;
// Altezza del box "una per pagina" (verticale a dimensione di default): meno di una
// pagina per via del padding dell'editor e del BREATHING (vedi ReportEditorView).
const MEDIA_FULLPAGE_H_PX = PAGE_H_PX - 2 * Math.round(6 * 96 / 25.4); // 812px

// Helper: URL → base64 per jsPDF
async function urlToBase64(url: string): Promise<{ data: string; format: string }> {
    const res = await fetch(url, { mode: 'cors' });
    const blob = await res.blob();
    const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
    const format = blob.type.includes('png') ? 'PNG' : 'JPEG';
    return { data, format };
}

// Padding verticale dell'editor (py-[6mm], uguale in tutte le modalità margine)
const EDITOR_PY_PX = Math.round(6 * 96 / 25.4); // ~23px
// La copertina parte dopo il padding-top dell'editor, quindi è più corta di una pagina
const COVER_H_PX = PAGE_H_PX - EDITOR_PY_PX; // 858 - 23 = 835px

// Sezioni della relazione: "first" (copertina, premessa, identificazione, dati),
// "body" (servizio investigativo) e "last" (clausole di utilizzo, conservazione,
// responsabilità). L'utente le vede singolarmente o tutte insieme dai tab.
// Ogni elemento di blocco del template ha data-rel-section="<section>".
type RelSection = 'first' | 'body' | 'last';
function tagRelSection(html: string, section: RelSection): string {
    // Aggiunge data-rel-section a ogni tag di blocco di primo livello. La regex basta
    // perché l'HTML è generato da noi. <td>/<tr> non si marcano: ereditano dal <table>.
    return html.replace(/<(p|div|table|hr|h[1-6]|ul|ol|blockquote)(\s|>)/gi,
        `<$1 data-rel-section="${section}"$2`);
}

// Mappa token della relazione di bonifica DOCX: stessi valori di
// buildBonificaTemplate, come token → valore per il template riempito dal BE
// (/init-from-template, templateType 'bonifica'). NUMPAGES è un campo Word gestito
// dal BE, TIMBRO viaggia come URL separato.
export function buildBonificaTokens(
    p: any,
    incarichiArr: any[],
    agencyName: string,
    timbroUrl: string = '',
    agencyCity: string = ''
): { tokens: Record<string, string>; timbroUrl: string; perContoProprio: boolean } {
    let d: any = {};
    try {
        const raw = p?.datiAggiuntiviJson;
        d = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
    } catch {}

    const liveCli = p?.client?.[0] || {};
    const cliente = (d._clientSnapshot && Object.keys(d._clientSnapshot).length > 0) ? d._clientSnapshot : liveCli;
    const isAzienda = cliente.tipo === 'Azienda' || cliente.type === 'COMPANY';
    // Spett.le: per l'Azienda ragione sociale e sede legale, non il referente.
    const nomeCliente = isAzienda
        ? String(cliente.ragioneSociale || cliente.denominazioneTerzi || cliente.companyName || cliente.nome || p.clientName || '').trim()
        : `${cliente.nome || p.clientName || ''} ${cliente.surname || ''}`.trim();
    const indirizzoLine1 = isAzienda ? (cliente.indirizzo || cliente.sedeTerzi || '') : (cliente.indirizzo || '');
    const indirizzoLine2 = (() => {
        const cap = isAzienda ? (cliente.cap || cliente.capTerzi || '') : (cliente.cap || '');
        const citta = isAzienda ? (cliente.citta || cliente.comuneTerzi || '') : (cliente.citta || '');
        const prov = isAzienda ? (cliente.provincia || cliente.provinciaTerzi || '') : (cliente.provincia || '');
        return [[cap, citta].filter(Boolean).join(' '), prov].filter(Boolean).join(' (') + (prov ? ')' : '');
    })();

    // "Per conto di" con sede, come in buildBonificaTemplate, in un'unica stringa
    // (nome — sede) per il token PER_CONTO_DI.
    const perContoDi = isAzienda
        ? (cliente.ragioneSociale || cliente.denominazioneTerzi || liveCli.denominazioneTerzi || cliente.nome || '')
        : isPrivatoPerContoProprio(cliente, liveCli) ? ''
            : (cliente.denominazioneTerzi || liveCli.denominazioneTerzi || d.denominazione_terzi || '');
    const perContoSede = isAzienda
        ? [cliente.indirizzo || cliente.sedeTerzi || liveCli.sedeTerzi, cliente.cap || cliente.capTerzi || liveCli.capTerzi, cliente.citta || cliente.comuneTerzi || liveCli.comuneTerzi].filter(Boolean).join(', ')
        : ([cliente.sedeTerzi || liveCli.sedeTerzi, cliente.capTerzi || liveCli.capTerzi, cliente.comuneTerzi || liveCli.comuneTerzi].filter(Boolean).join(', ') || d.sede_terzi || '');
    const perConto = perContoDi ? `${perContoDi}${perContoSede ? ' — ' + perContoSede : ''}` : '';

    // Tabella del soggetto per cui si agisce (T_*), come nella relazione: la società se il
    // cliente è un'azienda, il terzo dichiarato se è un privato per conto di terzi.
    const terzo = isAzienda
        ? {
            denominazione: perContoDi,
            sede: cliente.indirizzo || cliente.sedeTerzi || liveCli.sedeTerzi || '',
            comune: cliente.citta || cliente.comuneTerzi || liveCli.comuneTerzi || '',
            provincia: cliente.provincia || cliente.provinciaTerzi || liveCli.provinciaTerzi || '',
            cap: cliente.cap || cliente.capTerzi || liveCli.capTerzi || '',
            cfIva: cliente.partitaIva || cliente.pivaOrCfTerzi || liveCli.pivaOrCfTerzi || '',
        }
        : perContoDi
            ? {
                denominazione: perContoDi,
                sede: cliente.sedeTerzi || liveCli.sedeTerzi || d.sede_terzi || '',
                comune: cliente.comuneTerzi || liveCli.comuneTerzi || '',
                provincia: cliente.provinciaTerzi || liveCli.provinciaTerzi || '',
                cap: cliente.capTerzi || liveCli.capTerzi || '',
                cfIva: cliente.pivaOrCfTerzi || liveCli.pivaOrCfTerzi || '',
            }
            : { denominazione: '', sede: '', comune: '', provincia: '', cap: '', cfIva: '' };

    // Mandante (sez. 1 "Conferimento dell'incarico"), come nel template HTML.
    const mandante = {
        // Il mandante (art. 135) è la persona fisica che conferisce l'incarico: per
        // l'Azienda è il referente, con la sua residenza. I campi indirizzo del cliente
        // Azienda sono la sede legale e qui non vanno usati.
        nomeCompleto: `${cliente.surname || ''} ${cliente.nome || ''}`.trim(),
        natoA: cliente.luogoNascita || '',
        dataNascita: formatIsoDateToItalian(cliente.dataNascita || ''),
        // Per l'Azienda la residenza del mandante è quella del referente (campi *Referente),
        // non la sede legale.
        luogoResidenza: isAzienda ? (cliente.cittaReferente || '') : (cliente.citta || ''),
        provResidenza: isAzienda ? (cliente.provinciaReferente || '') : (cliente.provincia || ''),
        viaResidenza: isAzienda ? (cliente.indirizzoReferente || '') : (cliente.indirizzo || ''),
        cf: cliente.codiceFiscale || '',
        docTipo: cliente.docTipo || '',
        docNum: cliente.docNumero || '',
    };

    // Data esecuzione = primo giorno di servizio; vuota se non ci sono incarichi.
    const primoGiorno = incarichiArr.filter((i: any) => i?.dataInizio).map((i: any) => i.dataInizio).sort()[0];
    const dataEsec = primoGiorno ? new Date(primoGiorno).toLocaleDateString('it-IT') : '';
    const today = new Date().toLocaleDateString('it-IT');
    // Città di chiusura = sede legale dell'agenzia (settings.city), senza CAP.
    const cityAgency = (String(agencyCity || '').replace(/[,\s]*\b\d{5}\b\s*$/, '').trim())
        || p.sedeNome || p.citta || '';

    const tokens: Record<string, string> = {
        // Intestazione destinatario (copertina):
        INIZIO: isAzienda ? 'Spett.le' : 'Egr.',
        CLIENTE_NOME: nomeCliente,
        CLIENTE_INDIRIZZO: `${indirizzoLine1} ${indirizzoLine2}`.trim(),
        // Sez. 1 — conferimento incarico (tabella Voce/Dato):
        NOME: mandante.nomeCompleto,
        LUOGO: [mandante.natoA, mandante.dataNascita].filter(Boolean).join(' — '), // "Luogo e data di nascita / P. IVA"
        RESIDENZA: [mandante.viaResidenza, mandante.luogoResidenza, mandante.provResidenza].filter(Boolean).join(', '),
        CF: mandante.cf,
        DOCUMENTO: [mandante.docTipo, mandante.docNum].filter(Boolean).join(' '),
        QUALIFICA: '', // "Qualifica / Titolo ad agire" — compilato a mano
        PER_CONTO_DI: perConto,
        // Mandante e soggetto per cui agisce, come nella relazione investigativa.
        M_NOME: mandante.nomeCompleto,
        M_CF: mandante.cf,
        FLAG_PROPRIO: perContoDi ? '☐' : '☑',
        FLAG_PERCONTO: perContoDi ? '☑' : '☐',
        T_DENOMINAZIONE: terzo.denominazione,
        T_SEDE: terzo.sede,
        T_COMUNE: terzo.comune,
        T_PROVINCIA: terzo.provincia,
        T_CAP: terzo.cap,
        T_CFIVA: terzo.cfIva,
        // Sez. 3 — dati dell'intervento tecnico (celle compilate a mano dall'operatore):
        DATA_ESEC: dataEsec,
        LUOGO_ESEC: '',
        ORA_INIZIO: '',
        ORA_FINE: '',
        SOGGETTI_REFERENTI: '',
        ANNOTAZIONI: '',
        BENI_OGGETTO: '',
        // Sez. 5, esiti/note della matrice verifiche: non presenti nel template attuale,
        // inviati vuoti (nessun effetto se il token non c'è nel DOCX).
        NOTE_SOPRAL: '',
        NOTE_TERMOC: '',
        NOTE_VIDEOS: '',
        NOTE_ISP_VIS: '',
        NOTE_ELEM_ACC: '',
        // Chiusura ("Città, data"):
        CITTA: cityAgency,
        DATA: today,
    };
    // Per conto proprio (privato senza terzo) si usa il modello senza la tabella del terzo.
    return { tokens, timbroUrl: timbroUrl || '', perContoProprio: !perContoDi };
}

// Template iniziale precompilato
function buildBonificaTemplate(p: any, incarichiArr: any[], agencyName: string, timbroUrl?: string, agencyCity: string = ''): string {
    let d: any = {};
    try {
        const raw = p?.datiAggiuntiviJson;
        d = typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
    } catch {}

    // Usa lo snapshot salvato alla creazione del mandato; il dato live solo se lo
    // snapshot non c'è (pratiche più vecchie).
    const liveCli = p?.client?.[0] || {};
    const cliente = (d._clientSnapshot && Object.keys(d._clientSnapshot).length > 0) ? d._clientSnapshot : liveCli;
    const isAzienda = cliente.tipo === 'Azienda' || cliente.type === 'COMPANY';
    // Spett.le: per l'Azienda ragione sociale e sede legale, non il referente.
    const nomeCliente = isAzienda
        ? String(cliente.ragioneSociale || cliente.denominazioneTerzi || cliente.companyName || cliente.nome || p.clientName || '').trim()
        : `${cliente.nome || p.clientName || ''} ${cliente.surname || ''}`.trim();
    const indirizzoLine1 = isAzienda ? (cliente.indirizzo || cliente.sedeTerzi || '') : (cliente.indirizzo || '');
    const indirizzoLine2 = (() => {
        const cap = isAzienda ? (cliente.cap || cliente.capTerzi || '') : (cliente.cap || '');
        const citta = isAzienda ? (cliente.citta || cliente.comuneTerzi || '') : (cliente.citta || '');
        const prov = isAzienda ? (cliente.provincia || cliente.provinciaTerzi || '') : (cliente.provincia || '');
        return [[cap, citta].filter(Boolean).join(' '), prov].filter(Boolean).join(' (') + (prov ? ')' : '');
    })();

    // Nr. Mandato intero (può contenere trattini, es. MI-2026-045); dal nome del mandato si toglie
    // solo il " - cliente" finale.
    const rifNum = (d.rif_numero_pratica || (p.name || '').replace(/^mandato\s+/i, '').split(' - ')[0] || '').trim();
    const anno = new Date(d.data_incarico || Date.now()).getFullYear();
    const diritto = d.interesse_diritto_tutelato || '';
    // Per l'Azienda il "per conto di" è la società rappresentata dal referente
    // (ragione sociale e sede dai campi nativi del Client); per il Privato l'eventuale
    // terzo nei campi *Terzi. Come nei PDF del mandato.
    // I campi terzi possono essere stati compilati dopo la creazione del mandato
    // (snapshot vuoto): in quel caso si usa `liveCli`.
    const perContoDi = isAzienda
        ? (cliente.ragioneSociale || cliente.denominazioneTerzi || liveCli.denominazioneTerzi || cliente.nome || '')
        : isPrivatoPerContoProprio(cliente, liveCli) ? ''
            : (cliente.denominazioneTerzi || liveCli.denominazioneTerzi || d.denominazione_terzi || '');
    const perContoSede = isAzienda
        ? [cliente.indirizzo || cliente.sedeTerzi || liveCli.sedeTerzi, cliente.cap || cliente.capTerzi || liveCli.capTerzi, cliente.citta || cliente.comuneTerzi || liveCli.comuneTerzi].filter(Boolean).join(', ')
        : ([cliente.sedeTerzi || liveCli.sedeTerzi, cliente.capTerzi || liveCli.capTerzi, cliente.comuneTerzi || liveCli.comuneTerzi].filter(Boolean).join(', ') || d.sede_terzi || '');
    // Checkbox cliccabili per gli esiti della sez. 10 Conclusioni: span statici che
    // il click handler alterna tra ☐ e ☑ (con data-checked). Lo stato sta
    // nell'innerHTML e quindi viene salvato.
    const cb = () => '<span class="esito-cb" contenteditable="false" data-checked="false" style="cursor:pointer; user-select:none; display:inline-block; font-size:13pt; line-height:1; vertical-align:middle;">&#9744;</span>';


    const mandante = {
        // Il mandante (art. 135) è la persona fisica che conferisce l'incarico: per
        // l'Azienda è il referente, con la sua residenza. I campi indirizzo del cliente
        // Azienda sono la sede legale e qui non vanno usati.
        nomeCompleto: `${cliente.surname || ''} ${cliente.nome || ''}`.trim(),
        natoA: cliente.luogoNascita || '',
        dataNascita: formatIsoDateToItalian(cliente.dataNascita || ''),
        // Per l'Azienda la residenza del mandante è quella del referente (campi *Referente),
        // non la sede legale.
        luogoResidenza: isAzienda ? (cliente.cittaReferente || '') : (cliente.citta || ''),
        provResidenza: isAzienda ? (cliente.provinciaReferente || '') : (cliente.provincia || ''),
        viaResidenza: isAzienda ? (cliente.indirizzoReferente || '') : (cliente.indirizzo || ''),
        cf: cliente.codiceFiscale || '',
        docTipo: cliente.docTipo || '',
        docNum: cliente.docNumero || '',
        docData: formatIsoDateToItalian(cliente.docDataRilascio || ''),
        docDa: cliente.docRilascioDa || '',
    };

    let soggetti: any[] = [];
    if (d.soggetti && Array.isArray(d.soggetti)) {
        // Data di nascita in gg/mm/aaaa anche dall'array (formattazione idempotente).
        soggetti = d.soggetti.map((s: any) => ({ ...s, data_nascita: formatIsoDateToItalian(s.data_nascita || '') }));
    } else if (d.soggetto_nome || d.soggetto_denominazione) {
        soggetti = [{
            nome: d.soggetto_nome || d.soggetto_denominazione,
            nato_a: d.soggetto_nato_a,
            data_nascita: formatIsoDateToItalian(d.soggetto_data_nascita),
            indirizzo: d.soggetto_indirizzo,
            citta: d.soggetto_citta,
            cap: d.soggetto_cap,
            prov: d.soggetto_prov,
        }];
    }

    const primoGiorno = incarichiArr.filter((i: any) => i.dataInizio).map((i: any) => i.dataInizio).sort()[0];
    const primoGiornoStr = primoGiorno ? new Date(primoGiorno).toLocaleDateString('it-IT') : '_________________';
    const today = new Date().toLocaleDateString('it-IT');
    // Città nella riga di chiusura: sede legale dell'agenzia dalle Impostazioni
    // (settings.city), non la sede della pratica.
    const cityAgency = (String(agencyCity || '').replace(/[,\s]*\b\d{5}\b\s*$/, '').trim())
        || p.sedeNome || p.citta || '';
    // Nome istituto: da settings, poi da datiAggiuntiviJson, altrimenti vuoto
    const nomeAzienda = agencyName || d.nome_azienda || '';

    // Stili riutilizzabili: interlinea più stretta nel paragrafo e più spazio tra
    // paragrafi; titleStyle ha più margine sopra per staccarsi dal blocco precedente.
    const pj = 'margin:16px 0; text-align:justify; line-height:1.35;';
    const pj125 = 'margin:16px 0; text-align:justify; line-height:1.25;';
    const pj2 = 'margin:16px 0; text-align:justify; line-height:1;';
    const pl = 'margin:16px 0; line-height:1.35;';
    const pl2 = 'margin:12px 0; line-height:1;';
    const titleStyle = 'font-weight:bold; font-size:12pt; text-transform:uppercase; letter-spacing:0.08em; margin:30px 0 8px 0;';
    const hr = 'border:none; border-top:1px solid #bbb; margin:18px 0;';

    // Tabella dati stile PDF (etichetta | valore)
    const dataRow = (label: string, val: string) => val ? `
    <tr>
      <td style="font-weight:bold; font-size:10pt; padding:3px 10px 3px 0; white-space:nowrap; vertical-align:top; width:220px;">${label}</td>
      <td style="font-size:10pt; padding:3px 0; border-bottom:1px solid #ddd; min-width:200px;">${val}</td>
    </tr>` : '';

    const mandanteTable = `
    <table style="border-collapse:collapse; width:90%; margin:8px 0; letter-spacing:-0.3px;">
      ${dataRow('NOME E COGNOME', mandante.nomeCompleto)}
      ${dataRow('NATO/A A', mandante.natoA)}
      ${dataRow('IN DATA', mandante.dataNascita)}
      ${dataRow('LUOGO DI RESIDENZA', mandante.luogoResidenza)}
      ${dataRow('PROV. DI RESIDENZA', mandante.provResidenza)}
      ${dataRow('VIA DI RESIDENZA', mandante.viaResidenza)}
      ${dataRow('CODICE FISCALE', mandante.cf)}
      ${dataRow('TIPO DOCUMENTO', mandante.docTipo)}
      ${dataRow('NUMERO DOCUMENTO', mandante.docNum)}
      ${dataRow('RILASCIATO IN DATA', mandante.docData)}
      ${dataRow('RILASCIATO DA', mandante.docDa)}
    </table>`;

    const soggettiHtml = soggetti.map((s: any, idx: number) => `
    ${soggetti.length > 1 ? `<p style="${pl}; font-weight:bold; margin-top:14px;">Soggetto ${idx + 1}</p>` : ''}
    <table style="border-collapse:collapse; width:90%; margin:8px 0; letter-spacing:-0.3px;">
      ${dataRow('NOME E COGNOME', s.nome || '')}
      ${dataRow('NATO/A A', s.nato_a || '')}
      ${dataRow('IN DATA', s.data_nascita || '')}
      ${dataRow('LUOGO DI RESIDENZA', s.citta || '')}
      ${dataRow('PROV. DI RESIDENZA', s.prov || '')}
      ${dataRow('VIA DI RESIDENZA', s.indirizzo || '')}
    </table>`).join('');

    // Helper specifici della bonifica.
    // Riga "verifica" della matrice (sez. 5.1/5.2/5.3): nome verifica e ambito.
    // Richiesta/Eseguita usano i radio Sì/No (vedi radioSiNo); "Esito / note" resta
    // modificabile.
    const verRow = (cod: string, nome: string, ambito: string) => `
      <tr>
        <td style="border:0.6px solid #1a1a1a; padding:4px 6px; font-size:9pt;">${nome}</td>
        <td style="border:0.6px solid #1a1a1a; padding:4px 6px; font-size:9pt; color:#555;">${ambito}</td>
        <td contenteditable="false" style="border:0.6px solid #1a1a1a; padding:4px 6px; width:55px; text-align:center; font-size:9pt;">${radioSiNo()}</td>
        <td contenteditable="false" style="border:0.6px solid #1a1a1a; padding:4px 6px; width:55px; text-align:center; font-size:9pt;">${radioSiNo()}</td>
        <td style="border:0.6px solid #1a1a1a; padding:4px 6px; width:120px; font-size:9pt;">&nbsp;</td>
      </tr>`;
    // Radio Sì/No cliccabili per "Richiesta" ed "Eseguita" (sez. 2.1). Come per
    // .esito-cb, sono span contenteditable=false il cui stato (● / ○) sta
    // nell'innerHTML, quindi finisce nella bozza e nel PDF. Il click handler delegato
    // (.tipo-radio) rende esclusive le due opzioni della cella.
    const radioOpt = (val: 'si' | 'no', label: string) =>
        `<span class="tipo-radio" contenteditable="false" data-val="${val}" data-checked="false" style="cursor:pointer; user-select:none; display:inline-block; white-space:nowrap; font-size:9pt; line-height:1.6;">&#9675;&nbsp;${label}</span>`;
    const radioSiNo = () =>
        `<span class="tipo-radio-group" contenteditable="false" style="display:inline-flex; flex-direction:column; align-items:flex-start; gap:1px;">${radioOpt('si', 'Sì')}${radioOpt('no', 'No')}</span>`;
    // Riga "tipologia bonifica" (sez. 2.1): tipologia, Richiesta/Eseguita e note.
    const tipoRow = (tipologia: string, note: string) => `
      <tr>
        <td style="border:0.6px solid #1a1a1a; padding:4px 6px; font-size:9pt; width:200px;">${tipologia}</td>
        <td contenteditable="false" style="border:0.6px solid #1a1a1a; padding:4px 6px; width:60px; text-align:center; font-size:9pt;">${radioSiNo()}</td>
        <td contenteditable="false" style="border:0.6px solid #1a1a1a; padding:4px 6px; width:60px; text-align:center; font-size:9pt;">${radioSiNo()}</td>
        <td style="border:0.6px solid #1a1a1a; padding:4px 6px; font-size:9pt; color:#555;">${note}</td>
      </tr>`;
    // Riga "voce → dato": etichetta a sinistra, cella vuota modificabile a destra.
    // Usata nelle sez. 1 (mandante) e 3 (dati intervento).
    const voceDatoRow = (etichetta: string, precompilato: string = '') => `
      <tr>
        <td style="border:0.6px solid #1a1a1a; padding:5px 7px; font-size:9pt; width:190px; line-height:1.35;">${etichetta}</td>
        <td style="border:0.6px solid #1a1a1a; padding:5px 7px; font-size:9pt; min-height:18px; line-height:1.35;">${precompilato || '&nbsp;'}</td>
      </tr>`;
    // Tabella intestazione "Voce | Dato" comune a sez. 1 e 3.
    const headVoceDato = `
      <thead>
        <tr style="background:#e4ecf5;">
          <th style="border:0.6px solid #1a1a1a; padding:5px 8px; font-size:10pt; text-align:center; width:240px;">Voce</th>
          <th style="border:0.6px solid #1a1a1a; padding:5px 8px; font-size:10pt; text-align:center;">Dato</th>
        </tr>
      </thead>`;
    // Tabella intestazione tipologia bonifica (sez. 2.1).
    const headTipologia = `
      <thead>
        <tr style="background:#e4ecf5;">
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Tipologia</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center; width:60px;">Richiesta</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center; width:60px;">Eseguita</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Note / riferimento beni</th>
        </tr>
      </thead>`;
    // Tabella intestazione matrice verifiche (sez. 5).
    const headMatrice = `
      <thead>
        <tr style="background:#e4ecf5;">
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Verifica tecnica</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Ambito applicato</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center; width:55px;">Richiesta</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center; width:55px;">Eseguita</th>
          <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center; width:120px;">Esito / note</th>
        </tr>
      </thead>`;

    // Stili comuni per le tabelle bonifica (full-width, bordi sottili).
    const tblStyle = 'border-collapse:collapse; width:100%; margin:8px 0 12px;';
    // margin-top deve restare 0: applyPageBreaks misura solo il border-box e un
    // margine superiore su un titolo a inizio pagina diventerebbe una banda vuota
    // sotto l'header, sfasando l'allineamento. La spaziatura viene dal margin-bottom
    // del blocco precedente, come in ReportEditorView.
    const sectionTitle = 'font-weight:bold; font-size:13pt; text-transform:uppercase; letter-spacing:0.05em; margin:0 0 4px;';
    const sectionTitle2 = 'font-weight:bold; font-size:13pt; text-transform:uppercase; letter-spacing:0.05em; margin:0 0 4px;line-height:1';
    const subSectionTitle = 'font-weight:bold; font-size:11pt; margin:0 0 6px;';
    const subSectionTitle2 = 'font-weight:bold; font-size:11pt; margin:0 0 4px;';

    // Sezione "first": copertina della relazione di bonifica.
    // - "Spett.le" in alto a destra in un box bordato
    // - titolo "RELAZIONE TECNICA" centrato in un box bordato
    // - sottotitolo "BONIFICA AMBIENTALE, ELETTRONICA, INFORMATICA, TELEFONICA E VEICOLARE"
    // Niente premessa investigativa: è una relazione tecnica.
    const firstHtml = `<!-- ═══ PAGINA 1 — COPERTINA RELAZIONE BONIFICA ═══ -->
<div data-cover-page style="height:${COVER_H_PX}px; overflow:hidden; display:flex; flex-direction:column; box-sizing:border-box; font-family:'Tinos','Times New Roman',Times,serif;">
  <!-- Blocco indirizzo destinatario in box "quadratino" (alto, destra) -->
  <div style="display:flex; justify-content:flex-end; margin-top:40px; margin-right:30px;">
    <div style="border:1px solid #1a1a1a; padding:14px 22px; font-size:11pt; line-height:1.55; min-width:250px; max-width:50%;">
      <p style="margin:1px 0;">${isAzienda ? 'Spett.le' : 'Egr.'}</p>
      <p style="margin:2px 0 0px 18px; font-weight:bold;">${nomeCliente || '___________________'}</p>
      ${indirizzoLine1 ? `<p style="margin:2px 0 0px 18px;">${indirizzoLine1}</p>` : ''}
      ${indirizzoLine2 ? `<p style="margin:2px 0 0px 18px;">${indirizzoLine2}</p>` : ''}
    </div>
  </div>

  <!-- Box titolo grande "RELAZIONE TECNICA" + sottotitolo.
       Replica fedele del PDF demo Agfox: il box titolo è centrato a metà
       pagina con titolo grande e sotto la dicitura delle bonifiche coperte. -->
  <div style="margin:200px auto 6px; max-width:85%; border:1px solid #1a1a1a; padding:18px 18px; background:#fafafa; font-family:'Tinos','Times New Roman',Times,serif; text-align:center;">
    <p style="margin:0; font-size:24pt; font-weight:bold; text-transform:uppercase; letter-spacing:0.04em; font-family:'Tinos','Times New Roman',Times,serif;">Relazione Tecnica</p>
    <p style="margin:14px 0 0; font-size:12pt; font-weight:bold; text-transform:uppercase; letter-spacing:0.02em;">Bonifica Ambientale, Elettronica, Informatica,<br/>Telefonica e Veicolare</p>
  </div>
</div>

`;

    // Sezione "body": verbale di bonifica (sez. 1-13) con tabelle precompilate e
    // celle vuote che l'operatore compila a mano. Nelle celle "Richiesta/Eseguita"
    // si mette una "X" o "✓".
    const bodyHtmlRaw = `<!-- ═══ SEZIONE BODY — VERBALE BONIFICA AMBIENTALE (sez. 1-13) ═══ -->

<!-- ═══ 1. CONFERIMENTO DELL'INCARICO ═══ -->
<p data-section-start="body" style="${sectionTitle}">1. Conferimento dell'incarico</p>
<p style="${pj}">L'attività tecnica è stata svolta su mandato di</p>
<table style="${tblStyle}">
  ${headVoceDato}
  <tbody>
    ${voceDatoRow('Nome e cognome / Ragione sociale', mandante.nomeCompleto)}
    ${voceDatoRow('Luogo e data di nascita / P. IVA', [mandante.natoA, mandante.dataNascita].filter(Boolean).join(' — '))}
    ${voceDatoRow('Residenza / Via Sede legale', [mandante.viaResidenza, mandante.luogoResidenza, mandante.provResidenza].filter(Boolean).join(', '))}
    ${voceDatoRow('Codice fiscale', mandante.cf)}
    ${voceDatoRow('Documento di identificazione', [mandante.docTipo, mandante.docNum].filter(Boolean).join(' '))}
    ${voceDatoRow('Qualifica / Titolo ad agire', '')}
    ${voceDatoRow("Il Committente agisce in nome e per conto di", perContoDi || '')}
  </tbody>
</table>
<p style="${pj}">L'incarico è stato conferito in favore dello scrivente Istituto Investigativo, autorizzato ai sensi dell'art. 134 del T.U.L.P.S., per lo svolgimento di attività di bonifica tecnica ambientale, elettronica, informatica, telefonica e veicolare presso i luoghi, i beni, gli apparati, i dispositivi e i sistemi indicati dal Committente.</p>
<p style="${pj}">L'attività è stata richiesta al fine di tutelare la sicurezza, la riservatezza e l'integrità dei luoghi, dei beni, dei dispositivi e dei sistemi sottoposti a verifica, mediante controlli tecnici finalizzati all'eventuale individuazione di dispositivi, apparati, sistemi, software, configurazioni o strumenti non autorizzati di captazione, registrazione, trasmissione audio/video, localizzazione, accesso o acquisizione non consentita di dati e informazioni.</p>
<p style="${pj}">Il Committente ha indicato i beni oggetto di verifica e ne ha dichiarato la legittima disponibilità, nonché il titolo a richiedere l'esecuzione delle attività tecniche. Le verifiche sono state eseguite esclusivamente sui beni, sui luoghi, sui dispositivi e sugli apparati indicati e autorizzati dal Committente.</p>

<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<!-- ═══ 2. PERIMETRO DELL'INTERVENTO ═══ -->
<p style="${sectionTitle}">2. Perimetro dell'intervento</p>
<p style="${pj}">La relazione è organizzata secondo un criterio progressivo: prima viene definito il tipo di bonifica richiesta, poi vengono individuati i beni oggetto di ispezione, quindi sono riportate le tecniche di verifica effettivamente eseguite e i relativi esiti.</p>

<p style="${subSectionTitle}">2.1 Tipologia di bonifica richiesta</p>
<table style="${tblStyle}">
  ${headTipologia}
  <tbody>
    ${tipoRow('Bonifica ambientale / immobiliare', 'Uffici, abitazioni, locali, sale riunioni, magazzini, aree tecniche')}
    ${tipoRow('Bonifica veicolare', 'Autovetture, furgoni, mezzi aziendali, motoveicoli, natanti, altri mezzi')}
    ${tipoRow('Bonifica elettronica / oggetti e apparati', 'Arredi, suppellettili, alimentatori, power bank, orologi, penne, oggetti di uso comune')}
    ${tipoRow('Bonifica telefonica', 'Smartphone, telefoni, tablet, SIM, profili, impostazioni e servizi')}
    ${tipoRow('Bonifica informatica', 'PC, notebook, server, supporti dati, periferiche e account indicati')}
    ${tipoRow('Verifica reti e impianti', 'Linee telefoniche, rete dati, router, switch, access point, centralini, DVR/NVR, IoT')}
    ${tipoRow('Intervento misto / integrato', 'Intervento comprendente più categorie di verifica')}
  </tbody>
</table>

<!-- ═══ 3. DATI DELL'INTERVENTO TECNICO ═══ -->
<p style="${sectionTitle}">3. Dati dell'intervento tecnico</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;">
      <th style="border:0.6px solid #1a1a1a; padding:6px 9px; font-size:10pt; text-align:center; width:240px;">Voce</th>
      <th style="border:0.6px solid #1a1a1a; padding:6px 9px; font-size:10pt; text-align:center;">Dato / annotazioni</th>
    </tr>
  </thead>
  <tbody>
    ${voceDatoRow('Data esecuzione', primoGiornoStr !== '_________________' ? primoGiornoStr : '')}
    ${voceDatoRow('Luogo di esecuzione', '')}
    ${voceDatoRow('Ora inizio operazioni', '')}
    ${voceDatoRow('Ora fine operazioni', '')}
    ${voceDatoRow('Soggetti presenti / referenti', '')}
    ${voceDatoRow('Annotazioni preliminari', '')}
    ${voceDatoRow('Beni oggetto di verifica', '')}
  </tbody>
</table>

<p></br></p>
<!-- ═══ 4. METODOLOGIA OPERATIVA ═══ -->
<p style="${sectionTitle}">4. Metodologia operativa</p>
<p style="${pj}">Le attività sono state eseguite procedendo dal generale al particolare, secondo una sequenza logica di verifica: delimitazione del perimetro autorizzato, osservazione preliminare, ispezione visiva e manuale, controllo degli elementi fisicamente accessibili, verifica strumentale, approfondimento dei punti sensibili e annotazione dei riscontri tecnici.</p>
<p style="${pj}">Le tecniche di controllo sono state selezionate in funzione della tipologia di bonifica richiesta e dei beni effettivamente sottoposti a verifica. La stessa tecnica strumentale può essere applicata a più categorie di beni, quali ambienti, veicoli, oggetti, apparati elettronici, impianti e dispositivi informatici o telefonici.</p>

<!-- ═══ 5. MATRICE GENERALE DELLE VERIFICHE ESEGUITE ═══ -->
<p style="${sectionTitle}">5. Matrice generale delle verifiche eseguite</p>
<p style="${pj}">Nella matrice seguente sono riportate le verifiche tecniche disponibili per l'intervento. La colonna 'Ambito applicato' consente di indicare se la verifica è stata eseguita su ambienti, veicoli, dispositivi, oggetti, impianti o sistemi specifici.</p>

<p style="${subSectionTitle}">5.1 Operazioni preliminari e ispezioni fisiche</p>
<table style="${tblStyle}">
  ${headMatrice}
  <tbody>
    ${verRow('A1', 'Sopralluogo tecnico e mappatura punti sensibili', 'Ambienti / veicoli / dispositivi / impianti')}
    ${verRow('A2', 'Ispezione visiva e manuale generale', 'Tutti i beni sottoposti a verifica')}
    ${verRow('A3', 'Apertura, smontaggio e controllo di elementi accessibili', 'Arredi / prese / pannelli / vani / componenti veicolo')}
    ${verRow('A4', 'Ispezione con videosonda / boroscopio', 'Intercapedini / tubazioni / vani tecnici / cavità / componenti veicolo')}
    ${verRow('A5', 'Ispezione con termocamera', 'Ambienti / impianti / apparati / veicoli / alimentazioni')}
  </tbody>
</table>

<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${subSectionTitle}">5.2 Verifiche radiofrequenza e telecomunicazioni</p>
<table style="${tblStyle}">
  ${headMatrice}
  <tbody>
    ${verRow('B1', 'Rilevamento passivo radiofrequenza', 'Ambienti / veicoli / dispositivi / oggetti')}
    ${verRow('B2', 'Rilevamento attivo con stimolazione fonica/vibrazionale', 'Ambienti / oggetti / arredi / veicoli')}
    ${verRow('B3', 'Analisi spettro RF 0 - 3 GHz', 'RF analogiche/digitali, GSM, DECT, 433/868/915 MHz, Wi-Fi 2.4')}
    ${verRow('B4', 'Analisi spettro RF 3 - 6 GHz', 'Wi-Fi 5 GHz, apparati digitali, microcamere e sistemi wireless')}
    ${verRow('B5', 'Analisi spettro RF 6 - 12 GHz', 'Trasmettitori digitali, microcamere, link radio e segnali a corto raggio')}
    ${verRow('B6', 'Analisi spettro RF oltre 12 GHz', 'Verifiche estese su bande superiori con strumentazione professionale')}
    ${verRow('B7', 'Verifica sistemi GSM / GPRS / UMTS / LTE / 5G', 'Tracker, microspie cellulari, apparati SIM, dispositivi dati')}
    ${verRow('B8', 'Verifica Wi-Fi / Bluetooth / BLE / UWB / IoT', 'Dispositivi wireless, localizzatori, sensori e apparati connessi')}
  </tbody>
</table>

<p style="${subSectionTitle}">5.3 Verifiche elettroniche, cablate, non trasmissive e impiantistiche</p>
<table style="${tblStyle}">
  ${headMatrice}
  <tbody>
    ${verRow('C1', 'Rilevatore di giunzioni non lineari (NLJD)', 'Ambienti / veicoli / oggetti / arredi / apparati / componenti accessibili')}
    ${verRow('C2', 'Ricerca microcamere con verifica ottica', 'Fori, superfici, arredi, oggetti, ambienti, abitacoli veicolari')}
    ${verRow('C3', 'Ricerca dispositivi cablati e registratori non trasmittenti', 'Ambienti / oggetti / veicoli / apparati')}
    ${verRow('C4', 'Verifica onde convogliate / rete elettrica', 'Prese, cablaggi, adattatori, PLC, alimentazioni')}
    ${verRow('D1', 'Verifica linee telefoniche analogiche/digitali', 'Prese, derivazioni, centralini, cablaggi telefonici')}
    ${verRow('D2', 'Verifica rete dati e apparati di rete', 'Router, switch, access point, firewall, DVR/NVR, NAS, IoT')}
  </tbody>
</table>

<!-- ═══ 6. DESCRIZIONE DELLE PRINCIPALI VERIFICHE TECNICHE ═══ -->
<p style="${sectionTitle}">6. Descrizione delle principali verifiche tecniche</p>
<p style="${pj}">Le descrizioni che seguono indicano le modalità operative delle verifiche tecniche. In relazione finale devono intendersi riferite alle sole attività effettivamente selezionate, eseguite e riportate nelle sezioni precedenti.</p>

<p style="${subSectionTitle}">6.1 Ispezioni fisiche, visive e manuali</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;"><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center; width:200px;">Verifica</th><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center;">Modalità tecnica</th></tr>
  </thead>
  <tbody>
    ${voceDatoRow('Ispezione visiva e manuale', "Controllo diretto dei beni e dei luoghi indicati, con verifica di arredi, oggetti, superfici, pannelli, prese, canaline, vani, apparati elettronici, componenti veicolari accessibili e ulteriori punti sensibili. L'attività è finalizzata all'individuazione di anomalie fisiche, fori, manomissioni, cablaggi non riconducibili all'uso ordinario, dispositivi occultati o elementi tecnicamente sospetti.")}
    ${voceDatoRow('Apertura e smontaggio di elementi accessibili', "Apertura o rimozione di elementi accessibili e autorizzati, quali placche, pannelli, coperchi, vani tecnici, controsoffitti, pavimenti tecnici, parti di arredo, elementi di abitacolo o vani veicolari. L'attività è eseguita senza opere invasive non autorizzate e con attenzione alla conservazione dello stato dei beni.")}
    ${voceDatoRow('Videosonda / boroscopio', 'Ispezione di cavità, anfratti, tubazioni, canaline, intercapedini, controsoffitti, pavimenti tecnici, vani tecnici e spazi non direttamente visibili, mediante sonda ottica o telecamera endoscopica. La verifica consente di controllare punti nascosti o difficilmente raggiungibili senza procedere a smontaggi estesi.')}
    ${voceDatoRow('Termocamera', 'Verifica mediante analisi termografica di eventuali anomalie di temperatura compatibili con alimentazioni, batterie, apparati elettronici attivi, adattatori, alimentatori, cablaggi, componenti in funzione o sorgenti termiche non coerenti con il contesto ispezionato.')}
    ${voceDatoRow('Ricerca microcamere con strumenti ottici', 'Controllo di fori, superfici riflettenti, oggetti orientati verso aree sensibili, arredi, pareti, soffitti, abitacoli e punti di occultamento mediante strumenti ottici, illuminatori e sistemi di evidenziazione delle lenti, finalizzati alla ricerca di microcamere o apparati di ripresa occultati.')}
  </tbody>
</table>

<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${subSectionTitle}">6.2 Verifiche radiofrequenza e telecomunicazioni</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;"><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center; width:200px;">Verifica</th><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center;">Modalità tecnica</th></tr>
  </thead>
  <tbody>
    ${voceDatoRow('Rilevamento RF passivo', 'Rilevamento delle emissioni radio presenti in condizioni ordinarie, con attenzione a segnali persistenti, intermittenti, anomali o non coerenti con le sorgenti note. La verifica interessa microtrasmettitori, microcamere, apparati audio/video, moduli dati, sistemi wireless e dispositivi attivi al momento del controllo.')}
    ${voceDatoRow('Rilevamento RF attivo', 'Stimolazione fonica, acustica o vibrazionale dei luoghi e dei beni ispezionati, finalizzata a sollecitare eventuali dispositivi ad attivazione vocale, intermittente o sensibili al rumore ambientale.')}
    ${voceDatoRow('Analisi spettro 0 - 3 GHz', 'Analisi delle bande radio più ricorrenti per dispositivi analogici e digitali, radiomicrofoni, trasmettitori, moduli GSM/UMTS/LTE, 433/868/915 MHz, DECT e Wi-Fi 2.4 GHz, con valutazione dei segnali rispetto al contesto.')}
    ${voceDatoRow('Analisi spettro 3 - 6 GHz', 'Analisi delle bande superiori utilizzate da sistemi wireless, Wi-Fi 5 GHz, link digitali, apparati audio/video, microcamere e sistemi di comunicazione a corto raggio.')}
    ${voceDatoRow('Analisi spettro 6 - 12 GHz', 'Estensione della verifica su bande superiori potenzialmente impiegate da apparati digitali, microcamere, moduli miniaturizzati, link radio e sistemi di trasmissione a corto raggio.')}
    ${voceDatoRow('Analisi spettro oltre 12 GHz', "Analisi estesa delle bande superiori con strumentazione professionale, finalizzata alla verifica di segnali e trasmissioni non rilevabili nelle bande inferiori, secondo le modalità operative previste dall'intervento.")}
    ${voceDatoRow('GSM / GPRS / UMTS / LTE / 5G', "Verifica di possibili dispositivi dotati di modulo cellulare, scheda SIM, trasmissione dati, ascolto remoto, localizzazione o controllo a distanza. L'attività considera fasi di aggancio rete, chiamata, scambio dati o trasmissione attiva.")}
    ${voceDatoRow('Wi-Fi / Bluetooth / BLE / UWB / IoT', 'Verifica di dispositivi a corto raggio e apparati connessi, quali moduli Wi-Fi, Bluetooth, BLE, UWB, sensori, localizzatori, dispositivi IoT, access point non riconosciuti e sistemi in comunicazione locale.')}
  </tbody>
</table>

<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<div data-page-break="1" contenteditable="false" style="position:relative;height:0;margin:0;border-top:2px dashed #a5b4fc;user-select:none;"><span style="position:absolute;top:-8px;left:50%;transform:translateX(-50%);background:#ffffff;padding:0 8px;color:#4f46e5;font-size:10px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;white-space:nowrap;">Interruzione di pagina</span></div>
<p style="${subSectionTitle}">6.3 Verifiche elettroniche, cablate e non trasmissive</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;"><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center; width:200px;">Verifica</th><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center;">Modalità tecnica</th></tr>
  </thead>
  <tbody>
    ${voceDatoRow('Rilevatore di giunzioni non lineari (NLJD)', "Verifica mediante rilevatore di giunzioni non lineari, finalizzata all'individuazione di componenti elettroniche, semiconduttori, circuiti, microapparati o dispositivi occultati anche in assenza di emissione radio. La verifica può essere applicata a pareti, arredi, oggetti, apparati, vani tecnici, componenti veicolari accessibili e ulteriori beni indicati dal Committente.")}
    ${voceDatoRow('Dispositivi cablati e registratori allo stato solido', 'Ricerca di microfoni cablati, registratori audio, dispositivi digitali, memorie, apparati alimentati localmente o sistemi non necessariamente trasmittenti, compresi dispositivi spenti, in stand-by, programmati o privi di emissione radio al momento del controllo.')}
    ${voceDatoRow('Onde convogliate / rete elettrica', 'Verifica di eventuali sistemi che utilizzano il cablaggio elettrico per trasmettere segnali, dati o comunicazioni, con controllo di prese, alimentatori, adattatori, apparati PLC, moduli di comunicazione e cablaggi elettrici accessibili.')}
    ${voceDatoRow('Linee telefoniche analogiche e digitali', 'Controllo di prese, derivazioni, cablaggi, centralini, adattatori, apparati intermedi, microtraslatori, dispositivi di registrazione, deviazioni o anomalie tecniche compatibili con ascolto, registrazione o instradamento non autorizzato delle comunicazioni.')}
    ${voceDatoRow('Rete dati e apparati di rete', 'Verifica di modem, router, switch, access point, firewall, cablaggi dati, NAS, DVR/NVR, telecamere IP, apparati IoT e configurazioni di rete indicate, con attenzione a dispositivi non riconosciuti, collegamenti anomali, accessi o configurazioni non coerenti con il contesto.')}
  </tbody>
</table>

<p style="${subSectionTitle}">6.4 Verifiche su dispositivi informatici e telefonici</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;"><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center; width:200px;">Verifica</th><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center;">Modalità tecnica</th></tr>
  </thead>
  <tbody>
    ${voceDatoRow('Smartphone e tablet', 'Verifica di dispositivi mobili indicati o consegnati dal Committente, con controllo di applicazioni installate, permessi, profili di configurazione, servizi attivi, impostazioni di rete, localizzazione, inoltro dati, registrazione ambientale, accessi e ulteriori indicatori compatibili con software spia, controllo remoto o configurazioni non autorizzate.')}
    ${voceDatoRow('Personal computer e notebook', 'Verifica hardware e software dei computer indicati, con controllo di programmi installati, servizi e processi attivi, periferiche collegate, accessi, configurazioni di sicurezza, strumenti di controllo remoto, spyware, malware, backdoor e indicatori tecnici compatibili con attività informatiche non autorizzate.')}
    ${voceDatoRow('Supporti e periferiche', 'Controllo di supporti removibili, periferiche collegate, adattatori, webcam, microfoni, alimentatori, docking station e dispositivi esterni che possano essere utilizzati per registrazione, trasmissione, accesso non autorizzato o acquisizione di dati.')}
  </tbody>
</table>

<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<p style="${pl}">&nbsp;</p>
<div data-page-break="1" contenteditable="false" style="position:relative;height:0;margin:0;border-top:2px dashed #a5b4fc;user-select:none;"><span style="position:absolute;top:-8px;left:50%;transform:translateX(-50%);background:#ffffff;padding:0 8px;color:#4f46e5;font-size:10px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;white-space:nowrap;">Interruzione di pagina</span></div>
<p style="${subSectionTitle}">6.5 Verifiche specifiche su veicoli</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;"><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center; width:200px;">Verifica</th><th style="border:0.6px solid #1a1a1a; padding:6px 8px; font-size:10pt; text-align:center;">Modalità tecnica</th></tr>
  </thead>
  <tbody>
    ${voceDatoRow('Ispezione veicolare esterna e interna', "Controllo di carrozzeria, paraurti, passaruota, sottoscocca, vano motore, abitacolo, sedili, vani portaoggetti, bagagliaio, pannellature accessibili e ulteriori punti idonei all'occultamento di tracker, microspie, registratori o apparati alimentati a batteria.")}
    ${voceDatoRow('Verifica tracker GPS/GSM', "Ricerca di localizzatori, moduli magnetici, dispositivi GPS/GNSS, tracker con SIM, apparati a batteria o collegati all'impianto del veicolo, mediante ispezione fisica e verifiche radiofrequenza/cellulari.")}
    ${voceDatoRow('Verifica elettrica e OBD', 'Controllo dei punti di alimentazione e collegamento accessibili, compresi presa OBD, cablaggi, alimentazioni aggiunte, adattatori, apparati non riconducibili alla dotazione ordinaria del mezzo e anomalie tecniche rilevabili in sicurezza.')}
    ${voceDatoRow('NLJD su componenti veicolari', 'Applicazione del rilevatore di giunzioni non lineari su componenti, vani, oggetti, pannelli e punti accessibili del veicolo, al fine di individuare componenti elettroniche o circuiti occultati anche se non trasmittenti al momento della verifica.')}
    ${voceDatoRow('Termocamera e videosonda su veicolo', 'Verifica di anomalie termiche e controllo di cavità, vani e punti non direttamente visibili mediante termocamera e videosonda/boroscopio, in relazione alla struttura del mezzo e alle parti accessibili.')}
  </tbody>
</table>

<!-- ═══ 7. STRUMENTAZIONE UTILIZZATA ═══ -->
<p style="${sectionTitle}">7. Strumentazione utilizzata</p>
<p style="${pj}">Le molteplici tipologie di minaccia alla riservatezza degli ambienti richiedono l'impiego di strumentazione tecnica idonea, costantemente aggiornata e adeguata alle diverse modalità di captazione, registrazione, trasmissione e localizzazione oggi disponibili.</p>
<p style="${pj}">L'attività di bonifica tecnica ambientale è stata svolta mediante l'utilizzo di apparecchiature professionali dedicate alla rilevazione di segnali radiofrequenza, trasmissioni analogiche e digitali, dispositivi GSM/UMTS/LTE/5G, apparati Wi-Fi/Bluetooth, microcamere, dispositivi audio/video, localizzatori e ulteriori sistemi potenzialmente idonei alla captazione non autorizzata di conversazioni, immagini, dati o informazioni.</p>
<p style="${pj}">Sono state inoltre impiegate, ove necessario, attrezzature per l'analisi dello spettro radio, rilevatori di radiofrequenza fino a 12 GHz/+12&nbsp;GHz, sistemi di verifica ottica per l'individuazione di microcamere occultate, strumenti per l'ispezione visiva e manuale di arredi, impianti, punti elettrici, controsoffittature, pavimenti tecnici e ulteriori aree potenzialmente idonee all'occultamento di dispositivi.</p>
<p style="${pj}">La costante evoluzione delle tecnologie di intercettazione e controllo rende necessario un continuo aggiornamento delle metodologie operative e delle conoscenze tecniche, al fine di eseguire verifiche sempre più complete, mirate e coerenti con le possibili minacce presenti nel contesto oggetto di analisi.</p>
<p style="${pj}">Le attrezzature utilizzate sono strumenti professionali prodotti da aziende specializzate nel settore della sicurezza tecnica, della rilevazione elettronica e della bonifica ambientale.</p>
<p style="${pj2}"></p>
<!-- ═══ 8. NOTE TECNICHE SULL'INTERVENTO ═══ -->
<div data-page-break="1" contenteditable="false" style="position:relative;height:0;margin:0;border-top:2px dashed #a5b4fc;user-select:none;"><span style="position:absolute;top:-8px;left:50%;transform:translateX(-50%);background:#ffffff;padding:0 8px;color:#4f46e5;font-size:10px;font-weight:600;letter-spacing:0.06em;text-transform:uppercase;white-space:nowrap;">Interruzione di pagina</span></div>
<p style="${sectionTitle}">8. Note tecniche sull'intervento</p>
<p style="${pj}">L'attività è stata eseguita alla presenza di personale autorizzato dal Committente e si è sviluppata mediante un'analisi progressiva dei beni indicati, procedendo per singole aree, apparati, dispositivi e punti di interesse.</p>
<p style="${pj}">Le verifiche sono state svolte attraverso attività strumentali, visive e manuali, con particolare attenzione ai punti maggiormente compatibili con l'occultamento di dispositivi di captazione, registrazione, trasmissione, localizzazione, accesso o acquisizione non autorizzata di dati e informazioni.</p>
<p style="${pj}">L'intervento è stato condotto secondo criteri di riservatezza, accuratezza tecnica e proporzionalità, compatibilmente con lo stato dei luoghi e dei beni, con le autorizzazioni ricevute e con l'effettiva accessibilità delle aree o dei dispositivi da sottoporre a verifica.</p>

<!-- ═══ 9. RISCONTRI TECNICI E ANOMALIE ═══ -->
<p style="${sectionTitle}">9. Riscontri tecnici e anomalie</p>
<p style="${pj}">I riscontri tecnici emersi durante l'intervento sono riportati nella tabella seguente.</p>
<table style="${tblStyle}">
  <thead>
    <tr style="background:#e4ecf5;">
      <th style="border:0.6px solid #1a1a1a; padding:6px 6px; font-size:9.5pt; text-align:center; width:30px;">N.</th>
      <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Bene / area</th>
      <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Riscontro tecnico</th>
      <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Tecnica di verifica</th>
      <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Esito approfondimento</th>
      <th style="border:0.6px solid #1a1a1a; padding:6px 7px; font-size:9.5pt; text-align:center;">Azioni / note</th>
    </tr>
  </thead>
  <tbody>
    ${[1,2,3,4].map(n => `
      <tr>
        <td style="border:0.6px solid #1a1a1a; padding:8px 6px; text-align:center; font-weight:bold; font-size:10pt; vertical-align:top;">${n}</td>
        <td style="border:0.6px solid #1a1a1a; padding:8px 7px; font-size:9.5pt; min-height:50px; vertical-align:top;">&nbsp;</td>
        <td style="border:0.6px solid #1a1a1a; padding:8px 7px; font-size:9.5pt; vertical-align:top;">&nbsp;</td>
        <td style="border:0.6px solid #1a1a1a; padding:8px 7px; font-size:9.5pt; vertical-align:top;">&nbsp;</td>
        <td style="border:0.6px solid #1a1a1a; padding:8px 7px; font-size:9.5pt; vertical-align:top;">&nbsp;</td>
        <td style="border:0.6px solid #1a1a1a; padding:8px 7px; font-size:9.5pt; vertical-align:top;">&nbsp;</td>
      </tr>`).join('')}
  </tbody>
</table>

<!-- ═══ 10. CONCLUSIONI ═══ -->
<p style="${sectionTitle}">10. Conclusioni</p>
<p style="${pj125}">Sulla base delle attività tecniche eseguite, della strumentazione impiegata, dei beni effettivamente sottoposti a verifica e delle condizioni operative riscontrate al momento dell'intervento, lo scrivente Istituto Investigativo formula le seguenti conclusioni.</p>

<p style="font-weight:bold; font-size:10.5pt; margin:14px 0 6px;line-height:1">Esito finale dell'intervento:</p>
<p style="${pl2}">${cb()}&nbsp;Negativo</p>
<p style="${pl2}">${cb()}&nbsp;Positivo</p>
<p style="${pl2}">${cb()}&nbsp;Presenza di anomalie / elementi meritevoli di approfondimento</p>
<p style="${pl2}">${cb()}&nbsp;Non determinabile con certezza per limiti tecnici, ambientali o operativi</p>

<p style="${pj125}">In caso di <strong>esito negativo</strong>, dalle verifiche effettuate non sono emersi, al momento dell'intervento, elementi tecnici riconducibili alla presenza di dispositivi, apparati, sistemi, software, configurazioni o strumenti non autorizzati finalizzati alla captazione, registrazione, trasmissione, localizzazione, accesso o acquisizione non autorizzata di dati, immagini, suoni, comunicazioni o informazioni.</p>
<p style="${pj125}">In caso di <strong>esito positivo</strong> o di <strong>presenza di anomalie</strong>, i riscontri rilevati sono descritti nella sezione dedicata della presente relazione, con indicazione delle verifiche eseguite, delle valutazioni tecniche effettuate e delle eventuali attività di approfondimento ritenute opportune.</p>
<p style="${sectionTitle2}">11. Precisazione sull'esito</p>
<p style="${pj125}">L'esito indicato al precedente punto 10 costituisce la sintesi conclusiva delle verifiche tecniche effettuate e deve essere letto congiuntamente ai riscontri, alle eventuali anomalie e alle limitazioni operative descritte nelle precedenti sezioni della presente relazione.</p>

<p style="${pj2}">Esso è riferito esclusivamente alle condizioni riscontrate al momento dell'intervento e agli elementi effettivamente sottoposti a verifica.</p>


<!-- ═══ 12. AVVERTENZE FINALI ═══ -->
<p style="${sectionTitle}">12. Avvertenze finali</p>
<p style="${pj125}">Gli esiti riportati nella presente relazione si riferiscono esclusivamente ai beni, ai luoghi, agli apparati, ai dispositivi e ai sistemi effettivamente sottoposti a verifica, nonché allo stato tecnico rilevato al momento dell'intervento.</p>
<p style="${pj125}">L'attività di bonifica tecnica non consente di attestare con valore assoluto o retroattivo l'eventuale presenza, operatività o rimozione di dispositivi che possano essere stati presenti o attivi prima dell'esecuzione delle verifiche, né di estendere gli esiti a beni o aree non indicati, non accessibili o non autorizzati dal Committente.</p>
<p style="${pj125}">La relazione documenta le attività tecniche eseguite e i relativi riscontri, nei limiti dell'incarico conferito, della strumentazione impiegata e delle condizioni operative riscontrate.</p>

<!-- ═══ 13. RACCOMANDAZIONI TECNICHE ═══ -->
<p style="${sectionTitle}">13. Raccomandazioni tecniche</p>
<p style="${pj125}">In presenza di dubbi, sospetti o eventi anomali riconducibili a possibili attività di intercettazione, captazione, localizzazione, accesso o controllo non autorizzato, si consiglia di richiedere l'intervento tecnico nell'immediatezza dell'evento o nel più breve tempo possibile.</p>
<p style="${pj125}">Per ambienti, veicoli o dispositivi particolarmente sensibili, può essere valutata l'esecuzione di controlli periodici, l'applicazione di sigilli antimanomissione su punti selezionati, la definizione di procedure di accesso controllato e l'adozione di misure tecniche e organizzative coerenti con il livello di rischio rilevato.</p>`;

    // Sezione "last": sez. 14 (clausole 14.1-14.6), citazione del Reg. provvedimenti,
    // chiusura "Tanto si è accertato...", numero pagine, città e data, timbro.
    // data-section-start="last" forza il salto pagina.
    const lastHtmlRaw = `<!-- ═══ SEZIONE LAST — CLAUSOLE UTILIZZO, CONSERVAZIONE E RESPONSABILITÀ ═══ -->
<p data-section-start="last" style="${sectionTitle}">14. Clausole utilizzo, conservazione e responsabilità</p>

<p style="${subSectionTitle}">14.1 Fondamento dell'Incarico</p>
<p style="${pj2}">La presente Relazione è redatta in esecuzione di incarico formalmente conferito dal Mandante a:</p>
<p style="text-align:left; font-weight:bold; text-transform:uppercase; margin:1px 0 8px; font-size:10pt; line-height:1; letter-spacing:0.02em;">${nomeAzienda || '___________________'}</p>

<p style="${subSectionTitle}">14.2 Natura e Contenuto Relazione</p>
<p style="${pj125}">La presente Relazione costituisce fedele e obiettiva trasposizione delle attività tecniche effettivamente eseguite, con esposizione dei fatti accertati senza formulazione di valutazioni soggettive eccedenti l'oggetto dell'incarico.</p>
<p style="${pj125}">Gli eventuali allegati fotografici o audiovisivi rappresentano documentazione diretta delle attività svolte.</p>

<p style="${subSectionTitle}">14.3 Trattamento dei Dati Personali</p>
<p style="${pj125}">I dati personali eventualmente contenuti nel presente elaborato sono stati trattati nella misura strettamente necessaria al perseguimento delle finalità investigative dichiarate, nel rispetto della normativa vigente in materia di protezione dei dati personali, ivi compresi il Decreto Legislativo n. 196 del 30 giugno 2003 e il Regolamento UE 2016/679 (GDPR), secondo i principi di liceità, pertinenza e proporzionalità.</p>

<p style="${subSectionTitle}">14.4 Destinazione e Responsabilità di Utilizzo</p>
<p style="${pj125}">La presente Relazione è destinata esclusivamente al Mandante che, dalla consegna, ne diviene custode ed unico legittimo possessore, assumendosi ogni responsabilità in ordine alla conservazione, alla diffusione e all'utilizzo del documento e delle informazioni in esso contenute.</p>
<p style="${pj125}">Ogni utilizzo difforme o eccedente rispetto alle finalità dichiarate resta sotto la esclusiva responsabilità del soggetto che ne disponga.</p>

<p style="${subSectionTitle2}">14.5 Conservazione</p>
<p style="text-align:left;margin:16px 0; line-height:1; font-weight:bold; text-transform:uppercase; font-size:10pt; letter-spacing:0.02em;">${nomeAzienda || '___________________'}</p>
<p style="${pj125}">non conserva copia della presente Relazione e dei relativi allegati oltre il tempo strettamente necessario alla conclusione dell'incarico e agli adempimenti amministrativi e difensivi ad esso connessi.</p>
<p style="${pj125}">Decorso tale periodo, l'Istituto procede alla cancellazione della documentazione investigativa completa, cessando ogni ulteriore disponibilità della relazione e dei relativi allegati, che restano nella esclusiva disponibilità del Mandante quale unico legittimo custode del documento consegnato.</p>
<p style="${pj125}">Qualora, successivamente alla cancellazione, si renda necessaria la produzione della Relazione in sede giudiziaria o in altra sede, la relativa copia dovrà essere richiesta al Mandante.</p>
<p></br></p>
<p style="${subSectionTitle}">14.6 Organizzazione Operativa e Individuazione Testimoni</p>
<p style="${pj125}">Le attività descritte nella presente relazione sono state svolte sotto la direzione e responsabilità dello scrivente Istituto, anche mediante personale incaricato, collaboratori regolarmente segnalati ai sensi dell'art. 259 del Regolamento di esecuzione del T.U.L.P.S. e/o, ove necessario, con il supporto di altro Istituto regolarmente autorizzato.</p>

<p style="font-weight:bold; margin:14px 0 4px; text-decoration:underline;">IMPORTANTE – CITAZIONE TESTE:</p>
<p style="${pj125}; font-weight:bold; text-decoration:underline;">Qualora si renda necessaria la citazione testimoniale, i nominativi dei soggetti che hanno materialmente eseguito le attività investigative e che rivestono la qualità di testimoni oculari dovranno essere richiesti all'Istituto, che provvederà a fornire le complete generalità dei collaboratori o operatori direttamente informati sui fatti oggetto della presente Relazione.</p>

<p style="font-size:9pt; color:#555; margin:16px 0 2px; text-align:justify; font-style:italic; line-height:1.5;">(Art. 10 delle Regole deontologiche relative ai trattamenti di dati personali effettuati per svolgere investigazioni difensive o per fare valere o difendere un diritto in sede giudiziaria pubblicate ai sensi dell'art. 20, comma 4, del d.lgs. 10 agosto 2018, n. 101 - <strong>19 dicembre 2018</strong>, (Pubblicato sulla Gazzetta Ufficiale n. 12 del 15 gennaio 2019)</p>
<p style="font-size:9pt; color:#555; margin:0 0 14px; text-align:right; font-style:italic;">Registro dei provvedimenti n. 512 del 19 dicembre 2018</p>

<p style="margin:20px 0 6px;">Tanto si è accertato e si riferisce per gli usi consentiti dalla Legge.</p>
<p style="${pl}">Relazione composta di pagine - - - <strong><span data-pages-count>1</span></strong> - - -</p>
<p style="margin-top:14px; font-weight:bold;">${cityAgency ? cityAgency + ', ' : ''}${today}</p>
${timbroUrl ? `<!-- timbro agenzia in basso a destra. crossorigin="anonymous"
     abilita la cattura da html2canvas nel PDF (Backblaze restituisce CORS *).
     NIENTE margin-bottom: il timbro è già l'ultimo elemento; un margin grosso
     spingerebbe lo scrollHeight oltre PAGE_H_PX creando una pagina extra vuota.
     max-width/height ridotti da 180px a 140px + margin-top ridotto da 24px a 12px
     per evitare lo split in pagina nuova quando il blocco firma è già a fondo pagina. -->
<div style="margin-top:12px; padding-right:24px; display:flex; justify-content:flex-end;">
  <img src="${timbroUrl}" crossorigin="anonymous" alt="Timbro" style="display:block; margin-left:auto; max-width:302px; max-height:302px; object-fit:contain;" />
</div>` : ''}`;

    // Concatena le 3 sezioni marcate; i tab Prime / Ultime / Tutto usano data-rel-section.
    return tagRelSection(firstHtml, 'first')
        + tagRelSection(bodyHtmlRaw, 'body')
        + tagRelSection(lastHtmlRaw, 'last');
}

interface OrganizedMediaGroup {
    images: any[];
    videos: any[];
}

// Componente principale.
// Variante di ReportEditorView per le relazioni tecniche di bonifica
// (modelId 'prev-bonifiche'): stesso ciclo di vita, salvataggio
// (POST /api/praticas/{id}/relazione) e PDF. Cambia solo il template iniziale:
// il verbale tecnico con le 14 sezioni e le tabelle (mandante, tipologia, dati
// intervento, matrice A1-F2, riscontri, conclusioni, clausole).
export const ReportEditorViewBonifiche: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { addToast } = useToast();

    // Refs
    const editorRef = useRef<HTMLDivElement>(null);
    // Stato attivo dei comandi di formattazione, per evidenziare i pulsanti.
    const { formats: activeFmt, fontLabel, refresh: refreshActiveFmt } = useActiveFormats(editorRef);

    // Ultima selezione valida nell'editor. I <select> del font tolgono il focus al
    // contentEditable e la selezione collassa, quindi i comandi agirebbero nel punto
    // sbagliato (inizio documento). Salviamo qui l'ultimo range e lo ripristiniamo
    // prima di applicare.
    const savedRangeRef = useRef<Range | null>(null);
    useEffect(() => {
        const h = () => {
            const sel = window.getSelection();
            if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
                savedRangeRef.current = sel.getRangeAt(0).cloneRange();
            }
        };
        document.addEventListener('selectionchange', h);
        return () => document.removeEventListener('selectionchange', h);
    }, []);

    // Interlinea: il menù è controllato da `lineHeightVal` (come la dimensione testo da
    // `fontLabel`), così si vede quella applicata al cursore. I blocchi bersaglio sono
    // tenuti in un ref aggiornato a ogni selectionchange, perché aprendo il <select>
    // il contenteditable perde il focus.
    const lineHeightBlocksRef = useRef<HTMLElement[]>([]);
    // Ultima selezione valida nell'editor, per ripristinare il cursore dopo il menù
    // interlinea senza saltare in cima al documento.
    const lineHeightRangeRef = useRef<Range | null>(null);
    const [lineHeightVal, setLineHeightVal] = useState('');
    useEffect(() => {
        const BLOCK_TAGS = ['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'TD', 'TH'];
        const blockOf = (n: Node | null): HTMLElement | null => {
            let el: HTMLElement | null = n && n.nodeType === Node.TEXT_NODE ? n.parentElement : (n as HTMLElement | null);
            while (el && el !== editorRef.current && !BLOCK_TAGS.includes(el.tagName)) el = el.parentElement;
            return el && el !== editorRef.current ? el : null;
        };
        // Interlinea come rapporto (es. "1.35"): dallo stile inline senza unità, oppure
        // dal computed (lineHeight px / font px).
        const ratioOf = (block: HTMLElement): string => {
            const inline = block.style.lineHeight;
            if (inline && !inline.endsWith('px')) return String(parseFloat(inline));
            const cs = window.getComputedStyle(block);
            const lh = parseFloat(cs.lineHeight), fs = parseFloat(cs.fontSize);
            if (!isFinite(lh) || !isFinite(fs) || fs === 0) return '';
            return String(Math.round((lh / fs) * 100) / 100);
        };
        const onSel = () => {
            const editor = editorRef.current;
            const sel = window.getSelection();
            if (!editor || !sel || sel.rangeCount === 0) return;
            if (!sel.anchorNode || !editor.contains(sel.anchorNode)) return;
            const range = sel.getRangeAt(0);
            // Tutti i blocchi toccati dalla selezione, non solo quelli di anchor e focus;
            // teniamo solo i paragrafi foglia.
            const hit = Array.from(editor.querySelectorAll('p,div,li,h1,h2,h3,blockquote'))
                .filter(el => range.intersectsNode(el)) as HTMLElement[];
            let blocks = hit.filter(el => !hit.some(o => o !== el && el.contains(o)));
            if (blocks.length === 0) {
                const set = new Set<HTMLElement>();
                const a = blockOf(sel.anchorNode); if (a) set.add(a);
                const f = blockOf(sel.focusNode); if (f) set.add(f);
                blocks = Array.from(set);
            }
            lineHeightBlocksRef.current = blocks;
            lineHeightRangeRef.current = range.cloneRange();
            setLineHeightVal(blocks.length ? ratioOf(blocks[0]) : '');
        };
        document.addEventListener('selectionchange', onSel);
        return () => document.removeEventListener('selectionchange', onSel);
    }, []);
    const applyLineHeight = useCallback((val: string) => {
        const blocks = lineHeightBlocksRef.current;
        if (!val || !blocks.length) return;
        blocks.forEach(b => { b.style.lineHeight = val; });
        setLineHeightVal(val);
        handleEditorInputRef.current();   // Salvataggio (autosave) + history + salti pagina
        // Rimette focus e cursore senza scrollare: altrimenti il focus su un
        // contenteditable senza selezione porta il caret a inizio documento.
        editorRef.current?.focus({ preventScroll: true });
        const r = lineHeightRangeRef.current;
        if (r) {
            try { const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(r); } catch { /* Range non più valido: ignorato */ }
        }
    }, []);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const a4WrapperRef = useRef<HTMLDivElement>(null);
    const annotationSvgRef = useRef<SVGSVGElement>(null);
    const videoPlayerRef = useRef<HTMLVideoElement>(null);

    // Cache base64 di header/footer del PDF, per non rifare urlToBase64 a ogni salvataggio.
    // La chiave è l'URL senza querystring, quindi cambia solo se cambia il path S3.
    const pdfBrandingCacheRef = useRef<{
        headerKey: string | null; headerImg: { data: string; format: string } | null;
        footerKey: string | null; footerImg: { data: string; format: string } | null;
    }>({ headerKey: null, headerImg: null, footerKey: null, footerImg: null });

    // Dati
    const [pratica, setPratica] = useState<Pratica | null>(null);
    const [incarichi, setIncarichi] = useState<Incarico[]>([]);
    // Soggetto di un report: il report porta solo l'id dell'incarico, il nome sta sull'incarico.
    const soggettoDelReport = (r: any): string | null => {
        const id = r?.incarico?.id ?? r?.incaricoId;
        return r?.incarico?.soggettoNome
            || (incarichi.find(i => String(i.id) === String(id)) as any)?.soggettoNome
            || null;
    };
    // Con un soggetto scelto: il suo materiale più quello non assegnato a nessun soggetto.
    const delSoggetto = (nome: string | null | undefined, scelto: string): boolean =>
        !nome || nome.trim().toLowerCase() === scelto.trim().toLowerCase();
    const [reports, setReports] = useState<any[]>([]);

    // Stato editor
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    // Guardia modifiche non salvate
    const [isDirty, setIsDirty] = useState(false);
    // Ref a `markChanged` (dichiarata più sotto) per gli handler definiti prima
    // (undo/redo, etichette data-ora), evitando la TDZ nelle deps.
    const markChangedRef = useRef<() => void>(() => {});
    const [isSaving, setIsSaving] = useState(false);
    // Generazione PDF in corso: pilota l'overlay. Separato da isSaving per non
    // mostrarlo durante l'autosave.
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfProgress, setPdfProgress] = useState<{ current: number; total: number } | null>(null);
    // Anteprima PDF in-app: object URL del blob jsPDF in un <iframe> in modale.
    // Viene revocato alla chiusura o alla rigenerazione.
    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    // Zoom % dell'anteprima PDF tramite il fragment "#zoom=" del viewer di Chrome.
    const [previewZoom, setPreviewZoom] = useState<number>(100);
    // Modale di caricamento durante la generazione PDF.
    const [pdfGenerating, setPdfGenerating] = useState<false | 'preview' | 'download'>(false);

    // Anteprima PDF live affiancata (render del server, identico al download).
    const [livePreviewOpen, setLivePreviewOpen] = useState(false);
    const [livePreviewBlob, setLivePreviewBlob] = useState<Blob | null>(null);
    const [livePreviewLoading, setLivePreviewLoading] = useState(false);
    const [livePreviewAuto, setLivePreviewAuto] = useState(true);
    // Zoom % dell'anteprima live (100 = larghezza del pannello).
    const [livePreviewZoom, setLivePreviewZoom] = useState(100);
    const [livePreviewStale, setLivePreviewStale] = useState(false);
    const [livePreviewWidth, setLivePreviewWidth] = useState(640);
    const [previewResizing, setPreviewResizing] = useState(false);
    const previewResizeRef = useRef<{ x: number; w: number } | null>(null);
    // Drag foto: indicatore del punto di rilascio e auto-scroll.
    const [dropIndicatorTop, setDropIndicatorTop] = useState<number | null>(null);
    const autoScrollDirRef = useRef(0);
    const autoScrollRafRef = useRef<number | null>(null);
    const annotationsCountRef = useRef(0);
    const livePreviewRenderingRef = useRef(false);
    const livePreviewPendingRef = useRef(false);
    const livePreviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const livePreviewOpenRef = useRef(false);
    const livePreviewAutoRef = useRef(true);
    const livePreviewBlobRef = useRef<Blob | null>(null);
    const refreshLivePreviewRef = useRef<() => void>(() => {});
    useEffect(() => { livePreviewOpenRef.current = livePreviewOpen; }, [livePreviewOpen]);
    useEffect(() => { livePreviewAutoRef.current = livePreviewAuto; }, [livePreviewAuto]);
    useEffect(() => { livePreviewBlobRef.current = livePreviewBlob; }, [livePreviewBlob]);
    useEffect(() => {
        if (!livePreviewOpen) return;
        const onMove = (e: MouseEvent) => {
            const st = previewResizeRef.current;
            if (!st) return;
            e.preventDefault();
            const next = Math.max(360, Math.min(window.innerWidth - 480, st.w - (e.clientX - st.x)));
            setLivePreviewWidth(next);
        };
        const onUp = () => {
            if (!previewResizeRef.current) return;
            previewResizeRef.current = null;
            setPreviewResizing(false);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [livePreviewOpen]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [pageMargins, setPageMargins] = useState<'standard' | 'narrow' | 'wide'>('narrow');
    const [totalPages, setTotalPages] = useState(1);

    // Vista: prime pagine / corpo / ultime / tutto, tramite data-view-mode su
    // #editor-body che nasconde via CSS le altre sezioni.
    const [viewSection, setViewSection] = useState<'first' | 'body' | 'last' | 'all'>('all');
    // Le relazioni senza marker data-rel-section non supportano la vista per
    // sezione: tab nascosti e vista "all".
    const [hasSectionMarkers, setHasSectionMarkers] = useState(true);

    // Header / footer
    const [headerContent, setHeaderContent] = useState('');
    const [footerContent, setFooterContent] = useState('');

    // Strumenti annotazione
    const [activeTool, setActiveTool] = useState<AnnotationTool>('none');
    // Elenco annotazioni in toolbar (seleziona/elimina la singola annotazione).
    const [annListOpen, setAnnListOpen] = useState(false);
    const [annotations, setAnnotations] = useState<Annotation[]>([]);
    const [arrowColor, setArrowColor] = useState('#e63946');
    const [drawing, setDrawing] = useState(false);
    const [drawStart, setDrawStart] = useState<{x: number; y: number} | null>(null);
    const [currentDraw, setCurrentDraw] = useState<{x: number; y: number} | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Toolbar flottante immagine: position fixed, coordinate aggiornate a ogni scroll/resize
    const [selectedEditorImg, setSelectedEditorImg] = useState<HTMLImageElement | null>(null);
    const [imgToolbarRect, setImgToolbarRect] = useState<DOMRect | null>(null);

    // Etichetta data-ora
    type TsPos = 'tl' | 'tr' | 'bl' | 'br' | 'custom';
    const [tsModalImg, setTsModalImg] = useState<HTMLImageElement | null>(null);
    const [tsDate, setTsDate] = useState('');
    const [tsTime, setTsTime] = useState('');
    const [tsPosition, setTsPosition] = useState<TsPos>('bl');
    const [tsHasExisting, setTsHasExisting] = useState(false);

    /**
     * Aggiorna la posizione della toolbar dal bounding rect attuale dell'immagine.
     * Chiamata al click e a ogni scroll del contenitore.
     */
    const refreshImgToolbarPos = useCallback(() => {
        if (!selectedEditorImg) return;
        setImgToolbarRect(selectedEditorImg.getBoundingClientRect());
    }, [selectedEditorImg]);

    // Scroll/resize: tiene la toolbar agganciata all'immagine
    useEffect(() => {
        if (!selectedEditorImg) {
            setImgToolbarRect(null);
            return;
        }
        refreshImgToolbarPos();
        const scrollEl = scrollContainerRef.current;
        scrollEl?.addEventListener('scroll', refreshImgToolbarPos, { passive: true });
        window.addEventListener('resize', refreshImgToolbarPos, { passive: true });
        return () => {
            scrollEl?.removeEventListener('scroll', refreshImgToolbarPos);
            window.removeEventListener('resize', refreshImgToolbarPos);
        };
    }, [selectedEditorImg, refreshImgToolbarPos]);

    const handleEditorClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'IMG') {
            const img = target as HTMLImageElement;

            // Rimuove l'outline precedente
            document.querySelectorAll('#editor-body img').forEach(i => {
                (i as HTMLElement).style.outline = 'none';
            });
            img.style.outline = '2.5px solid #3b82f6';
            img.style.outlineOffset = '2px';

            setSelectedEditorImg(img);
            setImgToolbarRect(img.getBoundingClientRect());
        } else {
            setSelectedEditorImg(null);
            setImgToolbarRect(null);
            document.querySelectorAll('#editor-body img').forEach(i => {
                (i as HTMLElement).style.outline = 'none';
            });
        }
    };

    // ESC deseleziona l'immagine
    const deselctImg = useCallback(() => {
        setSelectedEditorImg(null);
        setImgToolbarRect(null);
        document.querySelectorAll('#editor-body img').forEach(i => {
            (i as HTMLElement).style.outline = 'none';
        });
    }, []);

    const openTsModal = (img: HTMLImageElement) => {
        const wrapper = img.parentElement;
        const existing = wrapper?.querySelector('.img-ts') as HTMLElement | null;
        setTsHasExisting(!!existing);
        if (existing) {
            setTsDate(existing.dataset.tsDate || '');
            setTsTime(existing.dataset.tsTime || '');
            setTsPosition((existing.dataset.tsPos as TsPos) || 'bl');
        } else {
            const now = new Date();
            // Data locale e non toISOString (UTC): subito dopo mezzanotte l'UTC è ancora ieri.
            const pad2 = (n: number) => String(n).padStart(2, '0');
            setTsDate(`${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`);
            setTsTime(now.toTimeString().slice(0, 5));
            setTsPosition('bl');
        }
        setTsModalImg(img);
    };

    const applyTs = () => {
        if (!tsModalImg || !tsDate || !tsTime) return;
        const text = `${tsDate.split('-').reverse().join('/')} ${tsTime}`;
        let wrapper = tsModalImg.parentElement;
        if (!wrapper?.classList.contains('ts-wrap')) {
            // Blocca la foto alla sua larghezza reale prima di avvolgerla (width in px +
            // max-width:none), altrimenti si rimpicciolirebbe all'aggiunta dell'etichetta.
            const beforeW = tsModalImg.clientWidth;
            const w = document.createElement('span');
            w.className = 'ts-wrap';
            w.style.cssText = 'position:relative; display:inline-block; line-height:0;';
            tsModalImg.parentNode?.insertBefore(w, tsModalImg);
            w.appendChild(tsModalImg);
            wrapper = w;
            if (beforeW > 0) { tsModalImg.style.maxWidth = 'none'; tsModalImg.style.width = beforeW + 'px'; }
        }
        let ts = wrapper.querySelector('.img-ts') as HTMLElement;
        if (!ts) {
            ts = document.createElement('span');
            ts.className = 'img-ts';
            wrapper.appendChild(ts);
        }
        const posStyle: Record<string, string> = {
            tl: 'top:8px; left:8px;', tr: 'top:8px; right:8px;',
            bl: 'bottom:8px; left:8px;', br: 'bottom:8px; right:8px;',
        };
        const currentFs = parseFloat(ts.dataset.tsFontSize || '') || 11;
        const p = Math.round(currentFs * 0.27);
        ts.style.cssText = `position:absolute; ${posStyle[tsPosition] ?? posStyle['bl']} background:rgba(0,0,0,0.68); color:#ffffff; font-family:monospace; font-size:${currentFs}px; line-height:1; font-weight:bold; padding:${Math.max(2,p-2)}px ${p*2}px; border-radius:4px; letter-spacing:0.5px; z-index:10; cursor:grab; display:inline-flex; align-items:center;`;
        ts.innerHTML = '';
        const tsText = document.createElement('span');
        tsText.style.cssText = 'display:inline-block; white-space:nowrap; vertical-align:middle;';
        tsText.textContent = text;
        ts.appendChild(tsText);
        const handle = document.createElement('span');
        handle.className = 'ts-resize';
        handle.title = 'Trascina per ridimensionare';
        handle.style.cssText = 'position:absolute;right:-5px;bottom:-5px;width:12px;height:12px;background:rgba(255,255,255,0.88);border:1px solid rgba(0,0,0,0.35);border-radius:2px;cursor:se-resize;display:inline-flex;align-items:center;justify-content:center;font-size:8px;color:#555;pointer-events:auto;user-select:none;line-height:1;';
        handle.textContent = '◢';
        ts.appendChild(handle);
        ts.dataset.tsDate = tsDate;
        ts.dataset.tsTime = tsTime;
        ts.dataset.tsPos = tsPosition;
        ts.dataset.tsFontSize = String(currentFs);
        setTsModalImg(null);
        pushHistory();
        markChangedRef.current();
    };

    const removeTs = () => {
        if (!tsModalImg) return;
        const wrapper = tsModalImg.parentElement;
        if (wrapper?.classList.contains('ts-wrap')) {
            wrapper.querySelector('.img-ts')?.remove();
        }
        setTsModalImg(null);
        pushHistory();
        markChangedRef.current();
    };

    const [dragState, setDragState] = useState<{
        annId: string;
        mode: 'move-blur' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br' | 'arrow-p1' | 'arrow-p2' | 'arrow-body';
        startX: number; startY: number;
        origAnn: Annotation;
    } | null>(null);

    // Ref per il drag&drop dalla sidebar: impostato in modo sincrono nel dragstart
    // e letto nell'onDrop, dove si può essere asincroni.
    const draggingItemRef = useRef<any>(null);
    const draggingInternalRef = useRef<HTMLElement | null>(null);

    // UI
    const [sidebarTab, setSidebarTab] = useState<'media' | 'report'>('media');
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState('');
    const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
    // Frame catturati dai video, salvati su S3 come file della pratica "Frame_<videoId>_<ts>.jpg".
    // Servono per l'anteprima immediata (dataUrl), il refresh degli URL al reload e l'eliminazione.
    type CapturedFrame = { url: string; originalUrl?: string; fileId?: number; pending?: boolean };
    const [capturedFrames, setCapturedFrames] = useState<Record<string, CapturedFrame[]>>({});
    const [selectedDate, setSelectedDate] = useState<string>("");
    // Categoria selezionata per filtrare i media
    const [selectedCategory, setSelectedCategory] = useState<string>("all");
    const [selectedSoggetto, setSelectedSoggetto] = useState<string>('');
    // Colonne della griglia media in sidebar (1–9)
    const [sidebarGridCols, setSidebarGridCols] = useState(3);
    // Zoom pagina (50–200%) e larghezza sidebar (240–700px)
    const [pageZoom, setPageZoom] = useState(115);
    const [sidebarWidth, setSidebarWidth] = useState(384);
    const sidebarDragRef = useRef<{ startX: number; startWidth: number } | null>(null);
    // Drawer media per tablet (< lg); su desktop la sidebar è sempre montata.
    const [mediaDrawerOpen, setMediaDrawerOpen] = useState(false);

    const onSidebarDragStart = (e: React.MouseEvent) => {
        e.preventDefault();
        sidebarDragRef.current = { startX: e.clientX, startWidth: sidebarWidth };
        const onMove = (ev: MouseEvent) => {
            if (!sidebarDragRef.current) return;
            const newW = Math.max(240, Math.min(700, sidebarDragRef.current.startWidth + (sidebarDragRef.current.startX - ev.clientX)));
            setSidebarWidth(newW);
        };
        const onUp = () => {
            sidebarDragRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // Undo / redo (max 50 voci)
    const historyStack = useRef<Array<{ html: string; caretOffset: number }>>([]);
    const historyIdx = useRef<number>(-1);
    const isUndoRedoing = useRef(false);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);

    // Ottimizzazione testo
    const [aiModalOpen, setAiModalOpen] = useState(false);
    const [aiResultText, setAiResultText] = useState('');
    const [aiCustomPrompt, setAiCustomPrompt] = useState('');
    const [currentAiContext, setCurrentAiContext] = useState('');
    const [isAiLoading, setIsAiLoading] = useState(false);

    const [editingImage, setEditingImage] = useState<string | null>(null);
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    // Lightbox anteprima sidebar
    const [previewFile, setPreviewFile] = useState<any | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Conteggio pagine (ResizeObserver sul contenuto).
    // Ref a applyPageBreaks, dichiarata più sotto con useCallback: evita la TDZ.
    const applyPageBreaksRef = useRef<() => void>(() => {});

    // Conta le pagine dal fondo del contenuto reale (ultimo elemento non spacer) e
    // non da scrollHeight, che con la min-height a pagine intere non cala mai.
    // Chiamato a fine applyPageBreaks.
    const recomputeTotalPages = useCallback(() => {
        const editor = editorRef.current; if (!editor) return;
        const kids = Array.from(editor.children) as HTMLElement[];
        let contentBottom = 0;
        for (let i = kids.length - 1; i >= 0; i--) {
            const c = kids[i];
            if (c.dataset.pbSpacer !== undefined || c.dataset.pbSectionSpacer !== undefined) continue;
            contentBottom = c.offsetTop + c.offsetHeight;
            break;
        }
        setTotalPages(Math.max(1, Math.ceil(contentBottom / PAGE_H_PX)));
    }, []);

    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        let pbTimer: ReturnType<typeof setTimeout> | null = null;
        const obs = new ResizeObserver(() => {
            // Conta le pagine dal fondo del contenuto reale, non da scrollHeight (che con
            // la min-height resta alto e lascia pagine vuote in coda).
            const kids = Array.from(editor.children) as HTMLElement[];
            let contentBottom = 0;
            for (let i = kids.length - 1; i >= 0; i--) {
                const c = kids[i];
                if (c.dataset.pbSpacer !== undefined || c.dataset.pbSectionSpacer !== undefined) continue;
                contentBottom = c.offsetTop + c.offsetHeight;
                break;
            }
            setTotalPages(Math.max(1, Math.ceil(contentBottom / PAGE_H_PX)));
            // Ricalcola i salti pagina anche dopo resize, rotazioni e larghezze rapide delle immagini
            if (pbTimer) clearTimeout(pbTimer);
            pbTimer = setTimeout(() => applyPageBreaksRef.current(), 80);
        });
        obs.observe(editor);
        return () => {
            obs.disconnect();
            if (pbTimer) clearTimeout(pbTimer);
        };
    }, [isLoaded]);

    // Tiene aggiornato lo span "[data-pages-count]" del template ("Relazione composta
    // di N pagine") col conteggio dinamico.
    useEffect(() => {
        if (!editorRef.current) return;
        editorRef.current.querySelectorAll('[data-pages-count]').forEach(el => {
            el.textContent = String(totalPages);
        });
    }, [totalPages, isLoaded]);

    // Spacer di pagina: spingono il testo sotto la linea di separazione
    const MAX_IMG_H = Math.round(PAGE_H_PX * 0.7); // 621px, altezza massima immagine
    const applyPageBreaks = useCallback(() => {
        const editor = editorRef.current;
        if (!editor) return;

        // Rimuove gli spacer precedenti (di pagina e di sezione)
        editor.querySelectorAll('[data-pb-spacer], [data-pb-section-spacer]').forEach(el => el.remove());

        // Guardia 1: elimina i marker data-section-start duplicati. Premendo Invio in un
        // paragrafo marcato il browser copia l'attributo sul nuovo <p>, e ogni marker
        // forzerebbe un salto pagina.
        const childrenForGuard = Array.from(editor.children) as HTMLElement[];
        const seenSections = new Set<string>();
        for (const c of childrenForGuard) {
            const ss = c.dataset.sectionStart;
            if (!ss) continue;
            if (seenSections.has(ss)) {
                c.removeAttribute('data-section-start');
            } else {
                seenSections.add(ss);
            }
        }

        // Guardia 2: ripristina il marker `last` se perso (es. backspace che unisce il
        // paragrafo delle clausole al corpo), riconoscendolo dal testo. Il body non ha marker.
        if (!seenSections.has('last')) {
            for (const c of childrenForGuard) {
                if (c.dataset.pbSpacer !== undefined) continue;
                if (c.dataset.pbSectionSpacer !== undefined) continue;
                if (c.dataset.coverPage !== undefined) continue;
                const text = (c.textContent || '').trim();
                // Il titolo nel template bonifica è "14. Clausole utilizzo, conservazione…",
                // diverso da quello investigativo: la regex deve riconoscere questo.
                if (/Clausole\s+(di\s+)?utilizzo/i.test(text)) {
                    c.dataset.sectionStart = 'last';
                    break;
                }
            }
        }

        // Forza il reflow: offsetTop/offsetHeight aggiornati dopo la rimozione degli spacer
        void editor.offsetHeight;

        // Box foto annidati (contenuti vecchi o drop del browser): paginazione e regola
        // "coppia" lavorano sui figli diretti dell'editor, quindi li portiamo a primo
        // livello subito dopo il blocco che li conteneva.
        editor.querySelectorAll<HTMLElement>('.report-media-box').forEach(box => {
            if (box.parentElement === editor) return;
            let topAnc: HTMLElement = box;
            while (topAnc.parentElement && topAnc.parentElement !== editor) topAnc = topAnc.parentElement;
            if (topAnc.parentElement !== editor || topAnc === box) return;
            editor.insertBefore(box, topAnc.nextSibling);
            const leftover = (topAnc.textContent || '').replace(/[\s ]/g, '').length > 0 || topAnc.querySelector('img,table,svg');
            if (!leftover) topAnc.remove();
        });

        // Riduce le immagini più alte di una pagina, comprese quelle fuori da un
        // .report-media-box, così non restano a cavallo (vedi ReportEditorView).
        const BREATHING_IMG = 16; // Spazio sotto il box ridotto (BREATHING della scansione è definito più sotto)
        editor.querySelectorAll<HTMLImageElement>('img').forEach(img => {
            // Centratura orizzontale come nel render server (display:block + margin auto):
            // le classi `mx-auto block` non bastano per le verticali. Salta i float espliciti.
            if (img.style.float !== 'left' && img.style.float !== 'right') {
                img.style.display = 'block';
                img.style.marginLeft = 'auto';
                img.style.marginRight = 'auto';
            }
            // Misuriamo il box intero (padding di rotazione, etichetta, margini), non solo
            // l'<img>: un box ≥ pagina viene ignorato dalla scansione e resterebbe a cavallo.
            // Esclusi i box "una per pagina" delle verticali.
            const boxEl = img.closest<HTMLElement>('.report-media-box');
            const isFullPageBox = !!boxEl && boxEl.dataset.mediaFullPage === '1';
            const boxH = boxEl ? boxEl.offsetHeight : img.offsetHeight;
            if (img.offsetHeight > PAGE_H_PX || (!isFullPageBox && boxH >= PAGE_H_PX)) {
                const overhang = Math.max(0, boxH - PAGE_H_PX);
                const target = Math.min(MAX_IMG_H, img.offsetHeight - overhang - BREATHING_IMG);
                img.style.maxHeight = `${Math.max(200, target)}px`;
                img.style.maxWidth = '85%';
                img.style.height = 'auto';
                img.style.width = 'auto';
            }
            // Box "una per pagina, centrata" per le verticali di default (come nel PDF).
            applyMediaBoxLayout(img);
        });

        // Coppie di orizzontali centrate in altezza (vedi ReportEditorView e render
        // server): pagina nuova + centratura. Qui si marcano first/second, lo spacer lo
        // inserisce la scansione.
        const isEmptyPara = (el: Element) => el.tagName === 'P'
            && (el.textContent || '').replace(/[\s ]/g, '').length === 0
            && !el.querySelector('img,table,svg');
        const isDefaultLandscapeBox = (el: HTMLElement) => {
            if (!el.classList.contains('report-media-box') || el.dataset.mediaFullPage === '1') return false;
            const img = el.querySelector('img');
            if (!img) return false;
            const nw = img.naturalWidth || 0, nh = img.naturalHeight || 0;
            if (nw <= 0 || nh <= 0 || nh > nw * 1.1) return false;
            if (/(?:^|;)\s*width\s*:\s*[0-9.]+(?:px|%)/i.test(img.getAttribute('style') || '')) return false;
            if (img.style.float === 'left' || img.style.float === 'right') return false;
            return true;
        };
        (Array.from(editor.children) as HTMLElement[]).forEach(c => { delete c.dataset.pairStart; delete c.dataset.pairEnd; });
        {
            let run: HTMLElement[] = [];
            const flushRun = () => {
                for (let i = 0; i + 1 < run.length; i += 2) {
                    run[i].dataset.pairStart = '1';
                    run[i + 1].dataset.pairEnd = '1';
                }
                run = [];
            };
            for (const k of Array.from(editor.children) as HTMLElement[]) {
                if (k.dataset.pbSpacer !== undefined || k.dataset.pbSectionSpacer !== undefined) continue;
                if (isDefaultLandscapeBox(k)) { run.push(k); continue; }
                if (run.length > 0 && isEmptyPara(k)) continue;
                flushRun();
            }
            flushRun();
        }

        const BREATHING = 12;
        // Offset in cima alla prima pagina di una sezione forzata (body/last) nel
        // documento concatenato, pari al padding-top dell'editor, così l'impaginazione
        // del PDF coincide con quella in modifica. Solo in multi-sezione.
        const SECTION_TOP_OFFSET = parseFloat(getComputedStyle(editor).paddingTop) || 0;
        let guard = 0;

        const scan = () => {
            // Paginazione lineare: un solo passaggio in avanti, dopo uno spacer si prosegue
            // col figlio successivo (gli spacer spingono solo verso il basso).
            if (guard++ > 20000) return;
            const children = Array.from(editor.children) as HTMLElement[];
            for (const child of children) {
                if (child.dataset.pbSpacer !== undefined) continue;
                if (child.dataset.pbSectionSpacer !== undefined) continue;
                if (child.dataset.coverPage !== undefined) continue;

                // Foto flottante che attraverserebbe il confine di pagina: va all'inizio della
                // pagina successiva (vedi ReportEditorView).
                if (child.classList.contains('report-media-box')) {
                    const fImg = child.querySelector('img') as HTMLElement | null;
                    if (fImg && (fImg.style.float === 'left' || fImg.style.float === 'right')) {
                        const imgTop = child.offsetTop;
                        const imgH = fImg.offsetHeight;
                        const startPg = Math.floor(imgTop / PAGE_H_PX);
                        const endPg = Math.floor((imgTop + imgH - 1) / PAGE_H_PX);
                        if (endPg > startPg && imgH < PAGE_H_PX) {
                            const targetTop = (startPg + 1) * PAGE_H_PX + BREATHING;
                            const spacerHeight = targetTop - imgTop;
                            if (spacerHeight > 2) {
                                const spacer = document.createElement('div');
                                spacer.dataset.pbSpacer = '1';
                                spacer.contentEditable = 'false';
                                spacer.style.cssText =
                                    `height:${spacerHeight}px;display:block;pointer-events:none;user-select:none;flex-shrink:0;`;
                                editor.insertBefore(spacer, child);
                                void editor.offsetHeight;
                                continue;
                            }
                        }
                    }
                }

                // Coppia di orizzontali: parte su pagina nuova (se non è già in cima) ed è
                // centrata in altezza con uno spacer pari a metà dello spazio libero.
                if (child.dataset.pairStart === '1') {
                    let second = child.nextElementSibling as HTMLElement | null;
                    while (second && second.dataset.pairEnd !== '1') second = second.nextElementSibling as HTMLElement | null;
                    if (second) {
                        const top = child.offsetTop;
                        const runH = (second.offsetTop + second.offsetHeight) - top;
                        const page = Math.floor(top / PAGE_H_PX);
                        let pageStart = page * PAGE_H_PX;
                        let prev = child.previousElementSibling as HTMLElement | null;
                        while (prev && (prev.dataset.pbSpacer !== undefined || prev.dataset.pbSectionSpacer !== undefined)) prev = prev.previousElementSibling as HTMLElement | null;
                        const prevBottom = prev ? prev.offsetTop + prev.offsetHeight : 0;
                        const atPageStart = prevBottom <= pageStart;
                        if (!atPageStart) pageStart = (page + 1) * PAGE_H_PX;
                        const contentTop = pageStart + (pageStart === 0 ? SECTION_TOP_OFFSET : 0);
                        const avail = PAGE_H_PX - (pageStart === 0 ? SECTION_TOP_OFFSET : 0);
                        // Blocco coppia = runH + 16px di margine sopra e sotto; il box mantiene il suo
                        // margin-top (16) sotto lo spacer, quindi +16. Come sul server.
                        const centerPad = Math.max(0, Math.floor((avail - runH - 32) / 2));
                        const spacerHeight = (contentTop + centerPad + 16) - top;
                        if (spacerHeight > 2) {
                            const spacer = document.createElement('div');
                            spacer.dataset.pbSpacer = '1';
                            spacer.contentEditable = 'false';
                            spacer.style.cssText =
                                `height:${spacerHeight}px;` +
                                `display:block;pointer-events:none;user-select:none;flex-shrink:0;`;
                            editor.insertBefore(spacer, child);
                            void editor.offsetHeight;
                            continue;
                        }
                    }
                }

                // Dopo una coppia la pagina contiene solo foto: il primo elemento non vuoto va
                // alla pagina successiva (sul server: breakAfter:page).
                if (child.dataset.pairEnd !== '1' && !isEmptyPara(child)) {
                    let prevReal = child.previousElementSibling as HTMLElement | null;
                    while (prevReal && (prevReal.dataset.pbSpacer !== undefined || prevReal.dataset.pbSectionSpacer !== undefined || isEmptyPara(prevReal))) {
                        prevReal = prevReal.previousElementSibling as HTMLElement | null;
                    }
                    if (prevReal && prevReal.dataset.pairEnd === '1') {
                        const top = child.offsetTop;
                        const pairPage = Math.floor(Math.max(0, prevReal.offsetTop + prevReal.offsetHeight - 1) / PAGE_H_PX);
                        const myPage = Math.floor(top / PAGE_H_PX);
                        if (myPage <= pairPage) {
                            const spacerHeight = (pairPage + 1) * PAGE_H_PX + BREATHING - top;
                            if (spacerHeight > 2) {
                                const spacer = document.createElement('div');
                                spacer.dataset.pbSpacer = '1';
                                spacer.contentEditable = 'false';
                                spacer.style.cssText =
                                    `height:${spacerHeight}px;` +
                                    `display:block;pointer-events:none;user-select:none;flex-shrink:0;`;
                                editor.insertBefore(spacer, child);
                                void editor.offsetHeight;
                                continue;
                            }
                        }
                    }
                }

                // Forza una nuova pagina per i marker data-section-start. offsetTop include il
                // padding-top dell'editor, quindi i confini di pagina sono multipli di PAGE_H_PX.
                // Niente BREATHING: lo spacer porta l'elemento esattamente al confine, così alla
                // scansione successiva non riparte il loop. L'attributo data-pb-section-spacer è
                // distinto perché non deve essere rimosso nell'onclone della generazione PDF.
                // Lo stesso vale per l'interruzione di pagina manuale (data-page-break), come
                // fa il render server con break-before:page.
                if ((child.dataset.sectionStart || child.dataset.pageBreak !== undefined) && child.offsetTop > 0) {
                    const top = child.offsetTop;
                    let prevSibling = child.previousElementSibling as HTMLElement | null;
                    while (prevSibling && (prevSibling.dataset.pbSpacer !== undefined || prevSibling.dataset.pbSectionSpacer !== undefined)) {
                        prevSibling = prevSibling.previousElementSibling as HTMLElement | null;
                    }
                    const prevBottom = prevSibling ? prevSibling.offsetTop + prevSibling.offsetHeight : 0;
                    const prevPage = Math.floor(Math.max(0, prevBottom - 1) / PAGE_H_PX);
                    const naturalPage = Math.floor(top / PAGE_H_PX);
                    const targetPage = Math.max(naturalPage, prevPage + 1);
                    // Le sezioni partono con SECTION_TOP_OFFSET (come in editor); le interruzioni
                    // manuali a filo bordo, come nel render.
                    const isManualBreak = child.dataset.pageBreak !== undefined;
                    const targetTop = targetPage * PAGE_H_PX + (isManualBreak ? 0 : SECTION_TOP_OFFSET);
                    const spacerHeight = targetTop - top;
                    if (spacerHeight > 5) {
                        const spacer = document.createElement('div');
                        spacer.dataset.pbSectionSpacer = '1';
                        spacer.contentEditable = 'false';
                        spacer.style.cssText =
                            `height:${spacerHeight}px;` +
                            `display:block;pointer-events:none;user-select:none;flex-shrink:0;`;
                        editor.insertBefore(spacer, child);
                        void editor.offsetHeight;
                        continue; // Paginazione lineare: si continua col figlio successivo
                    }
                }

                const childH = child.offsetHeight;
                // Elemento più alto di una pagina: niente spacer (evita loop)
                if (childH >= PAGE_H_PX) continue;
                const childTop = child.offsetTop;
                const childBot = childTop + childH;
                const page     = Math.floor(childTop / PAGE_H_PX);
                const boundary = (page + 1) * PAGE_H_PX;
                // Tolleranza sub-pixel per html2canvas a SCALE=3 (vedi ReportEditorView)
                const SUBPX_SAFETY = 3;
                if (childTop < boundary && childBot > boundary - SUBPX_SAFETY) {
                    const spacer = document.createElement('div');
                    spacer.dataset.pbSpacer = '1';
                    spacer.contentEditable = 'false';
                    spacer.style.cssText =
                        `height:${boundary - childTop + BREATHING}px;` +
                        `display:block;pointer-events:none;user-select:none;flex-shrink:0;`;
                    editor.insertBefore(spacer, child);
                    void editor.offsetHeight; // Reflow dopo l'inserimento, poi si prosegue
                }
            }
        };
        scan();
        recomputeTotalPages();
    }, [recomputeTotalPages]);

    // Aggiorna il ref usato dal ResizeObserver
    useEffect(() => { applyPageBreaksRef.current = applyPageBreaks; }, [applyPageBreaks]);

    // Se un'<img> non si carica (URL firmato scaduto) la rifirmiamo dalla
    // data-img-(preview-)key e riproviamo, al massimo 3 volte (vedi ReportEditorView).
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;
        const onImgError = async (e: Event) => {
            const img = e.target as HTMLElement;
            if (!img || img.tagName !== 'IMG') return;
            const el = img as HTMLImageElement;
            const key = el.getAttribute('data-img-preview-key') || el.getAttribute('data-img-key');
            if (!key) return;
            const n = Number(el.dataset.resignN || '0');
            if (n >= 3) return;
            el.dataset.resignN = String(n + 1);
            const url = await getSignedImageUrl(key);
            if (url && url !== el.getAttribute('src')) el.src = url;
        };
        editor.addEventListener('error', onImgError, true);
        return () => editor.removeEventListener('error', onImgError, true);
    }, [isLoaded]);

    // Indicatore del punto di rilascio e auto-scroll durante il drag
    const dropTargetElOf = useCallback((clientY: number): HTMLElement | null => {
        const editor = editorRef.current; if (!editor) return null;
        const kids = (Array.from(editor.children) as HTMLElement[]).filter(
            el => el.dataset.pbSpacer === undefined && el.dataset.pbSectionSpacer === undefined);
        for (const kid of kids) {
            const r = kid.getBoundingClientRect();
            if (clientY < r.top + r.height / 2) return kid;
        }
        return null;
    }, []);
    const updateDropIndicator = useCallback((clientY: number) => {
        const editor = editorRef.current, wrapper = a4WrapperRef.current;
        if (!editor || !wrapper) return;
        const zoom = (pageZoom || 100) / 100;
        const wrapTop = wrapper.getBoundingClientRect().top;
        const before = dropTargetElOf(clientY);
        let y: number | null = null;
        if (before) y = (before.getBoundingClientRect().top - wrapTop) / zoom;
        else {
            const kids = (Array.from(editor.children) as HTMLElement[]).filter(
                el => el.dataset.pbSpacer === undefined && el.dataset.pbSectionSpacer === undefined);
            if (kids.length) y = (kids[kids.length - 1].getBoundingClientRect().bottom - wrapTop) / zoom;
        }
        setDropIndicatorTop(y);
    }, [pageZoom, dropTargetElOf]);
    const stopAutoScroll = useCallback(() => {
        autoScrollDirRef.current = 0;
        if (autoScrollRafRef.current != null) { cancelAnimationFrame(autoScrollRafRef.current); autoScrollRafRef.current = null; }
    }, []);
    const startAutoScroll = useCallback(() => {
        if (autoScrollRafRef.current != null) return;
        const step = () => {
            const c = scrollContainerRef.current, dir = autoScrollDirRef.current;
            if (c && dir !== 0) { c.scrollTop += dir * 14; autoScrollRafRef.current = requestAnimationFrame(step); }
            else { autoScrollRafRef.current = null; }
        };
        autoScrollRafRef.current = requestAnimationFrame(step);
    }, []);

    // Spacer rimossi dall'HTML prima di salvare/esportare
    const getCleanHtml = useCallback((): string => {
        if (!editorRef.current) return '';
        const clone = editorRef.current.cloneNode(true) as HTMLElement;
        // Gli spacer si ricalcolano a ogni applyPageBreaks e non vanno salvati.
        clone.querySelectorAll('[data-pb-spacer], [data-pb-section-spacer]').forEach(el => el.remove());
        return clone.innerHTML;
    }, []);

    // Salti pagina dopo il caricamento e di nuovo quando tutte le immagini sono decodificate
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        const editor = editorRef.current;
        const t = setTimeout(async () => {
            applyPageBreaks(); // Primo passaggio (il testo ha già le sue dimensioni)
            const imgs = Array.from(editor.querySelectorAll<HTMLImageElement>('.report-media-box img'));
            if (imgs.length === 0) return;
            await Promise.allSettled(imgs.map(img => img.decode()));
            applyPageBreaks(); // Passaggio finale con le immagini decodificate
        }, 120);
        return () => clearTimeout(t);
    }, [isLoaded, applyPageBreaks]);

    // La relazione ha i marker di sezione? Le bozze più vecchie no, e il toggle
    // prime/ultime non serve.
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        const hasMarkers = !!editorRef.current.querySelector('[data-rel-section]');
        setHasSectionMarkers(hasMarkers);
        if (!hasMarkers) setViewSection('all');
    }, [isLoaded]);

    // Al cambio vista ricalcola i salti pagina (gli elementi nascosti hanno offsetHeight 0)
    useEffect(() => {
        if (!isLoaded) return;
        const t = setTimeout(() => applyPageBreaks(), 30);
        return () => clearTimeout(t);
    }, [viewSection, isLoaded, applyPageBreaks]);

    // History leggera (vedi ReportEditorView): niente voci per stati identici, tasti
    // ravvicinati (< 400 ms) uniti nell'ultima voce, limite di 50 voci e ~25 MB.
    const lastPushAtRef = useRef(0);
    const pushHistory = useCallback(() => {
        if (!editorRef.current || isUndoRedoing.current) return;
        const html = editorRef.current.innerHTML;
        const caretOffset = getCaretOffset(editorRef.current);
        const next = historyStack.current.slice(0, historyIdx.current + 1);
        const last = next[next.length - 1];
        const now = Date.now();
        if (last && last.html === html) {
            last.caretOffset = caretOffset;
            lastPushAtRef.current = now;
        } else if (last && next.length > 1 && now - lastPushAtRef.current < 400) {
            next[next.length - 1] = { html, caretOffset };
            lastPushAtRef.current = now;
        } else {
            next.push({ html, caretOffset });
            lastPushAtRef.current = now;
        }
        while (next.length > 50) next.shift();
        const MAX_HISTORY_BYTES = 25 * 1024 * 1024;
        let bytes = next.reduce((s, e) => s + e.html.length, 0);
        while (next.length > 1 && bytes > MAX_HISTORY_BYTES) bytes -= next.shift()!.html.length;
        historyStack.current = next;
        historyIdx.current = next.length - 1;
        setCanUndo(historyIdx.current > 0);
        setCanRedo(false);
    }, []);

    const undo = useCallback(() => {
        if (historyIdx.current <= 0 || !editorRef.current) return;
        historyIdx.current--;
        const entry = historyStack.current[historyIdx.current];
        isUndoRedoing.current = true;
        editorRef.current.innerHTML = entry.html;
        applyCachedImageUrls(editorRef.current); // URL in cache, nessun nuovo fetch S3
        restoreCaretOffset(editorRef.current, entry.caretOffset);
        isUndoRedoing.current = false;
        setCanUndo(historyIdx.current > 0);
        setCanRedo(true);
        applyPageBreaks();
        markChangedRef.current();
    }, [applyPageBreaks]);

    const redo = useCallback(() => {
        if (historyIdx.current >= historyStack.current.length - 1 || !editorRef.current) return;
        historyIdx.current++;
        const entry = historyStack.current[historyIdx.current];
        isUndoRedoing.current = true;
        editorRef.current.innerHTML = entry.html;
        applyCachedImageUrls(editorRef.current); // URL in cache, nessun nuovo fetch S3
        restoreCaretOffset(editorRef.current, entry.caretOffset);
        isUndoRedoing.current = false;
        setCanUndo(true);
        setCanRedo(historyIdx.current < historyStack.current.length - 1);
        applyPageBreaks();
        markChangedRef.current();
    }, [applyPageBreaks]);

    // Debounce per ricalcolare i salti pagina mentre si scrive
    const pbTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Autosave con debounce di 10s e attesa massima di 3 minuti (vedi ReportEditorView.tsx).
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const saveToBackendRef = useRef<() => Promise<boolean | void> | void>(() => {});
    const lastSavedAtRef = useRef<number>(Date.now());
    // Nessun salvataggio finché il caricamento iniziale non è riuscito, altrimenti
    // l'autosave azzererebbe la bozza (vedi ReportEditorView).
    const isLoadedRef = useRef(false);
    const MAX_WAIT_MS = 3 * 60 * 1000;
    const handleEditorInput = useCallback(() => {
        setIsDirty(true);
        // Anteprima live: se aperta in automatico, rigenera dopo una pausa di scrittura.
        if (livePreviewOpenRef.current && livePreviewAutoRef.current) {
            setLivePreviewStale(true);
            if (livePreviewDebounceRef.current) clearTimeout(livePreviewDebounceRef.current);
            livePreviewDebounceRef.current = setTimeout(() => refreshLivePreviewRef.current(), 3500);
        }
        pushHistory();
        if (pbTimerRef.current) clearTimeout(pbTimerRef.current);
        pbTimerRef.current = setTimeout(() => applyPageBreaks(), 50);
        const now = Date.now();
        if (now - lastSavedAtRef.current >= MAX_WAIT_MS) {
            if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
            lastSavedAtRef.current = now;
            Promise.resolve(saveToBackendRef.current()).catch(() => {});
        } else {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(() => {
                lastSavedAtRef.current = Date.now();
                Promise.resolve(saveToBackendRef.current()).catch(() => {});
            }, 10000);
        }
    }, [applyPageBreaks, pushHistory, MAX_WAIT_MS]);

    // Le modifiche fatte via DOM (toolbar immagine, etichette data-ora, drag foto,
    // undo/redo, doppio click) non passano da onInput: qui le segniamo come non
    // salvate e programmiamo l'autosave.
    const markChanged = useCallback(() => {
        setIsDirty(true);
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            lastSavedAtRef.current = Date.now();
            Promise.resolve(saveToBackendRef.current()).catch(() => {});
        }, 10000);
        // Le stesse modifiche via DOM aggiornano anche l'anteprima live, con lo stesso
        // debounce della scrittura.
        if (livePreviewOpenRef.current && livePreviewAutoRef.current) {
            setLivePreviewStale(true);
            if (livePreviewDebounceRef.current) clearTimeout(livePreviewDebounceRef.current);
            livePreviewDebounceRef.current = setTimeout(() => refreshLivePreviewRef.current(), 3500);
        }
    }, []);
    useEffect(() => { markChangedRef.current = markChanged; }, [markChanged]);

    // Ref sempre aggiornato a handleEditorInput, usato da applyLineHeight (definita
    // prima) per salvare l'interlinea evitando la TDZ.
    const handleEditorInputRef = useRef<() => void>(() => {});
    handleEditorInputRef.current = handleEditorInput;

    // Spostamento e ridimensionamento delle etichette data-ora sulle foto
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        const editor = editorRef.current;

        const onDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const isHandle = target.classList.contains('ts-resize');
            const tsEl = isHandle
                ? (target.closest('.img-ts') as HTMLElement | null)
                : target.classList.contains('img-ts')
                    ? target
                    : (target.closest('.img-ts') as HTMLElement | null);
            if (!tsEl) return;

            const wrapper = tsEl.parentElement as HTMLElement;
            if (!wrapper?.classList.contains('ts-wrap')) return;

            e.preventDefault();
            e.stopPropagation();

            // Fattore di zoom dell'a4-wrapper
            const a4 = editor.closest('#a4-wrapper') as HTMLElement | null;
            const zoom = parseFloat((a4?.style as unknown as Record<string,string>)?.zoom || '1') || 1;

            // Da posizionamento ad angolo a top/left assoluti
            const wRect = wrapper.getBoundingClientRect();
            const tRect = tsEl.getBoundingClientRect();
            const curTop = (tRect.top - wRect.top) / zoom;
            const curLeft = (tRect.left - wRect.left) / zoom;
            tsEl.style.top = `${curTop}px`;
            tsEl.style.left = `${curLeft}px`;
            tsEl.style.bottom = '';
            tsEl.style.right = '';

            const startX = e.clientX;
            const startY = e.clientY;
            const startTop = curTop;
            const startLeft = curLeft;
            const startFs = parseFloat(tsEl.dataset.tsFontSize || tsEl.style.fontSize || '11') || 11;
            const maxTop = (wRect.height / zoom) - (tRect.height / zoom);
            const maxLeft = (wRect.width / zoom) - (tRect.width / zoom);

            document.body.style.userSelect = 'none';
            tsEl.style.cursor = isHandle ? 'se-resize' : 'grabbing';

            const onMove = (ev: MouseEvent) => {
                const dx = ev.clientX - startX;
                const dy = ev.clientY - startY;
                if (isHandle) {
                    const newFs = Math.max(8, Math.min(48, startFs + dx / 5));
                    const p = Math.round(newFs * 0.27);
                    tsEl.style.fontSize = `${newFs}px`;
                    tsEl.style.padding = `${Math.max(2, p - 2)}px ${p * 2}px`;
                    tsEl.dataset.tsFontSize = String(newFs);
                } else {
                    tsEl.style.top = `${Math.max(0, Math.min(maxTop, startTop + dy / zoom))}px`;
                    tsEl.style.left = `${Math.max(0, Math.min(maxLeft, startLeft + dx / zoom))}px`;
                }
            };

            const onUp = () => {
                document.body.style.userSelect = '';
                tsEl.style.cursor = isHandle ? '' : 'grab';
                tsEl.dataset.tsPos = 'custom';
                tsEl.dataset.tsPosTop = tsEl.style.top;
                tsEl.dataset.tsPosLeft = tsEl.style.left;
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                pushHistory();
                markChanged();
            };

            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        };

        editor.addEventListener('mousedown', onDown);
        return () => editor.removeEventListener('mousedown', onDown);
    }, [isLoaded, pushHistory]);

    // Checkbox degli esiti (sez. 10 Conclusioni): handler delegato sull'editor che
    // alterna ☐ / ☑ e data-checked sugli span .esito-cb. Lo stato è nell'innerHTML,
    // quindi viene salvato; pushHistory mantiene undo/redo.
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        const editor = editorRef.current;
        const onClick = (e: Event) => {
            const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('.esito-cb');
            if (!target) return;
            e.preventDefault();
            const isChecked = target.dataset.checked === 'true';
            target.dataset.checked = isChecked ? 'false' : 'true';
            target.innerHTML = isChecked ? '&#9744;' : '&#9745;';
            pushHistory();
        };
        editor.addEventListener('click', onClick);
        return () => editor.removeEventListener('click', onClick);
    }, [isLoaded, pushHistory]);

    // Radio Sì/No (sez. 2.1, Richiesta/Eseguita): handler delegato che seleziona
    // l'opzione cliccata (●) e deseleziona l'altra (○). Cliccare quella già scelta la
    // azzera. Lo stato è nell'innerHTML; handleEditorInput programma l'autosave e
    // mantiene undo/redo.
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        const editor = editorRef.current;
        const onClick = (e: Event) => {
            const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('.tipo-radio');
            if (!target) return;
            e.preventDefault();
            const group = target.closest<HTMLElement>('.tipo-radio-group');
            const wasChecked = target.dataset.checked === 'true';
            group?.querySelectorAll<HTMLElement>('.tipo-radio').forEach(opt => {
                opt.dataset.checked = 'false';
                opt.innerHTML = '&#9675;&nbsp;' + (opt.dataset.val === 'si' ? 'Sì' : 'No');
            });
            if (!wasChecked) {
                target.dataset.checked = 'true';
                target.innerHTML = '&#9679;&nbsp;' + (target.dataset.val === 'si' ? 'Sì' : 'No');
            }
            handleEditorInput();
        };
        editor.addEventListener('click', onClick);
        return () => editor.removeEventListener('click', onClick);
    }, [isLoaded, handleEditorInput]);

    // Spostamento delle immagini già inserite
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        const editor = editorRef.current;
        const onDragStart = (e: DragEvent) => {
            const box = (e.target as HTMLElement).closest<HTMLElement>('.report-media-box');
            if (!box) return;
            draggingInternalRef.current = box;
            e.dataTransfer!.effectAllowed = 'move';
            e.dataTransfer!.setData('text/internal-move', 'true');
        };
        const onDragEnd = () => { draggingInternalRef.current = null; };
        editor.addEventListener('dragstart', onDragStart);
        editor.addEventListener('dragend', onDragEnd);
        return () => {
            editor.removeEventListener('dragstart', onDragStart);
            editor.removeEventListener('dragend', onDragEnd);
        };
    }, [isLoaded]);

    // 1. Caricamento dati
    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            try {
                const [praticaRes, incarichiRes, relazioneRes, reportsRes, settingsRes] = await Promise.all([
                    PraticaService.getById(Number(id)),
                    // Includiamo anche gli incarichi AUTOGENERATO, a cui sono agganciati i media
                    // caricati dall'ufficio; buildInitialTemplate li esclude per non falsare il
                    // primo giorno.
                    IncaricoService.getByPratica(Number(id), { includeAutogenerati: true }),
                    PraticaService.getRelazione(Number(id)),
                    RelazioneGiornalieraService.getByPratica(Number(id)),
                    SettingsService.getAll(),
                ]);

                setPratica((praticaRes as any).data || praticaRes);

                const rawIncarichi = (incarichiRes as any).data || incarichiRes;
                setIncarichi(Array.isArray(rawIncarichi) ? rawIncarichi : []);
                setReports(Array.isArray(reportsRes) ? reportsRes : (reportsRes as any).data || []);

                if (editorRef.current) {
                    const rel = (relazioneRes as any).data;
                    if (rel && rel.content) {
                        const parser = new DOMParser();
                        const parsed = parser.parseFromString(rel.content, 'text/html');

                        const metaEl = parsed.querySelector('[data-relazione-meta]');
                        if (metaEl) {
                            const annEl  = metaEl.querySelector('[data-annotations]');
                            const headEl = metaEl.querySelector('[data-header]');
                            const footEl = metaEl.querySelector('[data-footer]');
                            if (annEl?.textContent)  try { setAnnotations(JSON.parse(decodeURIComponent(annEl.textContent))); } catch {}
                            if (headEl?.textContent) setHeaderContent(decodeURIComponent(headEl.textContent));
                            if (footEl?.textContent) setFooterContent(decodeURIComponent(footEl.textContent));
                            // Margini pagina salvati, così il PDF server usa gli stessi dell'editor.
                            const marEl = metaEl.querySelector('[data-margins]');
                            const mar = marEl?.textContent?.trim();
                            if (mar === 'narrow' || mar === 'wide' || mar === 'standard') setPageMargins(mar);
                            metaEl.remove();
                        }

                        // Pulizia del contenuto salvato da versioni vecchie o copiato: toglie il layer
                        // annotazioni e gli spacer di pagina rimasti, e scioglie i wrapper
                        // position:relative;min-height.
                        parsed.querySelectorAll('div[style*="pointer-events"]').forEach(el => el.remove());
                        parsed.querySelectorAll('[data-pb-spacer], [data-pb-section-spacer]').forEach(el => el.remove());
                        for (let i = 0; i < 6; i++) {
                            const wrap = Array.from(parsed.body.querySelectorAll('div')).find(d => {
                                const s = (d.getAttribute('style') || '').replace(/\s+/g, '');
                                return s.includes('position:relative') && s.includes('min-height');
                            });
                            if (!wrap) break;
                            wrap.replaceWith(...Array.from(wrap.childNodes));
                        }

                        editorRef.current.innerHTML = parsed.body.innerHTML;
                        // Rimuove gli elementi vuoti iniziali che creerebbero pagine bianche
                        let firstEl = editorRef.current.firstElementChild as HTMLElement | null;
                        while (firstEl) {
                            const hasContent = (firstEl.textContent || '').replace(/[ \s]/g, '').length > 0
                                || firstEl.querySelector('img, table, svg') !== null;
                            if (!hasContent) {
                                const next = firstEl.nextElementSibling as HTMLElement | null;
                                firstEl.remove();
                                firstEl = next;
                            } else { break; }
                        }
                        setLastSaved(new Date(rel.updatedAt));
                    } else {
                        const pData = (praticaRes as any).data || praticaRes;
                        const settingsArr = (settingsRes as any).data || [];
                        const agencySettings = Array.isArray(settingsArr) ? settingsArr[0] : settingsArr;
                        const agencyName = agencySettings?.agencyName || '';
                        // Per il template iniziale escludiamo gli incarichi AUTOGENERATO (contenitori di
                        // upload tardivi), che falserebbero il primo giorno.
                        const incarichiTemplate = (Array.isArray(rawIncarichi) ? rawIncarichi : [])
                            .filter((i: any) => i?.tipoOrigine !== 'AUTOGENERATO');
                        editorRef.current.innerHTML = buildBonificaTemplate(pData, incarichiTemplate, agencyName, agencySettings?.timbroUrl, agencySettings?.city);
                    }
                }
                isLoadedRef.current = true; // Abilita saveToBackend solo a caricamento riuscito
                setIsLoaded(true);
                // Stato iniziale come prima voce della history
                setTimeout(() => pushHistory(), 80);
            } catch (e) {
                // Caricamento fallito: isLoadedRef resta false e nessun autosave sovrascrive la bozza.
                console.error('Errore caricamento relazione', (e as any)?.message ?? e);
                addToast("Errore caricamento dati.", "error");
            }
        };
        fetchData();
    }, [id, addToast]);

    // Ricostruisce capturedFrames dai file "Frame_<videoId>_" della pratica, così i
    // frame catturati ricompaiono sotto il loro video dopo il reload.
    useEffect(() => {
        if (!pratica?.files) return;
        const grouped: Record<string, CapturedFrame[]> = {};
        for (const f of pratica.files) {
            const m = (f.name || '').match(/^Frame_([^_]+)_(\d+)\.(jpe?g|png|webp)$/i);
            if (!m) continue;
            const videoId = m[1];
            const url = (f as any).urlCompressed || (f as any).urlOriginal;
            const originalUrl: string | undefined = (f as any).urlOriginal || (f as any).finalUrl || url;
            if (!url) continue;
            if (!grouped[videoId]) grouped[videoId] = [];
            grouped[videoId].push({ url, originalUrl, fileId: f.id, pending: false });
        }
        if (Object.keys(grouped).length > 0) {
            setCapturedFrames(prev => {
                // Tiene i frame ancora in upload
                const merged: Record<string, CapturedFrame[]> = { ...grouped };
                for (const [k, arr] of Object.entries(prev)) {
                    const stillPending = arr.filter(f => f.pending);
                    if (stillPending.length > 0) merged[k] = [...(merged[k] || []), ...stillPending];
                }
                return merged;
            });
        }
    }, [pratica?.files]);

    // Aggiorna gli URL delle immagini (link S3 scaduti) con la cache di sessione:
    // una firma per chiave, riusata anche negli scambi di innerHTML (undo/redo).
    useEffect(() => {
        if (!isLoaded || !editorRef.current) return;
        refreshImageUrls(editorRef.current);
    }, [isLoaded]);

    // 2. Drag globale annotazioni
    useEffect(() => {
        if (!dragState) return;
        const onMove = (e: MouseEvent) => {
            const wrapper = a4WrapperRef.current;
            if (!wrapper) return;
            const { x: currentX, y: currentY } = clientToDoc(e.clientX, e.clientY);
            const dx = currentX - dragState.startX;
            const dy = currentY - dragState.startY;

            setAnnotations(prev => prev.map(ann => {
                if (ann.id !== dragState.annId) return ann;
                if (ann.type === 'blur') {
                    const o = dragState.origAnn as BlurAnnotation;
                    switch (dragState.mode) {
                        case 'move-blur':  return { ...ann, x: Math.max(0, o.x+dx), y: Math.max(0, o.y+dy) };
                        case 'resize-br':  return { ...ann, w: Math.max(20, o.w+dx), h: Math.max(20, o.h+dy) };
                        case 'resize-bl': { const nw = o.w-dx; return { ...ann, x: nw>=20 ? o.x+dx : o.x+o.w-20, w: Math.max(20, nw), h: Math.max(20, o.h+dy) }; }
                        case 'resize-tr': { const nh = o.h-dy; return { ...ann, y: nh>=20 ? o.y+dy : o.y+o.h-20, w: Math.max(20, o.w+dx), h: Math.max(20, nh) }; }
                        case 'resize-tl': { const nw = o.w-dx; const nh = o.h-dy; return { ...ann, x: nw>=20 ? o.x+dx : o.x+o.w-20, y: nh>=20 ? o.y+dy : o.y+o.h-20, w: Math.max(20, nw), h: Math.max(20, nh) }; }
                    }
                }
                if (ann.type === 'arrow') {
                    const o = dragState.origAnn as ArrowAnnotation;
                    switch (dragState.mode) {
                        case 'arrow-p1':   return { ...ann, x1: o.x1+dx, y1: o.y1+dy };
                        case 'arrow-p2':   return { ...ann, x2: o.x2+dx, y2: o.y2+dy };
                        case 'arrow-body': return { ...ann, x1: o.x1+dx, y1: o.y1+dy, x2: o.x2+dx, y2: o.y2+dy };
                    }
                }
                return ann;
            }));
        };
        // Al rilascio ricalcola l'ancora (anchorIdx/anchorDy) dalla posizione finale.
        // Il drag aggiorna x/y/w/h ma non l'ancora, e il render server usa anchorDy per la
        // posizione verticale: senza questo una censura spostata in basso nel PDF resterebbe
        // dov'era.
        const movedId = dragState.annId;
        const onUp = () => {
            setAnnotations(prev => prev.map(ann => {
                if (ann.id !== movedId) return ann;
                const yAnchor = ann.type === 'arrow' ? Math.min(ann.y1, ann.y2) : ann.y;
                const anchor = computeAnnAnchor(yAnchor);
                return anchor ? { ...ann, ...anchor } : ann;
            }));
            setDragState(null);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    }, [dragState]);

    // 3. URL: miniature e alta risoluzione

    /** URL della miniatura compressa per la sidebar. Non usarlo nel documento. */
    const getThumbUrl = (item: any): string => {
        return item.thumbUrl || item.thumb || item.urlCompressed || item.urlOriginal || item.url || '';
    };

    /**
     * URL ad alta risoluzione, firmato al momento dal backend. Solo per l'inserimento
     * nel documento o per l'editor forense.
     */
    const getHdUrl = async (item: any): Promise<string> => {
        try {
            if (item.isFieldMedia) {
                // Media da campo (app collaboratori)
                const response = await IncaricoService.getMediaUrl(item.id);
                if (!response) throw new Error("URL HD mancante per media da campo");
                return response;
            } else {
                // File caricati dall'ufficio: chiede l'originale
                const res = await FilesService.getDownloadUrl(item.id);
                return res.data?.data || res.data || item.hdUrl || item.urlOriginal || item.full || item.url;
            }
        } catch (e) {
            console.error(`[getHdUrl] Fallback per item ${item.id}:`, e);
            return item.hdUrl || item.urlOriginal || item.full || item.url || '';
        }
    };

    /** Aggiorna il link della thumbnail scaduta (solo dopo un errore di caricamento). */
    const refreshThumbUrl = async (item: any): Promise<string> => {
        try {
            if (item.isFieldMedia) {
                // Per i media da campo lo stesso link vale per thumb e alta risoluzione
                const response = await IncaricoService.getMediaUrl(item.id);
                return response || item.thumb || '';
            } else {
                // Per i file ufficio si usa l'endpoint della thumbnail compressa
                const res = await api.get(`/filess/thumb/${item.id}`);
                return res.data?.data || res.data || item.thumb || '';
            }
        } catch {
            return item.thumb || '';
        }
    };

    // 4. Salvataggio
    const buildAnnotationLayer = (): string => {
        if (annotations.length === 0) return '';
        const arrows = annotations.filter(a => a.type === 'arrow') as ArrowAnnotation[];
        const blurs  = annotations.filter(a => a.type === 'blur')  as BlurAnnotation[];
        const uniqueColors = arrows.map(a => a.color).filter((c, i, arr) => arr.indexOf(c) === i);
        const defs = uniqueColors.map(c => `
            <marker id="ah-${c.replace('#','')}" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="${c}"/>
            </marker>`).join('');
        const lines = arrows.map(a => `
            <line x1="${a.x1+1}" y1="${a.y1+1}" x2="${a.x2+1}" y2="${a.y2+1}" stroke="rgba(0,0,0,0.3)" stroke-width="4" stroke-linecap="round"/>
            <line x1="${a.x1}" y1="${a.y1}" x2="${a.x2}" y2="${a.y2}" stroke="${a.color}" stroke-width="3" stroke-linecap="round" marker-end="url(#ah-${a.color.replace('#','')})"/>`).join('');
        const svgLayer = arrows.length > 0 ? `
            <svg xmlns="http://www.w3.org/2000/svg" style="position:absolute;top:0;left:0;width:100%;height:100%;overflow:visible;pointer-events:none;">
                <defs>${defs}</defs>${lines}
            </svg>` : '';
        const blurBoxes = blurs.map(b =>
            // Censura opaca (#6b7280) come negli altri editor: deve coprire il contenuto,
            // una censura semitrasparente lascerebbe leggibili testo e volti.
            `<div style="position:absolute;left:${b.x}px;top:${b.y}px;width:${b.w}px;height:${b.h}px;background:#6b7280;overflow:hidden;"></div>`
        ).join('');
        return `<div style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;">${svgLayer}${blurBoxes}</div>`;
    };

    // Restituisce true se salvato, false in caso di errore o se il caricamento non è
    // riuscito. Chi azzera isDirty o esce deve controllare il risultato.
    const runSave = useCallback(async (): Promise<boolean> => {
        if (!id || !editorRef.current) return false;
        if (!isLoadedRef.current) return false; // Caricamento fallito o in corso: non sovrascrivere mai
        setIsSaving(true);
        try {
            const meta = `
                <div data-relazione-meta style="display:none">
                    <span data-header>${encodeURIComponent(headerContent)}</span>
                    <span data-footer>${encodeURIComponent(footerContent)}</span>
                    <span data-annotations>${encodeURIComponent(JSON.stringify(annotations))}</span>
                    <span data-margins>${pageMargins}</span>
                </div>`;
            const annotationLayer = buildAnnotationLayer();
            const editorHtml = getCleanHtml();
            const bodyHtml = annotationLayer
                ? `<div style="position:relative;min-height:${editorRef.current.scrollHeight}px;">${editorHtml}${annotationLayer}</div>`
                : editorHtml;
            await PraticaService.saveRelazione(Number(id), meta + bodyHtml);
            const now = new Date();
            setLastSaved(now);
            // isDirty non si azzera qui: all'uscita chiediamo comunque conferma. Si azzera
            // solo col salvataggio manuale o dal modale della guardia.
            lastSavedAtRef.current = now.getTime();
            return true;
        } catch (e) {
            addToast("Errore salvataggio", "error");
            return false;
        } finally {
            setIsSaving(false);
        }
    }, [id, addToast, headerContent, footerContent, annotations, pageMargins]);

    // Salvataggi in coda su una catena di promise: ognuno parte dopo il precedente e
    // runSave legge il DOM quando parte (vedi ReportEditorView).
    const saveChainRef = useRef<Promise<boolean>>(Promise.resolve(true));
    const saveToBackend = useCallback((): Promise<boolean> => {
        const next = saveChainRef.current.then(() => runSave(), () => runSave());
        saveChainRef.current = next.catch(() => false);
        return next;
    }, [runSave]);

    // Aggiorna il ref usato dal debounce input (evita la TDZ)
    useEffect(() => { saveToBackendRef.current = saveToBackend; }, [saveToBackend]);

    useEffect(() => {
        if (!isLoaded) return; // Parte solo a caricamento riuscito
        const interval = setInterval(() => { void saveToBackend(); }, 3 * 60 * 1000);
        return () => clearInterval(interval);
    }, [saveToBackend, isLoaded]);

    const handleReset = async () => {
        if (!id || !window.confirm("Vuoi ELIMINARE la bozza e ripartire da zero?")) return;
        try {
            await PraticaService.resetRelazione(Number(id));
            // Come negli altri editor il reset ricarica la pagina: fetchData rilegge dal
            // backend (ora vuoto) e ricostruisce il template completo, azzerando anche
            // annotazioni e history. Il reload salta di proposito la guardia d'uscita.
            suppressNextUnload();
            window.location.reload();
        } catch { addToast("Errore reset.", "error"); }
    };

    // 5. Annotazioni: eventi mouse
    const clientToDoc = (clientX: number, clientY: number) => {
        const wrapper = a4WrapperRef.current;
        if (!wrapper) return { x: 0, y: 0 };
        const rect = wrapper.getBoundingClientRect();
        const zoom = parseFloat((wrapper.style as any).zoom || '1') || 1;
        return { x: (clientX - rect.left) / zoom, y: (clientY - rect.top) / zoom };
    };
    const getDocPoint = (e: React.MouseEvent) => clientToDoc(e.clientX, e.clientY);

    // Ancora un'annotazione al layout attuale (con gli spacer applicati): elemento
    // di contenuto sotto yAnchor più offset interno, nello stesso spazio di
    // clientToDoc, per il render server.
    const computeAnnAnchor = (yAnchor: number): { anchorIdx: number; anchorDy: number } | null => {
        const editor = editorRef.current; const wrapper = a4WrapperRef.current;
        if (!editor || !wrapper) return null;
        const els = (Array.from(editor.children) as HTMLElement[]).filter(el =>
            el.dataset.pbSpacer === undefined
            && el.dataset.pbSectionSpacer === undefined
            && el.dataset.coverPage === undefined);
        if (!els.length) return null;
        const wrapTop = wrapper.getBoundingClientRect().top;
        const zoom = parseFloat((wrapper.style as any).zoom || '1') || 1;
        const topDoc = (el: HTMLElement) => (el.getBoundingClientRect().top - wrapTop) / zoom;
        let idx = 0;
        for (let i = 0; i < els.length; i++) { if (topDoc(els[i]) <= yAnchor) idx = i; else break; }
        return { anchorIdx: idx, anchorDy: yAnchor - topDoc(els[idx]) };
    };

    const onOverlayMouseDown = (e: React.MouseEvent) => {
        if (activeTool === 'none') return;
        e.preventDefault();
        const pt = getDocPoint(e);
        setDrawing(true); setDrawStart(pt); setCurrentDraw(pt);
    };
    const onOverlayMouseMove = (e: React.MouseEvent) => {
        if (!drawing || !drawStart) return;
        setCurrentDraw(getDocPoint(e));
    };
    const onOverlayMouseUp = (e: React.MouseEvent) => {
        if (!drawing || !drawStart) return;
        const pt = getDocPoint(e);
        const dx = pt.x - drawStart.x; const dy = pt.y - drawStart.y;
        const minSize = 10;
        if (activeTool === 'arrow' && (Math.abs(dx) > minSize || Math.abs(dy) > minSize)) {
            const anchor = computeAnnAnchor(Math.min(drawStart.y, pt.y));
            setAnnotations(prev => [...prev, { id: `a-${Date.now()}`, type: 'arrow', x1: drawStart.x, y1: drawStart.y, x2: pt.x, y2: pt.y, color: arrowColor, ...(anchor ?? {}) }]);
        } else if (activeTool === 'blur' && Math.abs(dx) > minSize && Math.abs(dy) > minSize) {
            const anchor = computeAnnAnchor(Math.min(drawStart.y, pt.y));
            setAnnotations(prev => [...prev, { id: `b-${Date.now()}`, type: 'blur', x: Math.min(drawStart.x, pt.x), y: Math.min(drawStart.y, pt.y), w: Math.abs(dx), h: Math.abs(dy), ...(anchor ?? {}) }]);
        }
        setDrawing(false); setDrawStart(null); setCurrentDraw(null);
    };

    const removeAnnotation = (annId: string) => setAnnotations(prev => prev.filter(a => a.id !== annId));

    // Canc/Backspace eliminano l'annotazione selezionata quando il focus non è nel testo.
    useEffect(() => {
        if (!selectedId) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            const ae = document.activeElement as HTMLElement | null;
            if (ae && (ae.isContentEditable || ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return;
            e.preventDefault();
            removeAnnotation(selectedId);
            setSelectedId(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    /** Apre il lightbox: mostra subito la miniatura e carica l'alta risoluzione in background. */
    const handleOpenPreview = async (item: any) => {
        setPreviewFile({ ...item, previewUrl: item.thumbUrl });
        setPreviewLoading(true);
        try {
            const hdUrl = await getHdUrl(item);
            setPreviewFile((prev: any) => prev ? { ...prev, previewUrl: hdUrl } : null);
        } catch {
            // Resta la miniatura
        } finally {
            setPreviewLoading(false);
        }
    };

    // 6. Download PDF.
    // Generazione lato client (html2canvas), usata come fallback.
    const handleDownloadPdfClient = async (mode: 'download' | 'preview' = 'download') => {
        if (!a4WrapperRef.current || !scrollContainerRef.current) return;
        setIsSaving(true);
        setIsGeneratingPdf(true);
        setPdfProgress(null);
        // Libera l'eventuale anteprima precedente per evitare memory leak.
        if (previewPdfUrl) {
            URL.revokeObjectURL(previewPdfUrl);
            setPreviewPdfUrl(null);
        }
        const scrollContainer = scrollContainerRef.current;
        const wrapper = a4WrapperRef.current;
        const oldScrollTop = scrollContainer.scrollTop;
        const oldOverflow = scrollContainer.style.overflow;
        const oldHeight = scrollContainer.style.height;
        const prevZoom = (wrapper.style as any).zoom as string;
        try {
            await saveToBackend();

            // Header/footer dalle impostazioni, con cache base64. La chiave è l'URL senza
            // querystring (la firma S3 scade ma il path resta).
            let headerImg: { data: string; format: string } | null = null;
            let footerImg: { data: string; format: string } | null = null;
            try {
                const settingsRes = await SettingsService.getAll();
                const ag = (settingsRes as any).data?.[0] || (settingsRes as any)?.[0];
                const stableKey = (url?: string) => url ? url.split('?')[0] : null;
                const cache = pdfBrandingCacheRef.current;

                const newHeaderKey = stableKey(ag?.reportHeaderUrl);
                if (newHeaderKey && newHeaderKey === cache.headerKey && cache.headerImg) {
                    headerImg = cache.headerImg;
                } else if (ag?.reportHeaderUrl) {
                    headerImg = await urlToBase64(ag.reportHeaderUrl);
                    cache.headerKey = newHeaderKey;
                    cache.headerImg = headerImg;
                } else {
                    cache.headerKey = null;
                    cache.headerImg = null;
                }

                const newFooterKey = stableKey(ag?.reportFooterUrl);
                if (newFooterKey && newFooterKey === cache.footerKey && cache.footerImg) {
                    footerImg = cache.footerImg;
                } else if (ag?.reportFooterUrl) {
                    footerImg = await urlToBase64(ag.reportFooterUrl);
                    cache.footerKey = newFooterKey;
                    cache.footerImg = footerImg;
                } else {
                    cache.footerKey = null;
                    cache.footerImg = null;
                }
            } catch (e) {
                console.warn('Header/footer non caricati:', e);
            }

            const A4_W_MM = 210;
            const A4_H_MM = 297;
            // Geometria fissa, allineata a PAGE_H_PX (227mm). Non deve dipendere dalle
            // immagini, altrimenti la slice HTML verrebbe stirata e deformata.
            const HEADER_MM = 35;
            const FOOTER_MM = 25;
            const CONTENT_PAD_MM = 5;
            const CONTENT_Y_MM = HEADER_MM + CONTENT_PAD_MM;
            const CONTENT_H_MM = A4_H_MM - HEADER_MM - FOOTER_MM - CONTENT_PAD_MM * 2;
            const MARGIN_L_MM = pageMargins === 'narrow' ? 15 : pageMargins === 'wide' ? 35 : 25;

            scrollContainer.style.overflow = 'visible';
            scrollContainer.style.height = 'auto';
            scrollContainer.scrollTop = 0;

            // Forza la vista "all" così il PDF include tutte le sezioni anche se l'editor
            // ne mostra solo una parte.
            const editorEl = editorRef.current;
            const prevViewMode = editorEl?.dataset.viewMode || 'all';
            if (editorEl) editorEl.dataset.viewMode = 'all';
            // Reflow per misurare lo scrollHeight completo dopo aver mostrato tutto
            void wrapper.offsetHeight;
            await new Promise(r => setTimeout(r, 100));

            // Conta le pagine sul reflow appena fatto e aggiorna lo span del template prima
            // della cattura (il ResizeObserver è asincrono).
            if (editorEl) {
                const pagesNow = Math.max(1, Math.ceil(editorEl.scrollHeight / PAGE_H_PX));
                editorEl.querySelectorAll('[data-pages-count]').forEach(el => {
                    el.textContent = String(pagesNow);
                });
            }

            // Il canvas del browser ha un limite (~32767px per lato su desktop). Con scala 3
            // un report lungo lo supera e il PDF esce corrotto, quindi riduciamo la scala
            // quanto basta: sui report corti resta 3, su quelli lunghi cala di poco.
            const MAX_CANVAS_PX = 30000;
            const SCALE = Math.min(3, MAX_CANVAS_PX / Math.max(1, wrapper.scrollHeight));
            // Azzera lo zoom del wrapper prima della cattura
            (wrapper.style as any).zoom = '1';
            await new Promise(r => setTimeout(r, 50));

            // Aspetta i font (Tinos): html2canvas misura i glifi con measureText e con il font
            // di fallback le larghezze sarebbero sbagliate.
            try { await document.fonts.ready; } catch { /* Non bloccante */ }

            const baseCanvas = await html2canvas(wrapper, {
                scale: SCALE, useCORS: true, allowTaint: false, backgroundColor: '#ffffff',
                width: wrapper.offsetWidth, height: wrapper.scrollHeight, scrollY: 0, scrollX: 0,
                onclone: (clonedDoc) => {
                    clonedDoc.querySelectorAll<HTMLElement>('[style*="position:fixed"]').forEach(el => el.remove());
                    clonedDoc.querySelectorAll<HTMLElement>('span.select-none').forEach(el => { el.style.visibility = 'hidden'; });
                    // Nasconde i separatori di pagina nel PDF
                    clonedDoc.querySelectorAll<HTMLElement>('[data-page-sep]').forEach(el => el.remove());
                    // Nasconde la maniglia di resize delle etichette data-ora
                    clonedDoc.querySelectorAll<HTMLElement>('.ts-resize').forEach(el => el.remove());
                    // Gli spacer restano: html2canvas misura scrollHeight prima dell'onclone, e
                    // toglierli lascerebbe pagine vuote in coda. Sono div vuoti e non si vedono.
                    clonedDoc.querySelectorAll<HTMLImageElement>('img').forEach(img => { img.crossOrigin = 'anonymous'; });
                    // Azzera lo zoom nel clone
                    const wClone = clonedDoc.getElementById('a4-wrapper');
                    if (wClone) (wClone.style as any).zoom = '1';
                    // Normalizza word-spacing e letter-spacing: con letter-spacing ≠ 0 html2canvas
                    // disegna un glifo alla volta e perde il kerning, con testo attaccato nel PDF.
                    clonedDoc.querySelectorAll<HTMLElement>('p, div, span, li, td, th, h1, h2, h3, h4, table').forEach(el => {
                        if (!el.style.wordSpacing) el.style.wordSpacing = 'normal';
                        el.style.letterSpacing = 'normal';
                    });
                    // html2canvas mette la baseline del testo nelle celle un po' più in basso del
                    // browser. Alziamo il testo di CELL_TEXT_RISE_PX avvolgendo il contenuto in un
                    // blocco con position:relative e top negativo: il layout della tabella non cambia,
                    // si sposta solo il testo. Solo sul clone. Il valore si tara qui.
                    const CELL_TEXT_RISE_PX = 5;
                    clonedDoc.querySelectorAll<HTMLTableCellElement>('td, th').forEach(cell => {
                        if (!cell.firstChild) return;
                        const inner = clonedDoc.createElement('span');
                        inner.style.display = 'block';
                        inner.style.position = 'relative';
                        inner.style.top = `-${CELL_TEXT_RISE_PX}px`;
                        while (cell.firstChild) inner.appendChild(cell.firstChild);
                        cell.appendChild(inner);
                    });
                    // html2canvas non unisce i bordi con border-collapse:collapse e disegna quelli di
                    // ogni cella, raddoppiando i separatori interni. Li convertiamo in bordi separate
                    // senza duplicati: ogni cella tiene bottom+right, la prima riga il top, la prima
                    // colonna il left.
                    clonedDoc.querySelectorAll<HTMLTableElement>('table').forEach(tbl => {
                        const cs = clonedDoc.defaultView?.getComputedStyle(tbl);
                        if ((cs?.borderCollapse ?? tbl.style.borderCollapse) !== 'collapse') return;
                        tbl.style.borderCollapse = 'separate';
                        tbl.style.borderSpacing = '0';
                        Array.from(tbl.rows).forEach((tr, ri) => {
                            Array.from(tr.cells).forEach(td => {
                                if (ri > 0) td.style.borderTopWidth = '0';
                                if (td.cellIndex > 0) td.style.borderLeftWidth = '0';
                            });
                        });
                    });
                    clonedDoc.querySelectorAll<HTMLElement>('[data-blur-box]').forEach(el => {
                        el.style.backdropFilter = 'none';
                        el.style.setProperty('-webkit-backdrop-filter', 'none');
                        el.style.backgroundColor = '#9ca3af';
                        el.style.backgroundImage = `linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #9ca3af 75%, #9ca3af), linear-gradient(45deg, #ffffff 25%, transparent 25%, transparent 75%, #9ca3af 75%, #9ca3af)`;
                        el.style.backgroundPosition = '0 0, 5px 5px';
                        el.style.backgroundSize = '10px 10px';
                        el.style.border = '1px solid #9ca3af';
                        Array.from(el.children).forEach(child => { (child as HTMLElement).style.display = 'none'; });
                    });
                }
            });
            (wrapper.style as any).zoom = prevZoom;

            scrollContainer.style.overflow = oldOverflow;
            scrollContainer.style.height = oldHeight;
            scrollContainer.scrollTop = oldScrollTop;
            // Ripristina la vista dell'utente
            if (editorEl) editorEl.dataset.viewMode = prevViewMode;

            // px per mm nel canvas
            const pxPerMm = baseCanvas.width / A4_W_MM;
            // Slice allineata alle pagine dell'editor (PAGE_H_PX = area contenuto tra header e footer)
            const fullPagePx = Math.round(PAGE_H_PX * (baseCanvas.width / wrapper.offsetWidth));
            const numPages = Math.ceil(baseCanvas.height / fullPagePx);
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

            for (let i = 0; i < numPages; i++) {
                if (i > 0) pdf.addPage();
                setPdfProgress({ current: i + 1, total: numPages });
                // Cede il controllo al browser così lo stato arriva al modale
                await new Promise(r => setTimeout(r, 0));

                // Slice: 297mm di canvas per pagina, con taglio ai multipli fissi di PAGE_H_PX:
                // gli spacer di applyPageBreaks allineano il contenuto agli stessi confini.
                const sliceH = Math.min(fullPagePx, baseCanvas.height - i * fullPagePx);
                const slice = document.createElement('canvas');
                slice.width = baseCanvas.width; slice.height = Math.ceil(sliceH);
                const sCtx = slice.getContext('2d')!;
                sCtx.fillStyle = '#ffffff'; sCtx.fillRect(0, 0, slice.width, slice.height);
                sCtx.drawImage(baseCanvas, 0, -i * fullPagePx);

                // Pagina HTML mappata 1:1 sull'area contenuto del PDF
                const displayH_mm = (sliceH / fullPagePx) * CONTENT_H_MM;
                pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', 0, CONTENT_Y_MM, A4_W_MM, displayH_mm);

                // Header nei margini del documento, con un piccolo stacco dal bordo
                if (headerImg) {
                    pdf.addImage(headerImg.data, headerImg.format, MARGIN_L_MM, 1, A4_W_MM - 2 * MARGIN_L_MM, HEADER_MM - 2);
                }
                // Footer nei margini del documento, con un piccolo stacco dal bordo
                if (footerImg) {
                    pdf.addImage(footerImg.data, footerImg.format, MARGIN_L_MM, A4_H_MM - FOOTER_MM + 1, A4_W_MM - 2 * MARGIN_L_MM, FOOTER_MM - 2);
                }

                // Numero di pagina sopra il footer
                pdf.setFontSize(7); pdf.setTextColor(130);
                pdf.text(`${i + 1} / ${numPages}`, A4_W_MM / 2, A4_H_MM - (footerImg ? FOOTER_MM + 2 : 5), { align: 'center' });
            }

            if (mode === 'preview') {
                const blob = pdf.output('blob');
                const url = URL.createObjectURL(blob);
                setPreviewPdfUrl(url);
            } else {
                pdf.save(relazioneFileName(id, pratica, null, 'pdf'));
                addToast("PDF scaricato!", "success");
            }
        } catch (err) {
            console.error(err);
            addToast("Errore generazione PDF", "error");
            (wrapper.style as any).zoom = prevZoom;
            scrollContainer.style.overflow = oldOverflow;
            scrollContainer.style.height = oldHeight;
            scrollContainer.scrollTop = oldScrollTop;
            if (editorRef.current) editorRef.current.dataset.viewMode = viewSection;
        } finally {
            setIsSaving(false);
            setIsGeneratingPdf(false);
            setPdfProgress(null);
        }
    };

    // Download e anteprima tramite render server (Chromium); prima si salva, e se il
    // server fallisce si torna al client.
    // Anteprima live: un render alla volta, ripetuto se nel frattempo sono arrivate modifiche.
    const refreshLivePreview = useCallback(async () => {
        if (!id) return;
        if (livePreviewRenderingRef.current) { livePreviewPendingRef.current = true; return; }
        livePreviewRenderingRef.current = true;
        setLivePreviewLoading(true);
        try {
            await saveToBackendRef.current();
            const blob = await PraticaService.downloadRelazionePdf(Number(id));
            setLivePreviewBlob(blob);
            setLivePreviewStale(false);
        } catch (e: any) {
            const status = e?.response?.status;
            addToast(status ? `Anteprima non aggiornata (errore ${status})` : 'Anteprima non aggiornata: server non raggiungibile', 'error');
        } finally {
            setLivePreviewLoading(false);
            livePreviewRenderingRef.current = false;
            if (livePreviewPendingRef.current) { livePreviewPendingRef.current = false; refreshLivePreview(); }
        }
    }, [id, addToast]);
    useEffect(() => { refreshLivePreviewRef.current = refreshLivePreview; }, [refreshLivePreview]);
    useEffect(() => { annotationsCountRef.current = annotations.length; }, [annotations]);
    // Aggiungere, spostare o eliminare annotazioni è una modifica: segna "non salvato"
    // e rigenera l'anteprima live.
    // Il primo passaggio dopo il caricamento riguarda le annotazioni salvate: non è una modifica.
    const annotationsAtLoadRef = useRef<typeof annotations | null>(null);
    useEffect(() => {
        if (!isLoaded) return;
        if (annotationsAtLoadRef.current === null) { annotationsAtLoadRef.current = annotations; return; }
        if (annotations === annotationsAtLoadRef.current) return;
        setIsDirty(true);
        if (livePreviewOpenRef.current && livePreviewAutoRef.current) {
            setLivePreviewStale(true);
            if (livePreviewDebounceRef.current) clearTimeout(livePreviewDebounceRef.current);
            livePreviewDebounceRef.current = setTimeout(() => refreshLivePreviewRef.current(), 3500);
        }
    }, [annotations, isLoaded]);
    const toggleLivePreview = useCallback(() => {
        setLivePreviewOpen(o => {
            const next = !o;
            if (next && !livePreviewBlobRef.current) setTimeout(() => refreshLivePreviewRef.current(), 0);
            return next;
        });
    }, []);
    useEffect(() => () => {
        if (livePreviewDebounceRef.current) clearTimeout(livePreviewDebounceRef.current);
    }, []);

    const handleDownloadPdf = async (mode: 'download' | 'preview' = 'download') => {
        if (!id) return;
        // Sempre render server, anche con censure: html2canvas esce bianco oltre ~113
        // pagine. Il client resta solo come fallback.
        setIsSaving(true);
        setPdfGenerating(mode);
        addToast(mode === 'preview' ? "Genero l'anteprima…" : 'Genero il PDF…', 'info');
        try {
            await saveToBackend();
            const blob = await PraticaService.downloadRelazionePdf(Number(id));
            const url = URL.createObjectURL(blob);
            if (mode === 'preview') { setPreviewPdfUrl(url); }
            else {
                const a = document.createElement('a');
                a.href = url; a.download = relazioneFileName(id, pratica, null, 'pdf');
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 60000);
                addToast('PDF scaricato!', 'success');
            }
        } catch (e) {
            console.warn('Render server relazione fallito, uso il client:', e);
            addToast('Genero il PDF nel browser…', 'info');
            await handleDownloadPdfClient(mode);
        } finally { setIsSaving(false); setPdfGenerating(false); }
    };

    // 7. Comandi editor.
    // Ripristina l'ultima selezione valida (vedi savedRangeRef) prima di applicare
    // comandi lanciati dai <select> del font, che hanno preso il focus.
    const restoreSel = () => {
        const r = savedRangeRef.current;
        const editor = editorRef.current;
        if (!r || !editor) return;
        editor.focus();
        const sel = window.getSelection();
        if (!sel) return;
        sel.removeAllRanges();
        sel.addRange(r);
    };
    const execCmd = (cmd: string, val?: string) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); refreshActiveFmt(); };
    const insertHtml = (html: string) => { editorRef.current?.focus(); document.execCommand('insertHTML', false, html); };

    // Interruzione di pagina manuale: inserisce un marker atomico (contenteditable=false)
    // nel punto del cursore. A schermo applyPageBreaks lo porta a inizio pagina; nel PDF
    // il render server usa break-before:page e lo rende invisibile.
    const insertPageBreak = () => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        // Marker alto ~0 (bordo 2px) con etichetta in posizione assoluta, così non aggiunge
        // altezza e il contenuto successivo coincide col PDF.
        document.execCommand('insertHTML', false,
            `<div data-page-break="1" contenteditable="false" ` +
            `style="position:relative;height:0;margin:0;border-top:2px dashed #a5b4fc;user-select:none;">` +
            `<span style="position:absolute;top:-8px;left:50%;transform:translateX(-50%);` +
            `background:#ffffff;padding:0 8px;color:#4f46e5;font-size:10px;font-weight:600;` +
            `letter-spacing:0.06em;text-transform:uppercase;white-space:nowrap;">` +
            `Interruzione di pagina</span></div>`);
        editor.querySelectorAll('[data-page-break]').forEach(node => {
            const el = node as HTMLElement;
            const parent = el.parentElement;
            if (!parent || parent === editor) return;
            const after = parent.cloneNode(false) as HTMLElement;
            let n: ChildNode | null = el.nextSibling;
            while (n) { const nx: ChildNode | null = n.nextSibling; after.appendChild(n); n = nx; }
            editor.insertBefore(el, parent.nextSibling);
            if (after.childNodes.length) editor.insertBefore(after, el.nextSibling);
        });
        markChanged();
        setTimeout(() => { applyPageBreaks(); recomputeTotalPages(); }, 0);
    };

    const insertTriangleBullet = () => {
        if (!editorRef.current) return;
        // Ripristina la selezione salvata: senza, il click sul pulsante porterebbe il caret
        // a inizio documento e la vista alla prima pagina.
        editorRef.current.focus();
        restoreSel();
        document.execCommand('insertUnorderedList', false);
        // Dopo aver attivato la lista applica lo stile triangolo all'<ul> creato
        const sel = window.getSelection();
        if (sel && sel.anchorNode) {
            let node: Node | null = sel.anchorNode;
            while (node && node !== editorRef.current) {
                if ((node as HTMLElement).tagName === 'UL') break;
                node = node.parentNode;
            }
            if (node && (node as HTMLElement).tagName === 'UL') {
                const ul = node as HTMLUListElement;
                ul.style.listStyleType = '"▶  "';
                ul.style.paddingLeft = '22px';
                ul.style.lineHeight = '1.7';
                ul.style.marginLeft = '4px';
            }
        }
        editorRef.current.focus();
        refreshActiveFmt();
    };

    const extractS3Key = (url: string): string | null => {
        try {
            const u = new URL(url);
            return u.pathname.startsWith('/') ? u.pathname.slice(1) : u.pathname;
        } catch { return null; }
    };

    /**
     * Inserisce un'immagine nel documento.
     * @param hdUrl        URL firmato da usare nel tag <img>
     * @param originalPath URL originale o key S3 per il data attribute (usato al reload)
     */
    // Testo del collaboratore e risultato AI vanno inseriti come testo, non come HTML
    // (<, > e & romperebbero il markup). Ogni riga diventa un <p>.
    const escHtmlText = (s: string): string => String(s ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const textToParagraphs = (s: string): string => {
        const lines = String(s ?? '').replace(/\r\n?/g, '\n').split('\n');
        return lines.map(l => `<p>${l.trim() ? escHtmlText(l) : '<br/>'}</p>`).join('');
    };

    const insertImage = (hdUrl: string, originalPath?: string) => {
        // hdUrl è l'URL da mostrare: la versione compressa, che esiste sempre.
        // data-img-key è l'originale (alta risoluzione), data-img-preview-key la compressa
        // (usata per rinfrescare, vedi signedImageUrlCache).
        // Un URL `data:` (editor forense, frame video non caricato) non ha una chiave S3
        // e non deve finire negli attributi: sarebbe l'intero base64.
        const isDataUrl = (u?: string) => !!u && u.startsWith('data:');
        const originalKey = isDataUrl(originalPath) || (!originalPath && isDataUrl(hdUrl))
            ? null
            : (originalPath ? extractS3Key(originalPath) : extractS3Key(hdUrl));
        const previewKey = isDataUrl(hdUrl) ? null : extractS3Key(hdUrl);
        const keyAttrs =
            (originalKey ? ` data-img-key="${originalKey}"` : '') +
            (previewKey ? ` data-img-preview-key="${previewKey}"` : '');
        insertHtml(
            `<div class="report-media-box my-4 text-center">` +
            `<img src="${hdUrl}"${keyAttrs} crossorigin="anonymous" ` +
            `style="display:block; margin-left:auto; margin-right:auto; max-width:85%; max-height:${MAX_IMG_H}px; height:auto; border-radius:8px; border:1px solid #ddd; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);" ` +
            `class="mx-auto block"/>` +
            `</div><p><br/></p>`
        );
        // applyPageBreaks solo quando l'immagine è decodificata e ha le sue dimensioni
        requestAnimationFrame(async () => {
            const editor = editorRef.current;
            if (!editor) return;
            const imgs = Array.from(editor.querySelectorAll<HTMLImageElement>('.report-media-box img'));
            const target = [...imgs].reverse().find(img => img.src === hdUrl || img.src.endsWith(hdUrl));
            if (!target) { applyPageBreaks(); return; }
            try { await target.decode(); } catch { /* Già caricata o in errore */ }
            applyPageBreaks();
        });
    };

    const captureVideoFrame = async () => {
        if (!videoPlayerRef.current || !currentVideoId) return;
        const v = videoPlayerRef.current;
        const canvas = document.createElement("canvas");
        canvas.width = v.videoWidth; canvas.height = v.videoHeight;
        canvas.getContext("2d")?.drawImage(v, 0, 0);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        const videoIdLocal = currentVideoId;

        // UI ottimistica: aggiunge subito il frame con il dataUrl, segnato come in caricamento.
        setCapturedFrames(prev => ({
            ...prev,
            [videoIdLocal]: [...(prev[videoIdLocal] || []), { url: dataUrl, pending: true }],
        }));
        addToast("Frame acquisito! Caricamento in corso...", "info");

        // Upload su S3 come file della pratica "Frame_<videoId>_<ts>" (escluso dalle
        // gallerie principali per il prefisso).
        if (!id) return;
        try {
            const blob = await (await fetch(dataUrl)).blob();
            const ts = Date.now();
            const file = new File([blob], `Frame_${videoIdLocal}_${ts}.jpg`, { type: 'image/jpeg' });
            const res = await FilesService.uploadToPratica(Number(id), file, false);
            const uploaded: any = res?.data ?? res;
            const finalUrl = uploaded?.urlCompressed || uploaded?.urlOriginal || dataUrl;
            const originalUrl: string = uploaded?.urlOriginal || uploaded?.finalUrl || dataUrl;
            const fileId: number | undefined = uploaded?.id;
            // Sostituisce il segnaposto con i dati reali (URL S3 + fileId)
            setCapturedFrames(prev => {
                const arr = [...(prev[videoIdLocal] || [])];
                const idx = arr.findIndex(f => f.url === dataUrl && f.pending);
                if (idx >= 0) arr[idx] = { url: finalUrl, originalUrl, fileId, pending: false };
                return { ...prev, [videoIdLocal]: arr };
            });
            addToast("Frame salvato su cloud", "success");
        } catch (e) {
            console.error('Upload frame fallito:', e);
            addToast("Errore upload frame — il frame resta visibile ma non sara' disponibile al prossimo riavvio.", "error");
            // Toglie il flag di caricamento: il dataUrl resta in memoria ma non è salvato
            setCapturedFrames(prev => {
                const arr = [...(prev[videoIdLocal] || [])];
                const idx = arr.findIndex(f => f.url === dataUrl && f.pending);
                if (idx >= 0) arr[idx] = { url: dataUrl, pending: false };
                return { ...prev, [videoIdLocal]: arr };
            });
        }
    };

    const startEditing = (url: string) => { setEditingImage(url); setIsEditorOpen(true); };

    const getMarginClass = () => {
        switch (pageMargins) {
            case 'narrow': return `px-[15mm] py-[6mm]`;
            case 'wide':   return `px-[35mm] py-[6mm]`;
            default:       return `px-[25mm] py-[6mm]`;
        }
    };

    // 8. Ottimizzazione testo
    const openAiModal = (ctx: string) => { setCurrentAiContext(ctx); setAiResultText(''); setAiCustomPrompt(''); setAiModalOpen(true); runAi(ctx); };
    const runAi = async (baseText: string) => {
        setIsAiLoading(true);
        try {
            const res = await AiService.enhanceText(baseText, aiCustomPrompt);
            if (res.success) { setAiResultText(res.data); setAiCustomPrompt(''); }
            else addToast(res.message || "Errore AI", "error");
        } catch { addToast("Impossibile connettersi all'AI.", "error"); }
        finally { setIsAiLoading(false); }
    };

    // 9. Media e note

    /**
     * Ogni elemento di organizedMedia contiene:
     * thumbUrl     → URL compresso per la sidebar
     * hdUrl        → URL originale per l'inserimento nel documento
     * isFieldMedia → media da campo o file ufficio
     * category     → categoria dal nome file [CATEGORIA] o 'DOC. UFFICIO'
     */
    const organizedMedia = useMemo(() => {
        const groups: Record<string, Record<string, OrganizedMediaGroup>> = {};
        const allMedia: any[] = [];

        // Chiave data locale YYYY-MM-DD (evita lo spostamento UTC).
        const toLocalDateKey = (raw: any): string | null => {
            if (!raw) return null;
            const d = new Date(raw);
            if (isNaN(d.getTime())) return null;
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };

        // Categoria: prima il campo DB `categoria`, poi il prefisso [CATEGORIA] nel nome
        // per i media più vecchi. 'DOCUMENTAZIONE' se mancano entrambi (va in ufficioMedia).
        const extractCategory = (m: any): string => {
            const fromDb = (m?.categoria as string | undefined)?.trim();
            if (fromDb) return fromDb.toUpperCase();
            const catMatch = (m?.filename as string)?.match(/^\[(.*?)\]/);
            return catMatch ? catMatch[1].toUpperCase() : 'DOCUMENTAZIONE';
        };

        // Le foto del contachilometri non sono prove di campo e restano fuori dalla
        // sidebar. Gli upload recenti hanno solo la `categoria`, non il prefisso nel
        // nome, quindi controlliamo entrambi.
        const isKmPhoto = (m: any): boolean =>
            (m?.categoria || '').toString().trim().toUpperCase() === 'KM'
            || /^Km_(Iniziali|Finali)_/i.test(m?.filename || '');

        // I file ufficio (pratica.files) sono gestiti a parte in ufficioMedia.

        // B. Media dai report dei collaboratori (DOCUMENTAZIONE esclusa, va in ufficioMedia)
        reports.forEach(r => {
            const mediaList = r.media || r.mediaSelezionati || [];
            mediaList.forEach((m: any) => {
                // Come in TabMedia/FileManagerView: niente media in attesa di approvazione.
                if (m.analysisStatus === 'DUPLICATE' || m.analysisStatus === 'OUTSIDE_RANGE' || m.analysisStatus === 'PENDING_APPROVAL') return;
                if (isKmPhoto(m)) return; // Foto km fuori dalla sidebar
                const category = extractCategory(m);
                if (category === 'DOCUMENTAZIONE') return;
                allMedia.push({
                    id: m.id,
                    name: m.filename,
                    thumbUrl: m.urlCompressed || m.finalUrl,
                    hdUrl: m.finalUrl,
                    createdAt: m.dataAcquisizione || m.uploadTime || r.dataRelazione,
                    // reportDateKey = giorno del report (raggruppamento per il continuativo).
                    reportDateKey: toLocalDateKey(r.dataRelazione) || toLocalDateKey(m.dataAcquisizione || m.uploadTime),
                    type: isFieldVideo(m) ? 'video' : 'image',
                    isFieldMedia: true,
                    category,
                    soggettoNome: soggettoDelReport(r),
                });
            });
        });

        // C. Media dagli incarichi (DOCUMENTAZIONE esclusa, va in ufficioMedia)
        incarichi.forEach(inc => {
            const incReportKey = toLocalDateKey((inc as any).relazioni?.[0]?.dataRelazione);
            inc.media?.forEach((m: any) => {
                // Si escludono solo i media senza dataAcquisizione né uploadTime. I video di
                // solito non hanno dataAcquisizione, quindi filtrare solo su quella li escluderebbe.
                if (!m.dataAcquisizione && !m.uploadTime) return;
                // Come in TabMedia/FileManagerView: niente media in attesa di approvazione.
                if (m.analysisStatus === 'DUPLICATE' || m.analysisStatus === 'OUTSIDE_RANGE' || m.analysisStatus === 'PENDING_APPROVAL') return;
                const isDuplicate = allMedia.some(e => e.id === m.id && e.isFieldMedia);
                if (isDuplicate) return;
                if (isKmPhoto(m)) return; // Foto km fuori dalla sidebar
                const category = extractCategory(m);
                if (category === 'DOCUMENTAZIONE') return;
                allMedia.push({
                    id: m.id,
                    name: m.filename,
                    thumbUrl: m.urlCompressed || m.finalUrl,
                    hdUrl: m.finalUrl,
                    createdAt: m.dataAcquisizione || m.uploadTime,
                    reportDateKey: incReportKey || toLocalDateKey(m.dataAcquisizione || m.uploadTime),
                    type: isFieldVideo(m) ? 'video' : 'image',
                    isFieldMedia: true,
                    isUnreported: true,
                    category,
                    soggettoNome: (inc as any).soggettoNome || null,
                });
            });
        });

        // Filtro data (__ufficio__ indica la sezione ufficio e qui non filtra)
        let filtered = allMedia;
        if (selectedDate && selectedDate !== '__ufficio__') {
            // Filtra sul giorno del report (per il continuativo include le foto dopo mezzanotte).
            filtered = filtered.filter(m => (m.reportDateKey || (m.createdAt ? m.createdAt.slice(0, 10) : null)) === selectedDate);
        }

        // Filtro categoria
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(m => m.category === selectedCategory);
        }

        // Filtro soggetto
        if (selectedSoggetto) {
            filtered = filtered.filter(m => delSoggetto(m.soggettoNome, selectedSoggetto));
        }

        // Ordine cronologico crescente
        filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        // Raggruppamento per giorno del report
        filtered.forEach(item => {
            const groupSource = item.reportDateKey || (item.createdAt ? item.createdAt.slice(0, 10) : null);
            const dateKey = groupSource
                ? new Date(`${groupSource}T12:00:00`).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })
                : 'Senza Data';

            const category = item.category || 'DOCUMENTAZIONE';

            if (!groups[dateKey]) groups[dateKey] = {};
            if (!groups[dateKey][category]) groups[dateKey][category] = { images: [], videos: [] };

            if (item.type === 'video') groups[dateKey][category].videos.push(item);
            else groups[dateKey][category].images.push(item);
        });

        return groups;
    }, [pratica, reports, incarichi, selectedDate, selectedCategory, selectedSoggetto]);

    /** Categorie presenti nei media (per i filtri a pillola) */
    const availableCategories = useMemo(() => {
        const cats = new Set<string>();
        Object.values(organizedMedia).forEach(dateGroup => {
            Object.keys(dateGroup).forEach(cat => cats.add(cat));
        });
        return Array.from(cats).sort();
    }, [organizedMedia]);

    /** Soggetti distinti degli incarichi (mostrati solo se più di uno) */
    const availableSoggetti = useMemo(() => {
        const names = new Set<string>();
        incarichi.forEach(inc => { if ((inc as any).soggettoNome) names.add((inc as any).soggettoNome); });
        return Array.from(names).sort();
    }, [incarichi]);

    /** Documentazione senza data: pratica.files + media DOCUMENTAZIONE di report e incarichi */
    const ufficioMedia = useMemo(() => {
        // Le foto del contachilometri (Km_Iniziali_* / Km_Finali_*) non hanno categoria
        // né prefisso e finirebbero in "Documentazione Ufficio": le escludiamo.
        const isKmPhoto = (name?: string) => !!name && /^Km_(Iniziali|Finali)_/i.test(name);
        // I file privati del cliente (documento d'identità, privacy firmata, dati
        // soggetto, frame da video) non devono comparire in "Documentazione Ufficio".
        // Stessa regola di InfoTab/TabMedia/FileManagerView e del backend
        // (IncaricoController.PRIVATE_FILE_PREFIX_RE).
        const PRIVATE_FILE_RE = /^(datisoggetto_|privacy_|docid_|frame_)/i;
        const items: any[] = [];
        const seenIds = new Set<number | undefined>();

        // pratica.files va sempre in documentazione ufficio (esclusi i Frame_ dei video)
        pratica?.files?.forEach(f => {
            if (PRIVATE_FILE_RE.test(f.name || '')) return; // File privati del cliente
            const type = getMediaType(f);
            if (type !== 'doc' && !f.name.toLowerCase().includes('mandato') && !isKmPhoto(f.name)) {
                items.push({ id: f.id, name: f.name, thumbUrl: f.urlCompressed || f.urlOriginal, hdUrl: f.urlOriginal, type, isFieldMedia: false });
                seenIds.add(f.id);
            }
        });

        // I media vanno in "Documentazione Ufficio" solo se non hanno una categoria, né nel
        // campo DB né come prefisso [CAT] nel nome.
        const hasSpecificCategory = (m: any): boolean => {
            const fromDb = (m?.categoria as string | undefined)?.trim();
            if (fromDb && fromDb.toUpperCase() !== 'DOCUMENTAZIONE') return true;
            return /^\[(.*?)]/.test((m?.filename as string) || '');
        };

        // Media dei report con categoria DOCUMENTAZIONE
        reports.forEach(r => {
            const mediaList = r.media || r.mediaSelezionati || [];
            mediaList.forEach((m: any) => {
                if (isKmPhoto(m.filename) || seenIds.has(m.id)) return;
                // Come in organizedMedia: i video spesso non hanno dataAcquisizione, si usa uploadTime.
                if (!m.dataAcquisizione && !m.uploadTime) return;
                // Come in TabMedia/FileManagerView: niente media in attesa di approvazione.
                if (m.analysisStatus === 'DUPLICATE' || m.analysisStatus === 'OUTSIDE_RANGE' || m.analysisStatus === 'PENDING_APPROVAL') return;
                if (hasSpecificCategory(m)) return; // Con categoria va tra i media datati
                seenIds.add(m.id);
                items.push({ id: m.id, name: m.filename, thumbUrl: m.urlCompressed || m.finalUrl, hdUrl: m.finalUrl, type: isFieldVideo(m) ? 'video' : 'image', isFieldMedia: true });
            });
        });

        // Media degli incarichi con categoria DOCUMENTAZIONE
        incarichi.forEach(inc => {
            inc.media?.forEach((m: any) => {
                if (isKmPhoto(m.filename) || seenIds.has(m.id)) return;
                // Come in organizedMedia: i video spesso non hanno dataAcquisizione, si usa uploadTime.
                if (!m.dataAcquisizione && !m.uploadTime) return;
                // Come in TabMedia/FileManagerView: niente media in attesa di approvazione.
                if (m.analysisStatus === 'DUPLICATE' || m.analysisStatus === 'OUTSIDE_RANGE' || m.analysisStatus === 'PENDING_APPROVAL') return;
                if (hasSpecificCategory(m)) return;
                seenIds.add(m.id);
                items.push({ id: m.id, name: m.filename, thumbUrl: m.urlCompressed || m.finalUrl, hdUrl: m.finalUrl, type: isFieldVideo(m) ? 'video' : 'image', isFieldMedia: true });
            });
        });

        return items;
    }, [pratica, reports, incarichi]);

    /**
     * Report giornalieri inviati dai collaboratori dall'app, filtrati per data e
     * fascia oraria scelte nella sidebar.
     */
    const dailyReports = useMemo(() => {
        if (!reports.length) return [];
        return reports
            .map((r: any) => ({
                id: r.id,
                testo: r.testoRelazione || '',
                autore: `${r.collaboratore?.name || ''} ${r.collaboratore?.surname || ''}`.trim() || 'Collaboratore',
                data: r.dataRelazione ? new Date(r.dataRelazione).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'long' }) : 'N/D',
                fullDate: r.dataRelazione,
                oreGps: r.oreGps ?? null,
                oreExtra: r.oreExtra ?? null,
                km: r.km ?? null,
                stato: r.stato || 'INVIATO',
                spese: r.spese || [],
                soggettoNome: soggettoDelReport(r) || '',
            }))
            .filter(r => {
                if (!r.fullDate) return true;
                if (selectedDate && r.fullDate.split('T')[0] !== selectedDate) return false;
                if (selectedSoggetto && !delSoggetto(r.soggettoNome, selectedSoggetto)) return false;
                return true;
            })
            .sort((a, b) => new Date(b.fullDate).getTime() - new Date(a.fullDate).getTime());
    }, [reports, selectedDate, selectedSoggetto]);

    const availableDates = useMemo(() => {
        const dates = new Set<string>();
        // Data locale (YYYY-MM-DD), per non sfasarsi tra UTC e fuso dell'utente.
        const toLocalDateKey = (raw: any): string | null => {
            if (!raw) return null;
            const d = new Date(raw);
            if (isNaN(d.getTime())) return null;
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${dd}`;
        };
        // True per file/media da escludere dai filtri data ('DOCUMENTAZIONE' / 'DOC. UFFICIO').
        const isOfficeOnly = (cat?: string | null): boolean => {
            const c = (cat || '').trim().toUpperCase();
            return !c || c === 'DOCUMENTAZIONE' || c === 'DOC. UFFICIO';
        };

        // Report + media selezionati: la pillola è il giorno del report (continuativo).
        reports.forEach(r => {
            const k = toLocalDateKey(r.dataRelazione);
            if (k) dates.add(k);
            r.mediaSelezionati?.forEach((m: any) => {
                const dk = k || toLocalDateKey(m.dataAcquisizione || m.uploadTime);
                if (dk) dates.add(dk);
            });
        });

        // Media dei collaboratori: pillola = giorno del report dell'incarico (o data reale
        // del file se il report non c'è ancora).
        incarichi.forEach(inc => {
            const incReportKey = toLocalDateKey((inc as any).relazioni?.[0]?.dataRelazione);
            inc.media?.forEach((m: any) => {
                if (/^Frame_/.test(m.filename || '')) return; // I frame catturati nell'editor non contano per i filtri data
                const dk = incReportKey || toLocalDateKey(m.dataAcquisizione || m.uploadTime);
                if (dk) dates.add(dk);
            });
        });

        // Da pratica.files solo i file con categoria, che compaiono nei filtri data come
        // gli altri media; i file ufficio restano in "Documentazione Ufficio".
        pratica?.files?.forEach((f: any) => {
            if (/^Frame_/i.test(f.name || '')) return;
            if (isOfficeOnly(f.categoria)) return;
            const dk = toLocalDateKey(f.dataAcquisizione || f.createdAt);
            if (dk) dates.add(dk);
        });

        // Ordine cronologico crescente: il confronto su YYYY-MM-DD tiene conto dell'anno.
        return Array.from(dates).sort((a, b) => a.localeCompare(b));
    }, [reports, incarichi, pratica]);

    // Render freccia in bozza
    const renderDraftAnnotation = () => {
        if (!drawing || !drawStart || !currentDraw) return null;
        if (activeTool === 'arrow') {
            return (
                <g>
                    <line x1={drawStart.x} y1={drawStart.y} x2={currentDraw.x} y2={currentDraw.y}
                          stroke={arrowColor} strokeWidth="3" strokeLinecap="round"
                          markerEnd="url(#arrowhead-draft)" opacity="0.8"/>
                </g>
            );
        }
        if (activeTool === 'blur') {
            const x = Math.min(drawStart.x, currentDraw.x);
            const y = Math.min(drawStart.y, currentDraw.y);
            const w = Math.abs(currentDraw.x - drawStart.x);
            const h = Math.abs(currentDraw.y - drawStart.y);
            return <rect x={x} y={y} width={w} height={h} fill="rgba(80,80,80,0.4)" stroke="#fff" strokeWidth="1" strokeDasharray="4 2"/>;
        }
        return null;
    };

    // Render.
    // Guardia d'uscita su refresh, chiusura, barra indirizzi e tasto indietro.
    const { requestBack, modal: unsavedModal, suppressNextUnload } = useUnsavedGuard({
        isDirty, lastSaved,
        backPath: id ? `/pratiche/${id}` : undefined,
        onSaveNow: async () => { const ok = await saveToBackend(); if (ok) setIsDirty(false); return ok; },
    });

    return (
        <div className="flex flex-col h-screen bg-gray-100 overflow-hidden font-sans">
            {unsavedModal}

            {/*
              CSS del toggle di sezione: nasconde gli elementi con data-rel-section non
              corrispondente al data-view-mode di #editor-body. Nessun effetto con "all".
            */}
            <style>{`
                #editor-body[data-view-mode="first"] [data-rel-section="body"],
                #editor-body[data-view-mode="first"] [data-rel-section="last"] { display: none !important; }
                #editor-body[data-view-mode="body"] [data-rel-section="first"],
                #editor-body[data-view-mode="body"] [data-rel-section="last"] { display: none !important; }
                #editor-body[data-view-mode="last"] [data-rel-section="first"],
                #editor-body[data-view-mode="last"] [data-rel-section="body"] { display: none !important; }
            `}</style>

            {/* Toolbar principale */}
            {/*
              Tre zone: sinistra (indietro/stato) e destra (salva, anteprima, download) sono
              shrink-0 e sempre visibili; la formattazione al centro è flex-1 min-w-0 e
              scorre in orizzontale se non c'è spazio.
            */}
            <div className="bg-white border-b px-3 lg:px-4 py-2 flex items-center shrink-0 z-20 shadow-sm h-16 gap-2">
                <div className="flex items-center gap-3 shrink-0">
                    <button onClick={requestBack} className="text-gray-500 hover:bg-gray-100 p-2 rounded-full"><ChevronLeft size={20}/></button>
                    <div>
                        <h1 className="font-bold text-gray-800 text-lg hidden xl:block">Editor</h1>
                        <div className="flex items-center gap-2 text-xs">
                            {isSaving
                                ? <span className="text-blue-500 flex items-center gap-1"><RefreshCw size={10} className="animate-spin"/> Salvataggio...</span>
                                : lastSaved
                                    ? <span className="text-green-600 flex items-center gap-1"><Check size={10}/> Salvato {lastSaved.toLocaleTimeString()}</span>
                                    : <span className="text-gray-400">Non salvato</span>}
                            <span className="text-gray-300 mx-1">|</span>
                            <span className="text-gray-500 flex items-center gap-1"><FileText size={10}/> {totalPages} pag.</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-lg border border-gray-200 shadow-inner flex-1 min-w-0 overflow-x-auto no-scrollbar">
                    <button onMouseDown={e => e.preventDefault()} onClick={undo} disabled={!canUndo}
                            className="p-1.5 hover:bg-gray-200 rounded text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-default"
                            title="Annulla (Ctrl+Z)">
                        <RotateCcw size={16}/>
                    </button>
                    <button onMouseDown={e => e.preventDefault()} onClick={redo} disabled={!canRedo}
                            className="p-1.5 hover:bg-gray-200 rounded text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-default"
                            title="Ripristina (Ctrl+Y)">
                        <RotateCw size={16}/>
                    </button>
                    <Divider/>
                    {/*
                      Con una foto selezionata i comandi di formattazione testo sono disattivati
                      (applicati all'immagine la sposterebbero).
                    */}
                    <div
                        className={`flex items-center gap-1 ${selectedEditorImg ? 'pointer-events-none opacity-40' : ''}`}
                        title={selectedEditorImg ? 'Deseleziona la foto per formattare il testo' : undefined}
                    >
                    <ToolbarBtn icon={<Bold size={16}/>}      onClick={() => execCmd('bold')}          title="Grassetto"      active={activeFmt.bold}/>
                    <ToolbarBtn icon={<Italic size={16}/>}    onClick={() => execCmd('italic')}        title="Corsivo"        active={activeFmt.italic}/>
                    <ToolbarBtn icon={<Underline size={16}/>} onClick={() => execCmd('underline')}     title="Sottolineato"   active={activeFmt.underline}/>
                    <Divider/>
                    <ToolbarBtn icon={<AlignLeft size={16}/>}    onClick={() => execCmd('justifyLeft')}   title="Sinistra"       active={activeFmt.justifyLeft}/>
                    <ToolbarBtn icon={<AlignCenter size={16}/>}  onClick={() => execCmd('justifyCenter')} title="Centro"         active={activeFmt.justifyCenter}/>
                    <ToolbarBtn icon={<AlignRight size={16}/>}   onClick={() => execCmd('justifyRight')}  title="Destra"         active={activeFmt.justifyRight}/>
                    <ToolbarBtn icon={<AlignJustify size={16}/>} onClick={() => execCmd('justifyFull')}   title="Giustificato"   active={activeFmt.justifyFull}/>
                    <Divider/>
                    <ToolbarBtn icon={<List size={16}/>}        onClick={() => execCmd('insertUnorderedList')} title="Elenco"   active={activeFmt.insertUnorderedList && !activeFmt.triangleList}/>
                    <ToolbarBtn icon={<ListOrdered size={16}/>} onClick={() => execCmd('insertOrderedList')}  title="Numerato"  active={activeFmt.insertOrderedList}/>
                    <ToolbarBtn
                        icon={<span style={{ fontSize: 13, fontWeight: 'bold', lineHeight: 1 }}>▶</span>}
                        onClick={insertTriangleBullet}
                        title="Elenco triangolo ▶ (come Word)"
                        active={activeFmt.triangleList}
                    />
                    <Divider/>
                    <div className="flex items-center gap-1 bg-white border border-gray-300 rounded px-1.5 py-0.5">
                        <Type size={14} className="text-gray-500"/>
                        <select value={fontLabel}
                                onChange={e => { const s = FONT_SIZES.find(x => x.label === e.target.value); if (s) { restoreSel(); applyFontSize(editorRef, s.pt); setTimeout(() => applyPageBreaksRef.current(), 30); markChanged(); refreshActiveFmt(); } }}
                                className="text-xs bg-transparent outline-none w-14 cursor-pointer">
                            {FONT_SIZES.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
                        </select>
                    </div>
                    <select onChange={e => { restoreSel(); execCmd('fontName', e.target.value); }} defaultValue="Times New Roman"
                            className="text-xs bg-white border border-gray-300 outline-none w-24 cursor-pointer hover:bg-gray-50 rounded px-1.5 py-1 ml-1">
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times</option>
                        <option value="Courier New">Courier</option>
                        <option value="Verdana">Verdana</option>
                    </select>
                    <div className="flex items-center gap-1 bg-white border border-gray-300 rounded px-1.5 py-0.5 ml-1" title="Interlinea">
                        <span className="text-[10px] text-gray-500 font-bold select-none">≡</span>
                        <select
                            value={lineHeightVal}
                            onChange={e => applyLineHeight(e.target.value)}
                            className="text-xs bg-transparent outline-none w-16 cursor-pointer">
                            <option value="" disabled>Interlinea</option>
                            {/*
                              Valore corrente non tra quelli predefiniti (es. 1.35 o ereditato): lo mostriamo
                              come opzione così il menù riflette sempre l'interlinea al cursore.
                            */}
                            {lineHeightVal && !['1', '1.25', '1.35', '1.5', '1.75', '2'].includes(lineHeightVal) && (
                                <option value={lineHeightVal}>{lineHeightVal}</option>
                            )}
                            <option value="1">Singola</option>
                            <option value="1.25">1.25</option>
                            <option value="1.35">1.35</option>
                            <option value="1.5">1.5</option>
                            <option value="1.75">1.75</option>
                            <option value="2">Doppia</option>
                        </select>
                    </div>
                    </div>{/* Fine gruppo controlli testo */}
                    <Divider/>
                    <div className="flex items-center gap-1 bg-white border border-gray-300 rounded px-1.5 py-0.5">
                        <Layout size={14} className="text-gray-500"/>
                        <select onChange={e => {
                                    setPageMargins(e.target.value as any);
                                    markChanged();
                                    // Il margine cambia le dimensioni del testo: ricalcola la paginazione.
                                    setTimeout(() => applyPageBreaksRef.current(), 60);
                                    if (livePreviewOpenRef.current && livePreviewAutoRef.current) {
                                        setLivePreviewStale(true);
                                        if (livePreviewDebounceRef.current) clearTimeout(livePreviewDebounceRef.current);
                                        livePreviewDebounceRef.current = setTimeout(() => refreshLivePreviewRef.current(), 400);
                                    }
                                }} value={pageMargins}
                                className="text-xs bg-transparent outline-none w-20 py-0.5 cursor-pointer">
                            <option value="standard">Standard</option>
                            <option value="narrow">Stretti</option>
                            <option value="wide">Larghi</option>
                        </select>
                    </div>
                    <Divider/>
                    <ToolbarBtn
                        icon={<SeparatorHorizontal size={16}/>}
                        onClick={insertPageBreak}
                        title="Interruzione di pagina — il contenuto sotto va a pagina nuova"
                    />
                </div>

                <div className="flex gap-2 items-center shrink-0">
                    {/* Toggle drawer media, solo su tablet/mobile. */}
                    <button
                        onClick={() => setMediaDrawerOpen(o => !o)}
                        title="Pannello media"
                        className="lg:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200"
                        aria-label="Apri pannello media"
                    >
                        <PanelRightOpen size={18}/>
                    </button>
                    {/* Il toggle "Prime / Relazione / Ultime / Tutto" è nella barra strumenti sotto. */}
                    {/* Sotto xl (1280px) solo icone, testo completo sugli schermi grandi. */}
                    <button onClick={handleReset} title="Reset"
                            className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-xs font-medium border border-transparent hover:border-red-200 flex items-center gap-1">
                        <Trash2 size={16}/> <span className="hidden xl:inline">Reset</span>
                    </button>
                    <button onClick={async () => { const ok = await saveToBackend(); if (ok) setIsDirty(false); }} title="Salva"
                            className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                        <Save size={16}/>
                    </button>
                    {/* Anteprima PDF in-app: lo stesso PDF in un iframe in modale, senza scaricarlo. */}
                    <button onClick={() => handleDownloadPdf('preview')} disabled={isSaving} title="Anteprima PDF (senza scaricare)"
                            className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center disabled:opacity-50">
                        {isSaving ? <RefreshCw size={16} className="animate-spin"/> : <Eye size={16}/>}
                    </button>
                    <button onClick={toggleLivePreview} title="Anteprima PDF affiancata (live)"
                            className={`p-2 rounded-lg border flex items-center justify-center ${livePreviewOpen ? 'bg-blue-50 border-blue-300 text-blue-700' : 'text-gray-700 hover:bg-gray-100 border-gray-300'}`}>
                        <Columns2 size={16}/>
                    </button>
                    <button onClick={() => handleDownloadPdf('download')} disabled={isSaving} title="Scarica PDF"
                            className="p-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg shadow-sm flex items-center justify-center disabled:opacity-50">
                        {isSaving ? <RefreshCw size={16} className="animate-spin"/> : <Download size={16}/>}
                    </button>
                </div>
            </div>

            {/* Barra strumenti annotazione */}
            <div className="bg-white border-b px-4 py-1.5 flex items-center gap-3 shrink-0 z-10 shadow-sm overflow-x-auto no-scrollbar">
                <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg p-1">
                    <span className="text-[10px] font-bold text-gray-400 uppercase px-1">Strumenti</span>
                    <Divider/>
                    <button
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setActiveTool(t => t === 'arrow' ? 'none' : 'arrow'); setSelectedId(null); }}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-bold transition-all ${
                            activeTool === 'arrow' ? 'bg-red-500 text-white shadow-inner' : 'text-gray-600 hover:bg-gray-200'}`}>
                        <ArrowUpRight size={15}/>
                        {activeTool === 'arrow' ? 'Freccia attiva' : 'Freccia'}
                    </button>
                    {activeTool === 'arrow' && (
                        <div className="flex items-center gap-1 ml-1">
                            {['#e63946','#2ec4b6','#ff9f1c','#1982c4','#ffffff'].map(c => (
                                <button key={c} onClick={() => setArrowColor(c)}
                                        className={`w-5 h-5 rounded-full border-2 transition-transform ${arrowColor === c ? 'border-gray-800 scale-110' : 'border-gray-300'}`}
                                        style={{ backgroundColor: c }}/>
                            ))}
                        </div>
                    )}
                    <Divider/>
                    <button
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => { setActiveTool(t => t === 'blur' ? 'none' : 'blur'); setSelectedId(null); }}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-bold transition-all ${
                            activeTool === 'blur' ? 'bg-indigo-500 text-white shadow-inner' : 'text-gray-600 hover:bg-gray-200'}`}>
                        <Layers size={15}/>
                        {activeTool === 'blur' ? 'Censura attiva' : 'Censura'}
                    </button>
                    {annotations.length > 0 && (
                        <>
                            <Divider/>
                            <button onClick={() => setAnnotations(prev => prev.slice(0,-1))}
                                    className="flex items-center gap-1 px-2 py-1.5 text-xs text-orange-600 hover:bg-orange-50 rounded font-bold">
                                <Trash2 size={12}/> Annulla ultima
                            </button>
                            {/* Elenco annotazioni: eliminazione singola sempre disponibile. */}
                            <div className="relative">
                                <button onClick={() => setAnnListOpen(o => !o)}
                                        title="Elenco annotazioni: evidenzia o elimina la singola freccia/censura"
                                        className={`flex items-center gap-1 px-2 py-1.5 text-xs rounded font-bold ${annListOpen ? 'bg-slate-200 text-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}>
                                    <Layers size={12}/> Elenco ({annotations.length})
                                </button>
                                {annListOpen && (
                                    <div className="absolute left-0 top-full mt-1 z-50 w-72 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl p-1"
                                         onMouseLeave={() => setSelectedId(null)}>
                                        <p className="text-[10px] text-slate-400 px-2 py-1">Passa col mouse per evidenziare, ✕ per eliminare. Selezionata + tasto Canc = elimina.</p>
                                        {annotations.map((a, i) => {
                                            const sec = (a as any).section as string | undefined;
                                            const isSel = selectedId === a.id;
                                            return (
                                                <div key={a.id} onMouseEnter={() => setSelectedId(a.id)}
                                                     className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${isSel ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                                                    {a.type === 'arrow'
                                                        ? <span className="w-3 h-3 rounded-full border border-slate-300 shrink-0" style={{ backgroundColor: (a as ArrowAnnotation).color }}/>
                                                        : <span className="w-3 h-3 rounded-sm bg-slate-500 shrink-0"/>}
                                                    <span className="flex-1 truncate">
                                                        {i + 1}. {a.type === 'arrow' ? 'Freccia' : 'Censura'}
                                                        {sec ? ` · ${sec === 'first' ? 'Prime' : sec === 'last' ? 'Ultime' : 'Relazione'}` : ''}
                                                    </span>
                                                    <button onClick={() => { removeAnnotation(a.id); if (selectedId === a.id) setSelectedId(null); }}
                                                            className="px-1.5 py-0.5 text-[10px] font-bold text-red-600 hover:bg-red-50 rounded">✕ Elimina</button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <button onClick={() => { setAnnotations([]); setSelectedId(null); setAnnListOpen(false); }}
                                    className="flex items-center gap-1 px-2 py-1.5 text-xs text-red-500 hover:bg-red-50 rounded font-bold">
                                <X size={12}/> Elimina tutte ({annotations.length})
                            </button>
                        </>
                    )}
                </div>
                <div className="flex-1"/>
                {/*
                  Sui report di bonifica si mostrano sempre tutte le sezioni: `viewSection` resta
                  'all' e non c'è un comando per cambiarlo.
                */}
                {/* Zoom pagina */}
                <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-1.5 py-0.5">
                    <span className="text-[10px] font-bold text-gray-400 uppercase px-1">Zoom</span>
                    <button
                        onClick={() => setPageZoom(z => Math.max(50, z - 10))}
                        disabled={pageZoom <= 50}
                        className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:opacity-30 font-black text-base rounded transition-colors hover:bg-gray-200"
                        title="Riduci pagina"
                    >−</button>
                    <span className="text-[10px] font-black text-gray-700 w-8 text-center">{pageZoom}%</span>
                    <button
                        onClick={() => setPageZoom(z => Math.min(200, z + 10))}
                        disabled={pageZoom >= 200}
                        className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-900 disabled:opacity-30 font-black text-base rounded transition-colors hover:bg-gray-200"
                        title="Ingrandisci pagina"
                    >+</button>
                </div>
            </div>

            {/* Corpo principale */}
            <div className="flex flex-1 overflow-hidden">

                {/* Area documento */}
                <div ref={scrollContainerRef} className="flex-1 bg-gray-300 overflow-auto p-4 lg:p-8" style={{ position: 'relative' }}>
                    <div ref={a4WrapperRef} id="a4-wrapper" className="relative mx-auto bg-white" style={{ width: '210mm', zoom: pageZoom / 100 }}>

                        {/* Indicatore del punto di rilascio durante il drag di una foto */}
                        {dropIndicatorTop !== null && (
                            <div style={{ position: 'absolute', left: 0, right: 0, top: dropIndicatorTop, height: 0,
                                          borderTop: '4px solid #2563eb', boxShadow: '0 0 8px rgba(37,99,235,0.8)',
                                          zIndex: 50, pointerEvents: 'none', transition: 'top 0.06s linear' }}>
                                <div style={{ position: 'absolute', left: -6, top: -7, width: 14, height: 14, borderRadius: '50%', background: '#2563eb', border: '2px solid #fff' }}/>
                                <div style={{ position: 'absolute', right: 8, top: -12, background: '#2563eb', color: '#fff',
                                              fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>Rilascia qui</div>
                            </div>
                        )}

                        {/* 1. Flusso di testo */}
                        <div
                            id="editor-body"
                            ref={editorRef}
                            data-view-mode={viewSection}
                            onClick={handleEditorClick}
                            onDoubleClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (target.tagName === 'IMG') {
                                    (target as HTMLImageElement).style.width = '100%';
                                    addToast("Immagine a larghezza piena", "info");
                                    setTimeout(() => applyPageBreaksRef.current(), 30);
                                    markChanged();
                                }
                            }}
                            onDragOver={(e) => {
                                const internal = !!draggingInternalRef.current;
                                const dragging = internal || !!draggingItemRef.current
                                    || e.dataTransfer.types.includes('text/media-url')
                                    || e.dataTransfer.types.includes('text/internal-move');
                                if (dragging) {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = internal ? 'move' : 'copy';
                                    updateDropIndicator(e.clientY);
                                    const c = scrollContainerRef.current;
                                    if (c) {
                                        const rect = c.getBoundingClientRect(); const EDGE = 70;
                                        autoScrollDirRef.current = e.clientY < rect.top + EDGE ? -1
                                            : e.clientY > rect.bottom - EDGE ? 1 : 0;
                                        if (autoScrollDirRef.current !== 0) startAutoScroll();
                                    }
                                }
                            }}
                            onDragLeave={(e) => {
                                if (!editorRef.current?.contains(e.relatedTarget as Node)) { setDropIndicatorTop(null); stopAutoScroll(); }
                            }}
                            onDrop={async (e) => {
                                e.preventDefault();
                                setDropIndicatorTop(null); stopAutoScroll();

                                // Spostamento immagine già inserita, al bordo indicato
                                const box = draggingInternalRef.current;
                                if (box) {
                                    draggingInternalRef.current = null;
                                    const editorEl = editorRef.current!;
                                    const before = dropTargetElOf(e.clientY);
                                    if (before !== box) {
                                        box.parentNode?.removeChild(box);
                                        if (before && before.parentElement === editorEl) editorEl.insertBefore(box, before);
                                        else editorEl.appendChild(box);
                                        if (!box.nextSibling || (box.nextSibling as HTMLElement).tagName === 'DIV') {
                                            const p = document.createElement('p');
                                            p.innerHTML = '<br/>';
                                            editorEl.insertBefore(p, box.nextSibling);
                                        }
                                        setTimeout(() => applyPageBreaks(), 60);
                                    }
                                    pushHistory();
                                    markChanged();
                                    return;
                                }

                                // Inserimento dalla sidebar
                                const item = draggingItemRef.current;
                                draggingItemRef.current = null;
                                if (!item) return;

                                // Cursore al bordo indicato dalla linea blu
                                {
                                    const editorEl = editorRef.current!;
                                    const before = dropTargetElOf(e.clientY);
                                    const sel = window.getSelection();
                                    const range = document.createRange();
                                    if (before) range.setStartBefore(before);
                                    else if (editorEl.lastChild) range.setStartAfter(editorEl.lastChild);
                                    else range.selectNodeContents(editorEl);
                                    range.collapse(true);
                                    sel?.removeAllRanges();
                                    sel?.addRange(range);
                                }

                                // getHdUrl è asincrono
                                try {
                                    // Anteprima = versione compressa (thumbUrl); l'originale (item.hdUrl) resta per l'alta risoluzione.
                                    insertImage(item.thumbUrl || item.urlCompressed || item.hdUrl, item.hdUrl);
                                } catch {
                                    // Fallback: miniatura già disponibile
                                    const fallback = e.dataTransfer.getData('text/media-url');
                                    if (fallback) insertImage(fallback);
                                }
                            }}
                            className={`relative z-10 outline-none text-gray-900 leading-relaxed text-[11pt] ${getMarginClass()} [&_p]:my-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5`}
                            style={{
                                fontFamily: "'Tinos', 'Times New Roman', Times, serif",
                                // Altezza pari a un numero intero di pagine: l'ultima si vede completa, come in Word.
                                minHeight: `${Math.max(1, totalPages) * PAGE_H_PX}px`,
                                // Sfondo trasparente: il bianco lo dà l'#a4-wrapper, così i separatori (z-index
                                // basso) restano dietro al testo.
                                backgroundColor: 'transparent',
                            }}
                            contentEditable={activeTool === 'none' && !dragState}
                            suppressContentEditableWarning
                            onInput={handleEditorInput}
                            onPaste={(e) => {
                                // Come in ReportEditorView: il paste può avvolgere tutto in un unico blocco che
                                // applyPageBreaks salta. Normalizziamo in un <p> per paragrafo.
                                e.preventDefault();
                                const cd = e.clipboardData;
                                const text = cd?.getData('text/plain') || '';
                                if (!text) return;
                                const esc = (s: string) => s
                                    .replace(/&/g, '&amp;')
                                    .replace(/</g, '&lt;')
                                    .replace(/>/g, '&gt;')
                                    .replace(/"/g, '&quot;')
                                    .replace(/'/g, '&#39;');
                                const html = text
                                    .replace(/\r\n?/g, '\n')
                                    .split(/\n{2,}/)
                                    .map(para => {
                                        const inner = para
                                            .split('\n')
                                            .map(esc)
                                            .join('<br>');
                                        return `<p>${inner || '<br>'}</p>`;
                                    })
                                    .join('');
                                document.execCommand('insertHTML', false, html);
                                handleEditorInput();
                            }}
                            onKeyDown={(e) => {
                                if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
                                    e.preventDefault();
                                    undo();
                                } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                                    e.preventDefault();
                                    redo();
                                }
                            }}
                        />

                        {/* 2. Separatori di pagina: linea sul bordo + etichette nel margine */}
                        {Array.from({ length: totalPages - 1 }).map((_, i) => (
                            <React.Fragment key={i}>
                                {/* Stacco pagina evidente: ombra sotto la pagina che finisce, linea e ombra sopra la successiva. */}
                                <div data-page-sep="" style={{
                                    position: 'absolute', left: 0, right: 0,
                                    top: `${(i + 1) * PAGE_H_PX - 16}px`,
                                    height: '16px',
                                    background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.16))',
                                    pointerEvents: 'none', zIndex: 4,
                                }}/>
                                {/* Linea di separazione */}
                                <div data-page-sep="" style={{
                                    position: 'absolute', left: 0, right: 0,
                                    top: `${(i + 1) * PAGE_H_PX}px`,
                                    height: '4px',
                                    background: '#d1d5db',
                                    boxShadow: '#d1d5db 0 0 0 5px, rgba(0,0,0,0.25) 0 0 0',
                                    pointerEvents: 'none', zIndex: 5,
                                }}/>
                                {/* Ombra inizio pagina successiva */}
                                <div data-page-sep="" style={{
                                    position: 'absolute', left: 0, right: 0,
                                    top: `${(i + 1) * PAGE_H_PX + 4}px`,
                                    height: '16px',
                                    background: 'linear-gradient(to top, transparent, rgba(0,0,0,0.12))',
                                    pointerEvents: 'none', zIndex: 4,
                                }}/>
                                {/* Badge "Pag X" nel margine sinistro, fuori dal testo */}
                                <div data-page-sep="" style={{
                                    position: 'absolute',
                                    right: 'calc(100% + 6px)',
                                    top: `${(i + 1) * PAGE_H_PX - 20}px`,
                                    pointerEvents: 'none', zIndex: 22,
                                    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px',
                                }}>
                                    <span style={{
                                        background: '#475569', color: 'white',
                                        padding: '2px 7px', borderRadius: '3px',
                                        fontSize: '8px', fontWeight: 'bold',
                                        whiteSpace: 'nowrap', userSelect: 'none', letterSpacing: '0.06em',
                                    }}>Pag {i + 1} ↑</span>
                                    <span style={{
                                        background: '#6366f1', color: 'white',
                                        padding: '2px 7px', borderRadius: '3px',
                                        fontSize: '8px', fontWeight: 'bold',
                                        whiteSpace: 'nowrap', userSelect: 'none', letterSpacing: '0.06em',
                                    }}>↓ Pag {i + 2}</span>
                                </div>
                            </React.Fragment>
                        ))}

                        {/* Layer annotazioni */}
                        <div
                            style={{
                                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                pointerEvents: activeTool !== 'none' ? 'all' : 'none',
                                cursor: activeTool === 'arrow' ? 'crosshair' : activeTool === 'blur' ? 'cell' : 'default',
                                zIndex: 10,
                            }}
                            onMouseDown={onOverlayMouseDown}
                            onMouseMove={onOverlayMouseMove}
                            onMouseUp={onOverlayMouseUp}
                            onClick={() => setSelectedId(null)}
                        >
                            <svg ref={annotationSvgRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                                <defs>
                                    {['#e63946','#2ec4b6','#ff9f1c','#1982c4','#ffffff'].map(c => (
                                        <marker key={c} id={`arrowhead-${c.replace('#','')}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                            <polygon points="0 0, 10 3.5, 0 7" fill={c}/>
                                        </marker>
                                    ))}
                                    <marker id="arrowhead-draft" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                        <polygon points="0 0, 10 3.5, 0 7" fill={arrowColor} opacity="0.8"/>
                                    </marker>
                                </defs>

                                {annotations.filter(a => a.type === 'arrow').map(a => {
                                    const arr = a as ArrowAnnotation;
                                    return (
                                        <g key={`line-${arr.id}`} style={{ pointerEvents: 'none' }}>
                                            <line x1={arr.x1+1} y1={arr.y1+1} x2={arr.x2+1} y2={arr.y2+1} stroke="rgba(0,0,0,0.3)" strokeWidth="4" strokeLinecap="round"/>
                                            <line x1={arr.x1} y1={arr.y1} x2={arr.x2} y2={arr.y2} stroke={arr.color} strokeWidth="3" strokeLinecap="round" markerEnd={`url(#arrowhead-${arr.color.replace('#','')})`}/>
                                        </g>
                                    );
                                })}

                                {annotations.filter(a => a.type === 'arrow').map(a => {
                                    const arr = a as ArrowAnnotation;
                                    const isSelected = selectedId === arr.id;
                                    const startArrowDrag = (mode: 'arrow-p1'|'arrow-p2'|'arrow-body', e: React.MouseEvent) => {
                                        e.stopPropagation(); e.preventDefault();
                                        setSelectedId(arr.id);
                                        const { x: startX, y: startY } = clientToDoc(e.clientX, e.clientY);
                                        setDragState({ annId: arr.id, mode, startX, startY, origAnn: { ...arr } });
                                    };
                                    return (
                                        <g key={`handle-${arr.id}`} style={{ pointerEvents: 'all' }}>
                                            <line x1={arr.x1} y1={arr.y1} x2={arr.x2} y2={arr.y2} stroke="transparent" strokeWidth="14" style={{ cursor: 'move' }}
                                                  onMouseDown={e => startArrowDrag('arrow-body', e)} onClick={e => { e.stopPropagation(); setSelectedId(arr.id); }}/>
                                            {isSelected && (
                                                <>
                                                    <circle cx={arr.x1} cy={arr.y1} r={7} fill="white" stroke="#3b82f6" strokeWidth="2" style={{ cursor: 'crosshair' }} onMouseDown={e => startArrowDrag('arrow-p1', e)}/>
                                                    <circle cx={arr.x2} cy={arr.y2} r={7} fill="#3b82f6" stroke="white" strokeWidth="2" style={{ cursor: 'crosshair' }} onMouseDown={e => startArrowDrag('arrow-p2', e)}/>
                                                    <foreignObject x={(arr.x1+arr.x2)/2 - 28} y={Math.min(arr.y1, arr.y2) - 30} width="56" height="22">
                                                        <button onClick={e => { e.stopPropagation(); removeAnnotation(arr.id); setSelectedId(null); }}
                                                                style={{ width: '100%', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer', padding: '2px 0', fontWeight: 'bold' }}>
                                                            ✕ Elimina
                                                        </button>
                                                    </foreignObject>
                                                </>
                                            )}
                                        </g>
                                    );
                                })}

                                {renderDraftAnnotation()}
                            </svg>

                            {annotations.filter(a => a.type === 'blur').map(a => {
                                const bl = a as BlurAnnotation;
                                const isSelected = selectedId === bl.id;
                                const startBlurDrag = (mode: NonNullable<typeof dragState>['mode'], e: React.MouseEvent) => {
                                    e.stopPropagation(); e.preventDefault();
                                    setSelectedId(bl.id);
                                    const { x: startX, y: startY } = clientToDoc(e.clientX, e.clientY);
                                    setDragState({ annId: bl.id, mode, startX, startY, origAnn: { ...bl } });
                                };
                                return (
                                    <div key={bl.id} data-blur-box=""
                                         style={{ position: 'absolute', left: bl.x, top: bl.y, width: bl.w, height: bl.h, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', background: 'rgba(200,200,200,0.15)', border: isSelected ? '2px solid #3b82f6' : '1.5px solid rgba(0,0,0,0.2)', borderRadius: 2, overflow: 'hidden', pointerEvents: 'all', cursor: 'move', zIndex: 8 }}
                                         onMouseDown={e => startBlurDrag('move-blur', e)}
                                         onClick={e => { e.stopPropagation(); setSelectedId(bl.id); }}>
                                        {isSelected && (
                                            <>
                                                {(['tl','tr','bl','br'] as const).map(corner => (
                                                    <div key={corner} onMouseDown={e => startBlurDrag(`resize-${corner}` as any, e)}
                                                         style={{ position: 'absolute', width: 10, height: 10, background: '#3b82f6', border: '1.5px solid white', borderRadius: 2, zIndex: 20, cursor: (corner === 'tl' || corner === 'br') ? 'nw-resize' : 'ne-resize', top: corner.startsWith('t') ? -5 : undefined, bottom: corner.startsWith('b') ? -5 : undefined, left: corner.endsWith('l') ? -5 : undefined, right: corner.endsWith('r') ? -5 : undefined }}/>
                                                ))}
                                                <button onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); removeAnnotation(bl.id); setSelectedId(null); }}
                                                        style={{ position: 'absolute', top: -26, right: 0, background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', zIndex: 20, whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                                                    ✕ Elimina
                                                </button>
                                            </>
                                        )}
                                    </div>
                                );
                            })}

                            {activeTool !== 'none' && (
                                <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 100, pointerEvents: 'none' }}>
                                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-2xl text-sm font-bold text-white ${activeTool === 'arrow' ? 'bg-red-500' : 'bg-indigo-500'}`}>
                                        {activeTool === 'arrow' ? <ArrowUpRight size={16}/> : <Layers size={16}/>}
                                        {activeTool === 'arrow' ? 'Trascina per disegnare una freccia' : 'Trascina per censurare un\'area'}
                                        <span className="opacity-70 text-xs">· ESC per uscire</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Anteprima PDF live affiancata (render del server) */}
                {livePreviewOpen && (
                    <div className="hidden lg:flex flex-col bg-slate-100 border-l border-slate-200 shrink-0 relative" style={{ width: livePreviewWidth, minWidth: 360 }}>
                        <div
                            onMouseDown={(e) => {
                                previewResizeRef.current = { x: e.clientX, w: livePreviewWidth };
                                setPreviewResizing(true);
                                document.body.style.cursor = 'col-resize';
                                document.body.style.userSelect = 'none';
                            }}
                            title="Trascina per ridimensionare l'anteprima"
                            className="absolute left-0 top-0 bottom-0 w-2 -ml-1 cursor-col-resize z-30 hover:bg-blue-400/40"
                            style={{ background: previewResizing ? 'rgba(96,165,250,0.5)' : undefined }}
                        />
                        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white shrink-0 gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <Eye size={15} className="text-blue-600 shrink-0"/>
                                <span className="text-xs font-bold text-slate-800 truncate">Anteprima PDF · identica al download</span>
                                {livePreviewLoading
                                    ? <span className="text-[10px] text-blue-600 flex items-center gap-1 shrink-0"><RefreshCw size={11} className="animate-spin"/> aggiorno…</span>
                                    : livePreviewStale
                                        ? <span className="text-[10px] text-amber-600 shrink-0">• da aggiornare</span>
                                        : livePreviewBlob ? <span className="text-[10px] text-emerald-600 shrink-0">• aggiornata</span> : null}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <label className="text-[10px] text-slate-500 flex items-center gap-1 cursor-pointer select-none px-1" title="Aggiorna da solo dopo una pausa di scrittura">
                                    <input type="checkbox" checked={livePreviewAuto} onChange={e => setLivePreviewAuto(e.target.checked)}/> auto
                                </label>
                                {/* Zoom anteprima live */}
                                <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5" title="Zoom anteprima">
                                    <button onClick={() => setLivePreviewZoom(z => Math.max(50, z - 25))} className="w-5 h-5 flex items-center justify-center text-slate-600 hover:bg-white rounded text-sm font-bold" title="Riduci">−</button>
                                    <button onClick={() => setLivePreviewZoom(100)} className="text-[10px] font-bold text-slate-600 w-9 text-center tabular-nums hover:text-blue-600" title="Ripristina 100%">{livePreviewZoom}%</button>
                                    <button onClick={() => setLivePreviewZoom(z => Math.min(300, z + 25))} className="w-5 h-5 flex items-center justify-center text-slate-600 hover:bg-white rounded text-sm font-bold" title="Ingrandisci">+</button>
                                </div>
                                <button onClick={() => refreshLivePreviewRef.current()} disabled={livePreviewLoading} title="Aggiorna ora"
                                        className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50">
                                    <RefreshCw size={14} className={livePreviewLoading ? 'animate-spin' : ''}/>
                                </button>
                                <button onClick={() => setLivePreviewOpen(false)} title="Chiudi anteprima"
                                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg">
                                    <X size={16}/>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 relative">
                            {previewResizing && <div className="absolute inset-0 z-20" style={{ cursor: 'col-resize' }}/>}
                            {livePreviewBlob
                                ? <PdfLivePreview blob={livePreviewBlob} zoom={livePreviewZoom} className="absolute inset-0 overflow-auto bg-slate-200 p-3"/>
                                : <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm px-4 text-center">
                                    {livePreviewLoading ? 'Genero l’anteprima…' : 'Anteprima non ancora generata. Premi Aggiorna.'}
                                  </div>}
                        </div>
                    </div>
                )}

                {/* Overlay del drawer su tablet quando è aperto */}
                {mediaDrawerOpen && (
                    <div
                        onClick={() => setMediaDrawerOpen(false)}
                        className="fixed inset-0 bg-black/40 z-20 lg:hidden"
                        aria-hidden="true"
                    />
                )}

                {/* Sidebar */}
                {/* Da lg in su sidebar ridimensionabile; sotto lg drawer che entra da destra. */}
                <div
                    className={`bg-white border-l border-gray-200 flex flex-col shadow-xl shrink-0 transition-transform duration-300 lg:relative lg:translate-x-0 lg:z-10 fixed inset-y-0 right-0 z-30 ${mediaDrawerOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`}
                    style={{ width: typeof window !== 'undefined' && window.innerWidth < 1024 ? 'min(420px, 90vw)' : sidebarWidth }}
                >
                    {/* Maniglia di ridimensionamento, solo desktop */}
                    <div
                        onMouseDown={onSidebarDragStart}
                        className="absolute left-0 top-0 bottom-0 w-1.5 -translate-x-full cursor-col-resize z-20 group hidden lg:block"
                    >
                        <div className="absolute inset-y-0 left-0 w-full bg-transparent group-hover:bg-blue-400/40 transition-colors rounded-l"/>
                    </div>

                    {/* Chiudi drawer su tablet */}
                    <button
                        onClick={() => setMediaDrawerOpen(false)}
                        className="lg:hidden absolute top-2 right-2 z-30 p-1.5 rounded-full bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow"
                        aria-label="Chiudi pannello"
                    >
                        <X size={16}/>
                    </button>

                    {/* Selettore tab */}
                    <div className="bg-gray-50 p-2 border-b border-gray-200">
                        <div className="flex gap-1 mb-2 items-center">
                            <button onClick={() => setSidebarTab('media')} className={`flex-1 py-1.5 text-xs font-bold rounded flex items-center justify-center gap-1 ${sidebarTab === 'media' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:bg-gray-200'}`}><ImageIcon size={12}/> Media</button>
                            <button onClick={() => setSidebarTab('report')}  className={`flex-1 py-1.5 text-xs font-bold rounded flex items-center justify-center gap-1 ${sidebarTab === 'report' ? 'bg-white shadow text-emerald-600' : 'text-gray-500 hover:bg-gray-200'}`}>
                                <FileText size={12}/>
                                Report
                                {dailyReports.length > 0 && (
                                    <span className="ml-1 bg-emerald-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none">
                                        {dailyReports.length}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Filtri: Tutte · Doc. Ufficio · date */}
                        <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between px-1">
                                <span className="text-[10px] font-bold text-gray-400 uppercase">Filtra per</span>
                                {/* Controlli dimensione griglia */}
                                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-1.5 py-0.5">
                                    <button
                                        onClick={() => setSidebarGridCols(c => Math.max(1, c - 1))}
                                        disabled={sidebarGridCols <= 1}
                                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30 font-black text-sm rounded transition-colors"
                                        title="Rimpicciolisci"
                                    >−</button>
                                    <span className="text-[10px] font-black text-gray-600 w-3 text-center">{sidebarGridCols}</span>
                                    <button
                                        onClick={() => setSidebarGridCols(c => Math.min(9, c + 1))}
                                        disabled={sidebarGridCols >= 9}
                                        className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30 font-black text-sm rounded transition-colors"
                                        title="Ingrandisci"
                                    >+</button>
                                </div>
                            </div>

                            {/* Riga unica: Tutto · Doc. Ufficio · date di campo */}
                            {/* Chip data orizzontali con barra di scorrimento visibile. */}
                            <div className="flex gap-1.5 overflow-x-auto pb-1">
                                <button
                                    onClick={() => setSelectedDate("")}
                                    className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${!selectedDate ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-500 border-gray-200 hover:border-slate-400'}`}
                                >TUTTO</button>
                                {ufficioMedia.length > 0 && (
                                    <button
                                        onClick={() => setSelectedDate(selectedDate === '__ufficio__' ? '' : '__ufficio__')}
                                        className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all flex items-center gap-1 ${selectedDate === '__ufficio__' ? 'bg-slate-700 text-white border-slate-700 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-slate-400'}`}
                                    ><FileText size={9}/> DOC. UFFICIO</button>
                                )}
                                {availableDates.map(dateStr => {
                                    const d = new Date(dateStr);
                                    const isActive = selectedDate === dateStr;
                                    return (
                                        <button key={dateStr} onClick={() => setSelectedDate(isActive ? "" : dateStr)}
                                                className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all flex flex-col items-center min-w-[58px] ${isActive ? 'bg-slate-800 text-white border-slate-800 shadow-md' : 'bg-white text-gray-600 border-gray-200 hover:border-slate-400'}`}>
                                            <span className="uppercase opacity-80 leading-tight">{d.toLocaleDateString('it-IT', { weekday: 'short' })}</span>
                                            <span className="leading-tight">{d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: '2-digit' })}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Soggetti (solo se più di uno e non in Doc. Ufficio) */}
                            {availableSoggetti.length > 1 && selectedDate !== '__ufficio__' && (
                                <div className="flex gap-1.5 overflow-x-auto pb-1 no-scrollbar flex-wrap">
                                    <button
                                        onClick={() => setSelectedSoggetto('')}
                                        className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${!selectedSoggetto ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300'}`}
                                    >TUTTI</button>
                                    {availableSoggetti.map(s => (
                                        <button
                                            key={s}
                                            onClick={() => setSelectedSoggetto(selectedSoggetto === s ? '' : s)}
                                            className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-all ${selectedSoggetto === s ? 'bg-orange-500 text-white border-orange-500 shadow-sm' : 'bg-white text-gray-500 border-gray-200 hover:border-orange-300'}`}
                                        >{s}</button>
                                    ))}
                                </div>
                            )}

                            {/* Azzera filtri */}
                            {(selectedDate || selectedSoggetto) && (
                                <button onClick={() => { setSelectedDate(""); setSelectedSoggetto(""); }}
                                        className="self-start text-[9px] font-bold text-red-400 hover:text-red-600 flex items-center gap-1 px-1">
                                    <X size={10}/> Rimuovi filtri
                                </button>
                            )}
                        </div>
                    </div>{/* Fine selettore tab */}

                    {/* Contenuto sidebar */}
                    <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-6">

                        {/* Tab media */}
                        {/* Tab media */}
                        {sidebarTab === 'media' && (
                            <div className="space-y-8">
                                {Object.keys(organizedMedia).length === 0 && ufficioMedia.length === 0 && (
                                    <div className="text-center py-10 opacity-40">
                                        <ImageIcon size={40} className="mx-auto mb-2"/>
                                        <p className="text-xs font-bold uppercase">Nessun file trovato</p>
                                        <p className="text-[10px] text-gray-400 mt-1">Carica file o cambia filtri</p>
                                    </div>
                                )}

                                {/* Documentazione ufficio: visibile in Tutto e Doc. Ufficio */}
                                {ufficioMedia.length > 0 && (selectedDate === '' || selectedDate === '__ufficio__') && (
                                    <div className="space-y-3">
                                        <div className="sticky top-0 bg-white/90 backdrop-blur-sm z-10 py-2 border-b border-slate-100">
                                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                                                <FileText size={12} className="text-slate-400"/> Documentazione Ufficio
                                                <span className="text-[9px] text-slate-400 font-mono ml-auto">{ufficioMedia.filter(f => f.type === 'image').length} img · {ufficioMedia.filter(f => f.type === 'video').length} video</span>
                                            </h3>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sidebarGridCols}, minmax(0, 1fr))`, gap: sidebarGridCols >= 6 ? '3px' : '6px' }}>
                                            {ufficioMedia.filter(f => f.type === 'image').map((f: any) => (
                                                <SidebarImageCard
                                                    key={f.id}
                                                    item={f}
                                                    onPreview={() => handleOpenPreview(f)}
                                                    onInsert={async () => { insertImage(f.thumbUrl || f.urlCompressed || f.hdUrl, f.hdUrl); }}
                                                    onEdit={async () => { const hdUrl = await getHdUrl(f); startEditing(hdUrl); }}
                                                    onThumbError={async (imgEl) => { const t = await refreshThumbUrl(f); if (t) imgEl.src = t; return !!t; }}
                                                    onDragStart={() => { draggingItemRef.current = f; }}
                                                />
                                            ))}
                                        </div>
                                        {ufficioMedia.filter(f => f.type === 'video').map((f: any) => {
                                            const videoId = String(f.id);
                                            const frames = capturedFrames[videoId] || [];
                                            return (
                                                <div key={f.id} className="bg-slate-50 border border-slate-200 p-2 rounded-lg flex items-center gap-3 group">
                                                    <div className="relative w-20 aspect-video bg-slate-800 rounded overflow-hidden cursor-pointer shrink-0"
                                                         onClick={async () => { const hdUrl = await getHdUrl(f); setCurrentVideoUrl(hdUrl); setCurrentVideoId(videoId); setVideoModalOpen(true); }}>
                                                        <video src={f.thumbUrl || f.hdUrl} preload="metadata" muted className="absolute inset-0 w-full h-full object-cover opacity-70" onLoadedMetadata={e => { (e.target as HTMLVideoElement).currentTime = 0.1; }}/>
                                                        <PlayCircle className="absolute inset-0 m-auto text-white drop-shadow group-hover:scale-110 transition-transform" size={22}/>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[9px] font-bold text-slate-600 truncate mb-1">{f.name}</p>
                                                        <p className="text-[8px] text-slate-400 mb-1.5">{frames.length > 0 ? `${frames.length} frame acquisit${frames.length === 1 ? 'o' : 'i'}` : 'Nessun frame'}</p>
                                                        <button onClick={async () => { const hdUrl = await getHdUrl(f); setCurrentVideoUrl(hdUrl); setCurrentVideoId(videoId); setVideoModalOpen(true); }}
                                                                className="text-[8px] font-black text-indigo-600 uppercase tracking-wider bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded hover:bg-indigo-100 transition-colors">
                                                            Apri / Cattura Frame
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Media di campo raggruppati per data, nascosti in Doc. Ufficio */}
                                {selectedDate !== '__ufficio__' && Object.entries(organizedMedia).map(([date, categoriesGroup]) => (
                                    <div key={date} className="space-y-4">
                                        <div className="sticky top-0 bg-white/90 backdrop-blur-sm z-10 py-2 border-b border-slate-100">
                                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-2">
                                                <Calendar size={12} className="text-blue-500"/> {date}
                                            </h3>
                                        </div>

                                        {/* Cast esplicito per i tipi */}
                                        {Object.entries(categoriesGroup as Record<string, OrganizedMediaGroup>).map(([category, media]) => (
                                            <div key={category} className="pl-3 border-l-2 border-slate-200">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Tag size={12} className="text-indigo-500"/>
                                                    <h4 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{category}</h4>
                                                    <span className="text-[9px] text-slate-400 font-mono ml-auto">
                                {media.images.length > 0 && `${media.images.length} img`}
                                                        {media.images.length > 0 && media.videos.length > 0 && ' · '}
                                                        {media.videos.length > 0 && `${media.videos.length} video`}
                            </span>
                                                </div>

                                                {media.images.length > 0 && (
                                                    <div className="mb-4" style={{ display: 'grid', gridTemplateColumns: `repeat(${sidebarGridCols}, minmax(0, 1fr))`, gap: sidebarGridCols >= 6 ? '3px' : '6px' }}>
                                                        {media.images.map((f: any) => (
                                                            <div key={f.id} className="flex flex-col gap-0.5">
                                                                <SidebarImageCard
                                                                    item={f}
                                                                    onPreview={() => handleOpenPreview(f)}
                                                                    onInsert={async () => {
                                                                        insertImage(f.thumbUrl || f.urlCompressed || f.hdUrl, f.hdUrl);
                                                                    }}
                                                                    onEdit={async () => {
                                                                        const hdUrl = await getHdUrl(f);
                                                                        startEditing(hdUrl);
                                                                    }}
                                                                    onThumbError={async (imgEl) => {
                                                                        const freshThumb = await refreshThumbUrl(f);
                                                                        if (freshThumb) imgEl.src = freshThumb;
                                                                        return !!freshThumb;
                                                                    }}
                                                                    onDragStart={() => { draggingItemRef.current = f; }}
                                                                />
                                                                {sidebarGridCols <= 5 && f.createdAt && (
                                                                    <p className="text-[8px] text-gray-400 font-bold text-center leading-none truncate">
                                                                        {new Date(f.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {media.videos.map((f: any) => {
                                                    const videoId = String(f.id);
                                                    const frames = capturedFrames[videoId] || [];
                                                    return (
                                                        <div key={f.id} className="mb-3">
                                                            {/* Riga video */}
                                                            <div className="bg-slate-50 border border-slate-200 p-2 rounded-lg flex items-center gap-3 group">
                                                                <div
                                                                    className="relative w-20 aspect-video bg-slate-800 rounded overflow-hidden cursor-pointer shrink-0"
                                                                    onClick={async () => {
                                                                        const hdUrl = await getHdUrl(f);
                                                                        setCurrentVideoUrl(hdUrl);
                                                                        setCurrentVideoId(videoId);
                                                                        setVideoModalOpen(true);
                                                                    }}
                                                                >
                                                                    <video
                                                                        src={f.thumbUrl || f.hdUrl}
                                                                        preload="metadata"
                                                                        muted
                                                                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                                                                        onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).currentTime = 0.1; }}
                                                                    />
                                                                    <PlayCircle className="absolute inset-0 m-auto text-white drop-shadow group-hover:scale-110 transition-transform" size={22}/>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[9px] font-bold text-slate-600 truncate mb-1">{f.name}</p>
                                                                    <p className="text-[8px] text-slate-400 mb-1.5">
                                                                        {frames.length > 0 ? `${frames.length} frame acquisit${frames.length === 1 ? 'o' : 'i'}` : 'Nessun frame'}
                                                                    </p>
                                                                    <button
                                                                        onClick={async () => {
                                                                            const hdUrl = await getHdUrl(f);
                                                                            setCurrentVideoUrl(hdUrl);
                                                                            setCurrentVideoId(videoId);
                                                                            setVideoModalOpen(true);
                                                                        }}
                                                                        className="text-[8px] font-black text-indigo-600 uppercase tracking-wider bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded hover:bg-indigo-100 transition-colors"
                                                                    >
                                                                        Apri / Cattura Frame
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Frame acquisiti, in griglia come le immagini */}
                                                            {frames.length > 0 && (
                                                                <div className="ml-3 border-l-2 border-indigo-200 pl-2 mt-1.5">
                                                                    <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1.5">Frame acquisiti</p>
                                                                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${sidebarGridCols}, minmax(0, 1fr))`, gap: sidebarGridCols >= 6 ? '3px' : '6px' }}>
                                                                        {frames.map((frame, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="flex flex-col gap-1 group/frame"
                                                                                draggable={!frame.pending}
                                                                                onDragStart={(e) => {
                                                                                    const insertUrl = frame.originalUrl || frame.url;
                                                                                    draggingItemRef.current = { hdUrl: insertUrl, url: insertUrl };
                                                                                    e.dataTransfer.setData('text/media-url', insertUrl);
                                                                                    e.dataTransfer.effectAllowed = 'copy';
                                                                                }}
                                                                                onDragEnd={() => { draggingItemRef.current = null; }}
                                                                            >
                                                                                <div className="relative">
                                                                                    <img
                                                                                        src={frame.url}
                                                                                        alt={`Frame ${idx + 1}`}
                                                                                        className={`w-full aspect-video object-cover rounded ${frame.pending ? 'opacity-60 cursor-default' : 'cursor-grab active:cursor-grabbing'}`}
                                                                                        onClick={() => { if (!frame.pending) startEditing(frame.url); }}
                                                                                    />
                                                                                    {frame.pending && (
                                                                                        <Loader2 className="absolute inset-0 m-auto text-white animate-spin" size={14}/>
                                                                                    )}
                                                                                </div>
                                                                                <button
                                                                                    disabled={frame.pending}
                                                                                    onClick={() => insertImage(frame.originalUrl || frame.url)}
                                                                                    className="text-[8px] font-black text-emerald-600 uppercase bg-emerald-50 border border-emerald-200 px-1 py-0.5 rounded hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed truncate"
                                                                                >
                                                                                    {frame.pending ? '…' : '+ Inserisci'}
                                                                                </button>
                                                                                <button
                                                                                    onClick={async () => {
                                                                                        if (!window.confirm(`Sei sicuro di voler eliminare il Frame ${idx + 1}?`)) return;
                                                                                        const target = capturedFrames[videoId]?.[idx];
                                                                                        if (target?.fileId) {
                                                                                            try { await FilesService.delete(target.fileId); } catch { /* Ignorato */ }
                                                                                        }
                                                                                        setCapturedFrames(prev => {
                                                                                            const updated = [...(prev[videoId] || [])];
                                                                                            updated.splice(idx, 1);
                                                                                            return { ...prev, [videoId]: updated };
                                                                                        });
                                                                                    }}
                                                                                    className="text-[8px] text-red-400 hover:text-red-600 uppercase font-bold opacity-0 group-hover/frame:opacity-100 transition-opacity truncate"
                                                                                >
                                                                                    Elimina
                                                                                </button>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Tab report collaboratori */}
                        {sidebarTab === 'report' && (
                            <div className="space-y-4">
                                {dailyReports.length === 0 && (
                                    <div className="text-center mt-10">
                                        <FileText size={32} className="mx-auto mb-2 text-gray-300"/>
                                        <p className="text-gray-400 text-xs font-semibold">Nessun report trovato.</p>
                                        <p className="text-[10px] text-gray-300 mt-1">
                                            {selectedDate ? 'Nessun report per la data selezionata.' : 'I report inviati dai collaboratori appariranno qui.'}
                                        </p>
                                    </div>
                                )}

                                {dailyReports.map((report) => (
                                    <div key={report.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden group hover:shadow-md transition-shadow">

                                        {/* Header report */}
                                        <div className="bg-slate-50 border-b border-slate-100 px-3 py-2 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                                                    <User size={12} className="text-emerald-700"/>
                                                </div>
                                                <div>
                                                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-wide block leading-tight">{report.autore}</span>
                                                    <span className="text-[9px] text-slate-400 font-mono">{report.data}</span>
                                                </div>
                                            </div>
                                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                                                report.stato === 'APPROVATO' ? 'bg-emerald-100 text-emerald-700' :
                                                    report.stato === 'RIFIUTATO' ? 'bg-red-100 text-red-700' :
                                                        'bg-blue-100 text-blue-700'
                                            }`}>{report.stato}</span>
                                        </div>

                                        {/* Metriche km/ore */}
                                        {(report.oreGps != null || report.oreExtra != null || report.km != null) && (
                                            <div className="flex gap-3 px-3 py-2 bg-slate-50/50 border-b border-slate-100 text-[10px] font-bold text-slate-500">
                                                {report.oreGps != null && (
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={10} className="text-emerald-500"/> {report.oreGps}h
                                                    </span>
                                                )}
                                                {report.oreExtra != null && report.oreExtra > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={10} className="text-blue-500"/> +{report.oreExtra}h extra
                                                    </span>
                                                )}
                                                {report.km != null && report.km > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <MapPin size={10} className="text-orange-500"/> {report.km} km
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Corpo testuale */}
                                        <div className="px-3 py-2.5">
                                            {report.testo ? (
                                                <p className="text-[11px] text-slate-700 leading-relaxed line-clamp-5 italic">
                                                    "{report.testo}"
                                                </p>
                                            ) : (
                                                <p className="text-[10px] text-slate-400 italic">Nessun testo nel report.</p>
                                            )}
                                        </div>

                                        {/* Spese allegate */}
                                        {report.spese?.length > 0 && (
                                            <div className="px-3 pb-2">
                                                <div className="bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5">
                                                    <span className="text-[9px] font-black text-amber-700 uppercase tracking-wider">
                                                        {report.spese.length} spesa/e: €{report.spese.reduce((acc: number, s: any) => acc + (s.importo || 0), 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Azioni */}
                                        {report.testo && (
                                            <div className="px-3 pb-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                {/* Ottimizza testo */}
                                                <button
                                                    onClick={() => openAiModal(report.testo)}
                                                    className="flex items-center gap-1 text-[10px] bg-indigo-50 text-indigo-700 px-2 py-1.5 rounded-lg hover:bg-indigo-100 font-bold flex-1 justify-center"
                                                >
                                                    <Sparkles size={10}/> AI
                                                </button>

                                                {/* Inserisci come testo */}
                                                <button
                                                    onClick={() => insertHtml(textToParagraphs(report.testo) + '<br/>')}
                                                    className="flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 px-2 py-1.5 rounded-lg hover:bg-emerald-100 font-bold flex-1 justify-center"
                                                >
                                                    <PlusCircle size={10}/> Inserisci
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Toolbar immagine flottante (position fixed, aggiornata allo scroll) */}
            {selectedEditorImg && imgToolbarRect && (
                <div
                    style={{
                        position: 'fixed',
                        // Centrata sopra l'immagine, con un margine in alto
                        top: Math.max(72, imgToolbarRect.top - 48),
                        left: imgToolbarRect.left + imgToolbarRect.width / 2,
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        pointerEvents: 'all',
                    }}
                >
                    <div className="bg-white shadow-2xl border border-gray-200 rounded-xl px-2 py-1.5 flex items-center gap-0.5"
                         style={{ backdropFilter: 'blur(8px)', boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)' }}>

                        {/* Allineamento */}
                        <span className="text-[9px] font-bold text-gray-300 uppercase px-1">Posizione</span>
                        <ImgBtn title="Sinistra" onClick={() => { selectedEditorImg.style.float = 'left'; selectedEditorImg.style.marginRight = '12px'; selectedEditorImg.style.marginLeft = '0'; markChanged(); }} icon={<AlignLeft size={13}/>}/>
                        <ImgBtn title="Centro"   onClick={() => { selectedEditorImg.style.float = 'none'; selectedEditorImg.style.display = 'block'; selectedEditorImg.style.margin = '0 auto'; markChanged(); }} icon={<AlignCenter size={13}/>}/>
                        <ImgBtn title="Destra"   onClick={() => { selectedEditorImg.style.float = 'right'; selectedEditorImg.style.marginLeft = '12px'; selectedEditorImg.style.marginRight = '0'; markChanged(); }} icon={<AlignRight size={13}/>}/>

                        <div className="w-px h-4 bg-gray-200 mx-1"/>

                        {/* Dimensione */}
                        <span className="text-[9px] font-bold text-gray-300 uppercase px-1">Dim.</span>
                        <ImgBtn title="Riduci (−10%)"  onClick={() => { const w = selectedEditorImg.clientWidth; selectedEditorImg.style.maxWidth = 'none'; selectedEditorImg.style.maxHeight = 'none'; selectedEditorImg.style.height = 'auto'; selectedEditorImg.style.width = Math.max(40, w * 0.9) + 'px'; refreshImgToolbarPos(); setTimeout(() => applyPageBreaksRef.current(), 30); markChanged(); }} icon={<Minimize2 size={13}/>}/>
                        <ImgBtn title="Ingrandisci (+10%)" onClick={() => { const w = selectedEditorImg.clientWidth; selectedEditorImg.style.maxWidth = 'none'; selectedEditorImg.style.maxHeight = 'none'; selectedEditorImg.style.height = 'auto'; selectedEditorImg.style.width = w * 1.1 + 'px'; refreshImgToolbarPos(); setTimeout(() => applyPageBreaksRef.current(), 30); markChanged(); }} icon={<Maximize2 size={13}/>}/>
                        <ImgBtn title="Larghezza piena" onClick={() => { const wrap = selectedEditorImg.parentElement?.classList.contains('ts-wrap') ? selectedEditorImg.parentElement as HTMLElement : null; if (wrap) { wrap.style.display = 'block'; wrap.style.width = '100%'; } selectedEditorImg.style.maxWidth = 'none'; selectedEditorImg.style.maxHeight = 'none'; selectedEditorImg.style.height = 'auto'; selectedEditorImg.style.width = '100%'; selectedEditorImg.style.float = 'none'; selectedEditorImg.style.display = 'block'; selectedEditorImg.style.margin = '0 auto'; refreshImgToolbarPos(); setTimeout(() => applyPageBreaksRef.current(), 30); markChanged(); }} icon={<MoveHorizontal size={13}/>}/>
                        {/*
                          Torna alla dimensione automatica (azzera larghezza manuale e float), così la
                          foto rientra nelle regole "coppia" / "una per pagina".
                        */}
                        <ImgBtn title="Dimensione originale (regola automatica: verticale 80%, orizzontale 40%)"
                                onClick={() => {
                                    const im = selectedEditorImg;
                                    im.style.width = ''; im.style.height = 'auto'; im.style.float = 'none';
                                    im.style.display = 'block'; im.style.marginLeft = 'auto'; im.style.marginRight = 'auto';
                                    const nw = im.naturalWidth || 0, nh = im.naturalHeight || 0;
                                    if (nh > nw * 1.1) { im.style.maxHeight = `${Math.round(PAGE_H_PX * 0.80)}px`; im.style.maxWidth = '70%'; }
                                    else { im.style.maxHeight = `${Math.round(PAGE_H_PX * 0.40)}px`; im.style.maxWidth = '85%'; }
                                    refreshImgToolbarPos();
                                    setTimeout(() => applyPageBreaksRef.current(), 30);
                                    markChanged();
                                }}
                                icon={<RefreshCw size={13}/>}/>

                        {/* Larghezze rapide */}
                        {[25, 50, 75].map(pct => (
                            <button key={pct} title={`Larghezza ${pct}%`}
                                    onClick={() => { selectedEditorImg.style.width = `${pct}%`; selectedEditorImg.style.float = 'none'; selectedEditorImg.style.display = 'block'; selectedEditorImg.style.margin = '0 auto'; refreshImgToolbarPos(); markChanged(); }}
                                    className="text-[9px] font-bold text-gray-500 hover:bg-blue-50 hover:text-blue-600 px-1.5 py-1 rounded transition-colors">
                                {pct}%
                            </button>
                        ))}

                        <div className="w-px h-4 bg-gray-200 mx-1"/>

                        {/* Rotazione */}
                        <span className="text-[9px] font-bold text-gray-300 uppercase px-1">Ruota</span>
                        <ImgBtn title="Ruota di 90° in senso orario"
                                onClick={() => {
                                    // La rotazione è in data-rotate (multipli di 90°), applicata come transform CSS;
                                    // poi si aggiorna la posizione della toolbar.
                                    const current = parseInt(selectedEditorImg.dataset.rotate || '0', 10) || 0;
                                    const next = (current + 90) % 360;
                                    selectedEditorImg.dataset.rotate = String(next);
                                    selectedEditorImg.style.transform = next === 0 ? '' : `rotate(${next}deg)`;
                                    selectedEditorImg.style.transformOrigin = 'center center';
                                    // Compensa lo sbordo verticale di un'immagine orizzontale ruotata di 90/270
                                    // (vedi ReportEditorView.tsx): padding sul box, non margin sull'img.
                                    const w = selectedEditorImg.clientWidth;
                                    const h = selectedEditorImg.clientHeight;
                                    const isLandscape90 = (next === 90 || next === 270);
                                    const overflow = isLandscape90 ? Math.max(0, (w - h) / 2) : 0;
                                    const box = selectedEditorImg.closest<HTMLElement>('.report-media-box');
                                    if (box) {
                                        box.style.paddingTop = overflow > 0 ? `${overflow}px` : '';
                                        box.style.paddingBottom = overflow > 0 ? `${overflow}px` : '';
                                    }
                                    selectedEditorImg.style.marginTop = '';
                                    selectedEditorImg.style.marginBottom = '';
                                    refreshImgToolbarPos();
                                    setTimeout(() => applyPageBreaksRef.current(), 30);
                                    markChanged();
                                }}
                                icon={<RotateCw size={13}/>}/>

                        <div className="w-px h-4 bg-gray-200 mx-1"/>

                        {/* Data-ora */}
                        <span className="text-[9px] font-bold text-gray-300 uppercase px-1">Data/Ora</span>
                        <ImgBtn title="Aggiungi / modifica etichetta data-ora" onClick={() => openTsModal(selectedEditorImg)} icon={<Clock size={13}/>}/>

                        <div className="w-px h-4 bg-gray-200 mx-1"/>

                        {/* Elimina */}
                        <ImgBtn title="Elimina immagine" danger
                                onClick={() => {
                                    if (window.confirm("Rimuovere questa immagine dalla relazione?")) {
                                        selectedEditorImg.closest('.report-media-box')?.remove() ?? selectedEditorImg.remove();
                                        deselctImg();
                                        pushHistory();
                                        markChanged(); // L'eliminazione è una modifica da salvare
                                    }
                                }}
                                icon={<Trash2 size={13}/>}/>

                        {/* Chiudi toolbar */}
                        <ImgBtn title="Chiudi" onClick={deselctImg} icon={<X size={13}/>}/>
                    </div>

                    {/* Triangolino puntatore */}
                    <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid white', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }}/>
                </div>
            )}

            {/* Modale data-ora foto */}
            {tsModalImg && (
                <div className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                     onClick={() => setTsModalImg(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-80 overflow-hidden animate-in slide-in-from-bottom-4"
                         onClick={e => e.stopPropagation()}>
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Clock size={15} className="text-slate-600"/>
                                <span className="text-sm font-black text-slate-900">Etichetta data / ora</span>
                            </div>
                            <button onClick={() => setTsModalImg(null)} className="p-1 text-slate-400 hover:text-slate-700 rounded-lg transition-colors"><X size={15}/></button>
                        </div>

                        <div className="p-4 space-y-3">
                            {/* Data e ora */}
                            <div className="flex gap-2">
                                <div className="flex-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Data</label>
                                    <input type="date" value={tsDate} onChange={e => setTsDate(e.target.value)}
                                           className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-100"/>
                                </div>
                                <div className="flex-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Ora</label>
                                    <input type="time" value={tsTime} onChange={e => setTsTime(e.target.value)}
                                           className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-100"/>
                                </div>
                            </div>

                            {/* Posizione */}
                            <div>
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1.5">
                                    Posizione {tsPosition === 'custom' && <span className="text-blue-500 normal-case font-normal">(personalizzata — trascina l'etichetta per spostarla)</span>}
                                </label>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {([['tl','↖ Alto sinistra'],['tr','↗ Alto destra'],['bl','↙ Basso sinistra'],['br','↘ Basso destra']] as [TsPos,string][]).map(([p, label]) => (
                                        <button key={p} onClick={() => setTsPosition(p)}
                                                className={`text-xs py-1.5 px-2 rounded-lg border transition-all font-medium ${tsPosition === p ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Anteprima live */}
                            <div className="relative bg-slate-700 rounded-xl h-16 overflow-hidden">
                                <div className="absolute inset-0 opacity-20" style={{backgroundImage:'repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)',backgroundSize:'8px 8px'}}/>
                                <span className={`absolute text-[10px] font-mono font-bold text-white bg-black/65 px-2 py-0.5 rounded whitespace-nowrap ${
                                    tsPosition === 'tl' ? 'top-2 left-2' : tsPosition === 'tr' ? 'top-2 right-2' :
                                        tsPosition === 'bl' ? 'bottom-2 left-2' : 'bottom-2 right-2'
                                }`}>
                                    {tsDate ? tsDate.split('-').reverse().join('/') : 'gg/mm/aaaa'} {tsTime || '--:--'}
                                </span>
                            </div>
                        </div>

                        <div className="px-4 pb-4 flex gap-2">
                            {tsHasExisting && (
                                <button onClick={removeTs}
                                        className="px-3 py-2 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl border border-rose-100 transition-colors">
                                    Rimuovi
                                </button>
                            )}
                            <button onClick={() => setTsModalImg(null)}
                                    className="px-3 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors">
                                Annulla
                            </button>
                            <button onClick={applyTs} disabled={!tsDate || !tsTime}
                                    className="flex-1 px-3 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-xl flex items-center justify-center gap-1.5 transition-colors">
                                <Check size={13}/> Applica
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox anteprima sidebar */}
            {previewFile && (
                <div
                    className="fixed inset-0 z-[9998] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={() => setPreviewFile(null)}
                >
                    {/* Pannello contenuto: stopPropagation per non chiudere cliccando dentro */}
                    <div
                        className="relative max-w-4xl w-full flex flex-col items-center gap-4"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Chiudi */}
                        <button
                            onClick={() => setPreviewFile(null)}
                            className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors"
                        >
                            <X size={28}/>
                        </button>

                        {/* Immagine / spinner */}
                        <div className="relative w-full flex items-center justify-center min-h-[200px]">
                            {previewLoading && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <RefreshCw size={32} className="animate-spin text-white/60"/>
                                </div>
                            )}
                            <img
                                src={previewFile.previewUrl}
                                alt={previewFile.cleanName || previewFile.name}
                                crossOrigin="anonymous"
                                className={`max-h-[75vh] max-w-full rounded-xl shadow-2xl object-contain transition-opacity duration-300 ${previewLoading ? 'opacity-40' : 'opacity-100'}`}
                            />
                        </div>

                        {/* Info + azioni */}
                        <div className="bg-white/10 backdrop-blur-md rounded-xl px-5 py-3 flex items-center gap-4 w-full max-w-xl">
                            <div className="flex-1 min-w-0">
                                <p className="text-white text-xs font-semibold truncate">{previewFile.cleanName || previewFile.name}</p>
                                {previewFile.createdAt && (
                                    <p className="text-white/50 text-[10px] mt-0.5">
                                        {new Date(previewFile.createdAt).toLocaleString('it-IT')}
                                    </p>
                                )}
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button
                                    onClick={async () => {
                                        setPreviewFile(null);
                                        const hdUrl = await getHdUrl(previewFile);
                                        startEditing(hdUrl);
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-400 transition-colors"
                                >
                                    <Wand2 size={13}/> Modifica
                                </button>
                                <button
                                    onClick={async () => {
                                        // Anteprima compressa, originale come data-img-key per l'alta risoluzione.
                                        insertImage(previewFile.thumbUrl || previewFile.urlCompressed || previewFile.hdUrl, previewFile.hdUrl);
                                        setPreviewFile(null);
                                        addToast("Immagine inserita nel documento", "success");
                                    }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-500 transition-colors"
                                >
                                    <PlusCircle size={13}/> Inserisci HD
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale ottimizzazione testo */}
            {aiModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden max-h-[80vh]">
                        <div className="bg-indigo-600 p-3 flex justify-between items-center text-white">
                            <h3 className="font-bold flex items-center gap-2"><Bot size={18}/> Assistente AI</h3>
                            <button onClick={() => setAiModalOpen(false)}><X size={18}/></button>
                        </div>
                        <div className="p-4 flex-1 overflow-y-auto space-y-4">
                            <div className="bg-gray-50 p-3 rounded border text-xs text-gray-500"><strong>Originale:</strong> "{currentAiContext}"</div>
                            {isAiLoading
                                ? <div className="text-center py-4 text-indigo-500 text-sm flex justify-center gap-2"><RefreshCw className="animate-spin" size={16}/> Rielaborazione...</div>
                                : <textarea className="w-full p-3 border rounded-lg text-sm bg-indigo-50 border-indigo-100 min-h-[100px] outline-none focus:ring-2 focus:ring-indigo-500" value={aiResultText} onChange={e => setAiResultText(e.target.value)}/>}
                            <div className="flex gap-2">
                                <input type="text" className="flex-1 border rounded px-3 py-2 text-sm" placeholder="Es: Rendi il tono più formale..." value={aiCustomPrompt} onChange={e => setAiCustomPrompt(e.target.value)} onKeyDown={e => e.key === 'Enter' && runAi(aiResultText || currentAiContext)}/>
                                <button onClick={() => runAi(aiResultText || currentAiContext)} disabled={isAiLoading} className="bg-gray-800 text-white p-2 rounded hover:bg-gray-700"><Send size={16}/></button>
                            </div>
                        </div>
                        <div className="p-3 border-t bg-gray-50 flex justify-end gap-2">
                            <button onClick={() => setAiModalOpen(false)} className="px-3 py-1.5 text-sm text-gray-600">Chiudi</button>
                            <button onClick={() => { insertHtml(textToParagraphs(aiResultText) + '<br/>'); setAiModalOpen(false); }} className="px-4 py-1.5 bg-indigo-600 text-white rounded text-sm font-medium hover:bg-indigo-700">Inserisci</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale video */}
            {videoModalOpen && (
                <div className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-4" onClick={() => setVideoModalOpen(false)}>
                    <button onClick={() => setVideoModalOpen(false)} className="absolute top-4 right-4 text-white hover:text-gray-300"><X size={32}/></button>
                    <div className="flex flex-col items-center gap-0 max-w-4xl w-full" onClick={e => e.stopPropagation()}>
                        <div className="relative w-full">
                            <video ref={videoPlayerRef} src={currentVideoUrl} controls className="w-full max-h-[70vh] rounded shadow-2xl" crossOrigin="anonymous"/>
                        </div>
                        <div className="mt-6 flex flex-col items-center gap-2">
                            <button
                                onClick={captureVideoFrame}
                                className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 text-base"
                            >
                                <Camera size={20}/> Cattura Frame
                            </button>
                            {currentVideoId && (capturedFrames[currentVideoId]?.length ?? 0) > 0 && (
                                <p className="text-white/60 text-sm">
                                    {capturedFrames[currentVideoId].length} frame acquisit{capturedFrames[currentVideoId].length === 1 ? 'o' : 'i'} — visibili sotto il video nella sidebar
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Editor forense */}
            {isEditorOpen && editingImage && (
                <ForensicImageEditor
                    imageUrl={editingImage}
                    onClose={() => setIsEditorOpen(false)}
                    onSave={dataUrl => {
                        insertImage(dataUrl);
                        setIsEditorOpen(false);
                        setEditingImage(null);
                        addToast("Immagine elaborata inserita", "success");
                    }}
                />
            )}

            <KeyHandler onEscape={() => {
                setActiveTool('none');
                setSelectedId(null);
                deselctImg();
            }}/>

            {/*
              Modale anteprima PDF in-app: portal su document.body con stile inline
              (position, inset, zIndex) per stare sopra qualsiasi stacking context.
            */}
            {pdfGenerating && createPortal(
                <div style={{ position: 'fixed', inset: 0, zIndex: 2147483645, background: 'rgba(15,23,42,0.55)',
                              backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 flex flex-col items-center gap-3 max-w-sm text-center">
                        <RefreshCw size={34} className="animate-spin text-blue-600"/>
                        <div className="font-black text-slate-900 text-base">
                            {pdfGenerating === 'preview' ? "Genero l'anteprima…" : 'Genero il PDF…'}
                        </div>
                        <div className="text-xs text-slate-500">Per relazioni lunghe può richiedere qualche secondo.</div>
                    </div>
                </div>,
                document.body
            )}

            {previewPdfUrl && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 2147483646,
                        background: 'rgba(0, 0, 0, 0.78)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem',
                        isolation: 'isolate',
                    }}
                    onClick={() => {
                        if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
                        setPreviewPdfUrl(null);
                    }}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
                        style={{ zIndex: 2147483647 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 shrink-0">
                            <div className="flex items-center gap-2">
                                <Eye size={18} className="text-blue-600"/>
                                <h3 className="font-bold text-slate-900 text-sm">Anteprima PDF</h3>
                                <span className="text-[11px] text-slate-400">Versione di stampa — non scaricata</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Zoom anteprima tramite il fragment #zoom= del viewer PDF */}
                                <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 mr-1">
                                    <button
                                        onClick={() => setPreviewZoom(z => Math.max(50, z - 25))}
                                        title="Riduci"
                                        className="w-6 h-6 flex items-center justify-center text-slate-600 hover:bg-white rounded-md text-sm font-bold"
                                    >−</button>
                                    <button
                                        onClick={() => setPreviewZoom(100)}
                                        title="Ripristina 100%"
                                        className="text-[11px] font-bold text-slate-600 w-11 text-center tabular-nums hover:text-blue-600"
                                    >{previewZoom}%</button>
                                    <button
                                        onClick={() => setPreviewZoom(z => Math.min(300, z + 25))}
                                        title="Ingrandisci"
                                        className="w-6 h-6 flex items-center justify-center text-slate-600 hover:bg-white rounded-md text-sm font-bold"
                                    >+</button>
                                </div>
                                <button
                                    onClick={() => handleDownloadPdf('download')}
                                    disabled={isSaving}
                                    title="Scarica questo PDF"
                                    className="px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    <Download size={14}/> Scarica
                                </button>
                                <button
                                    onClick={() => {
                                        if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
                                        setPreviewPdfUrl(null);
                                    }}
                                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                    title="Chiudi"
                                >
                                    <X size={18}/>
                                </button>
                            </div>
                        </div>
                        <iframe
                            key={previewZoom}
                            src={`${previewPdfUrl}#zoom=${previewZoom}`}
                            title="Anteprima PDF"
                            className="flex-1 w-full bg-slate-100"
                        />
                    </div>
                </div>,
                document.body,
            )}

            {/* Overlay generazione PDF */}
            {isGeneratingPdf && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-auto">
                    <div className="bg-white rounded-2xl shadow-2xl px-8 py-7 max-w-sm w-full mx-4 flex flex-col items-center gap-4 animate-in zoom-in-95 fade-in">
                        <div className="relative">
                            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                                <Loader2 size={28} className="text-blue-600 animate-spin"/>
                            </div>
                        </div>
                        <div className="text-center">
                            <h3 className="font-bold text-gray-900 text-base">Generazione PDF in corso</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {pdfProgress
                                    ? `Composizione pagina ${pdfProgress.current} di ${pdfProgress.total}…`
                                    : "Cattura del documento e impaginazione…"}
                            </p>
                        </div>
                        {pdfProgress && (
                            <div className="w-full">
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-600 transition-all duration-200 ease-out"
                                        style={{ width: `${Math.round((pdfProgress.current / pdfProgress.total) * 100)}%` }}
                                    />
                                </div>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mt-2">
                                    {Math.round((pdfProgress.current / pdfProgress.total) * 100)}%
                                </p>
                            </div>
                        )}
                        <p className="text-[11px] text-gray-400 text-center italic mt-1">
                            Attendi il completamento — non chiudere la pagina.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

// Card immagine della sidebar
interface SidebarImageCardProps {
    item: any;
    onPreview: () => void;
    onInsert: () => Promise<void>;
    onEdit: () => Promise<void>;
    /** Aggiorna la miniatura scaduta; true se ha impostato un nuovo URL. */
    onThumbError: (imgEl: HTMLImageElement) => Promise<boolean>;
    /** Chiamato in modo sincrono nel dragstart: salva l'item in un ref del genitore */
    onDragStart: () => void;
}

const SidebarImageCard: React.FC<SidebarImageCardProps> = ({ item, onPreview, onInsert, onEdit, onThumbError, onDragStart }) => {
    const [inserting, setInserting] = useState(false);
    const [editing, setEditing] = useState(false);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [imgError, setImgError] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    return (
        <div
            className={`group relative aspect-square bg-slate-100 rounded-lg overflow-hidden border shadow-sm hover:shadow-lg transition-all cursor-grab active:cursor-grabbing ${isDragging ? 'border-blue-500 ring-2 ring-blue-300 opacity-70' : 'border-slate-200 hover:border-blue-300'}`}
            draggable
            onDragStart={(e) => {
                setIsDragging(true);
                // Deve restare sincrono: dataTransfer.setData va chiamato durante il dragstart.
                // L'item reale è nel draggingItemRef del genitore; la miniatura passa nel
                // dataTransfer come fallback.
                onDragStart();
                e.dataTransfer.setData('text/media-url', item.thumbUrl || item.hdUrl || String(item.id));
                e.dataTransfer.effectAllowed = 'copy';
            }}
            onDragEnd={() => setIsDragging(false)}
        >
            {/* Placeholder mentre l'immagine carica */}
            {!imgLoaded && !imgError && (
                <div className="absolute inset-0 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 animate-pulse"/>
            )}

            {/* Icona se l'immagine non carica */}
            {imgError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 gap-1">
                    <ImageIcon size={20} className="text-slate-300"/>
                    <span className="text-[8px] text-slate-400 font-medium px-1 text-center">Anteprima non disponibile</span>
                </div>
            )}

            {/* Miniatura: click per aprire il lightbox */}
            {!imgError && (
                <img
                    src={item.thumbUrl}
                    crossOrigin="anonymous"
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${imgLoaded ? 'opacity-90 group-hover:opacity-60' : 'opacity-0'}`}
                    alt={item.cleanName || item.name}
                    onLoad={() => setImgLoaded(true)}
                    onError={async e => {
                        const target = e.currentTarget;
                        // URL firmato scaduto: al primo errore chiediamo un URL nuovo e riproviamo;
                        // "Anteprima non disponibile" solo se anche il refresh fallisce.
                        if (target.dataset.refreshed) { setImgError(true); return; }
                        target.dataset.refreshed = "true";
                        let fresh = false;
                        try { fresh = await onThumbError(target); } catch { fresh = false; }
                        if (!fresh) setImgError(true);
                    }}
                    onClick={onPreview}
                />
            )}

            {/* Azioni, visibili in hover */}
            <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-200 flex flex-col items-center justify-center gap-1.5 p-1"
                style={{ background: 'linear-gradient(to top, rgba(15,23,42,0.85) 0%, rgba(15,23,42,0.3) 60%, transparent 100%)' }}
            >
                {/* Anteprima (click sull'area centrale) */}
                <button
                    onClick={onPreview}
                    className="absolute inset-0 w-full h-full cursor-zoom-in"
                    title="Anteprima"
                />

                {/* Pulsanti in basso */}
                <div className="absolute bottom-1.5 left-1 right-1 flex gap-1 justify-center">
                    <button
                        disabled={editing}
                        onClick={async e => { e.stopPropagation(); setEditing(true); await onEdit(); setEditing(false); }}
                        className="flex items-center gap-0.5 px-1.5 py-1 bg-amber-500/90 backdrop-blur-sm text-white rounded text-[9px] font-bold hover:bg-amber-500 disabled:opacity-50 transition-colors shadow-lg"
                        title="Apri editor forense"
                    >
                        {editing ? <RefreshCw size={9} className="animate-spin"/> : <Wand2 size={9}/>}
                        {editing ? '...' : 'Edit'}
                    </button>

                    <button
                        disabled={inserting}
                        onClick={async e => { e.stopPropagation(); setInserting(true); await onInsert(); setInserting(false); }}
                        className="flex items-center gap-0.5 px-1.5 py-1 bg-blue-600/90 backdrop-blur-sm text-white rounded text-[9px] font-bold hover:bg-blue-600 disabled:opacity-50 transition-colors shadow-lg"
                        title="Inserisci nel documento HD"
                    >
                        {inserting ? <RefreshCw size={9} className="animate-spin"/> : <PlusCircle size={9}/>}
                        {inserting ? '...' : 'Inserisci'}
                    </button>
                </div>
            </div>

            {/* Badge sorgente
            {item.isFieldMedia && (
                <div className="absolute top-1 left-1 bg-emerald-500/90 text-white text-[7px] font-black px-1 py-0.5 rounded uppercase tracking-wide shadow pointer-events-none">
                    CAMPO
                </div>
            )}*/}

            {/* Spinner durante l'azione */}
            {(inserting || editing) && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                    <RefreshCw size={16} className="animate-spin text-blue-600"/>
                </div>
            )}
        </div>
    );
};

// Componenti ausiliari
/** Pulsante compatto per la toolbar immagine */
const ImgBtn: React.FC<{ icon: React.ReactNode; onClick: () => void; title: string; danger?: boolean }> = ({ icon, onClick, title, danger }) => (
    <button
        onMouseDown={e => e.preventDefault()}
        onClick={onClick}
        title={title}
        className={`p-1.5 rounded transition-colors ${danger ? 'text-red-500 hover:bg-red-50' : 'text-gray-600 hover:bg-gray-100'}`}
    >
        {icon}
    </button>
);

const Divider = () => <div className="w-px h-4 bg-gray-300 mx-1 shrink-0"/>;

const KeyHandler: React.FC<{ onEscape: () => void }> = ({ onEscape }) => {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onEscape(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onEscape]);
    return null;
};

export default ReportEditorViewBonifiche;